import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { copyText } from "@/lib/clipboard";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  VideoTrack,
  useTracks,
  useParticipants,
  useSpeakingParticipants,
  useLocalParticipant,
  useRoomContext,
  useConnectionState,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { RoomEvent, Track, ConnectionState, LocalVideoTrack, LocalAudioTrack, ScreenSharePresets } from "livekit-client";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShareWatermark } from "@/components/ShareWatermark";
import { BrandAvatar } from "@/components/BrandAvatar";
import { UdyogLogo } from "@/components/UdyogLogo";
import { isHostParticipant, setLocalHostRole } from "@/lib/roles";
import { ZoomPan } from "@/components/ZoomPan";
import { CropFrame, CropEditor, isFullCrop, loadSavedCrops } from "@/components/ShareCrop";
import { useImageReady, preloadImage } from "@/lib/imageReady";
import { PayCheckout } from "@/components/PayCheckout";
import { WhatsappUs } from "@/components/WhatsappUs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  X,
  MessageCircle,
  Megaphone,
  Loader2,
  Square,
  Maximize2,
  Mic,
  MicOff,
  Video as VideoIcon,
  VideoOff,
  MonitorUp,
  PhoneOff,
  Phone,
  Users,
  ExternalLink,
  Hand,
  Copy,
  Wifi,
  WifiOff,
  Lock,
  MessageSquare,
  ShieldCheck,
  DoorOpen,
  Circle,
  MoreHorizontal,
  PictureInPicture2,
  Smile,
  Pin,
  UserMinus,
  MoreVertical,
  Sparkles,
  Search,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { StudioMicProcessor, getStudioMicPref, setStudioMicPref, getStudioMicMode, prewarmStudioMic, destroyWarmStudioMic } from "@/lib/studioMicProcessor";
import { holdCallAudio, releaseCallAudio, isMobileDevice } from "@/lib/callAudio";
import {
  startMeetingSession,
  endMeetingSession,
  markUserLeft,
  handleNavigationAway,
  enterPip as pipEnter,
  armFloating,
  disarmFloating,
  isFloatingArmed,
  tagVideosForAutoPip,
  getMeetingState,
} from "@/lib/meetingPip";
import {
  goFullscreenNow,
  isFullscreen as pwaIsFullscreen,
  isStandalone,
  canInstall,
  promptInstall,
  onInstallabilityChange,
  requestWakeLock,
  isFullscreenEligible,
} from "@/lib/pwa";
import { avatarUrl, setAvatarScope } from "@/lib/avatar";
import { startStillHere, stopStillHere, askNotificationPermission } from "@/lib/stillHere";
import {
  QUALITY_PRESETS,
  applyPublishQuality,
  setLowDataMode,
  VIEW_QUALITY,
  applyViewQuality,
  qualityCeiling,
  dropRemoteCameras,
  SLIDE_ONLY_FROM,
} from "@/lib/publishQuality";

/**
 * Meeting page — 100% custom Udyog UI on top of LiveKit SFU.
 * Everything is our own components: tiles, controls, chat, participant list.
 */

export default function Meeting() {
  const { roomName } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [status, setStatus] = useState("preparing"); // preparing | joining | joined | error | left
  const [errMsg, setErrMsg] = useState("");
  const [creds, setCreds] = useState(null); // { livekit_url, token, is_host, user_id, user_name }
  // NEVER seed userName from localStorage. Every meeting must re-ask the
  // visitor for whatever the host configured on THIS meeting (name/WA/both/
  // none). The join page handles all four cases and passes ?name= back here.
  const [userName, setUserName] = useState(
    params.get("name") || (user && user.name) || ""
  );
  const whatsappRef = useRef(params.get("wa") || "");

  // Session persistence — if a student's browser crashes or reloads mid-class,
  // seamlessly rejoin the same room without re-doing the join form. Only
  // scoped to THIS room + THIS tab (sessionStorage), never cross-meeting.
  useEffect(() => {
    if (!roomName) return;
    if (userName.trim()) {
      try {
        sessionStorage.setItem(
          `udyog:sess:${roomName}`,
          JSON.stringify({ userName, whatsapp: whatsappRef.current })
        );
        // Survives a closed tab too, so "Join the class again" never asks a
        // returning student to type their details a second time. Hosts are
        // skipped — they must never be silently re-seated as a student.
        if (!params.get("host") && !params.get("cohost"))
          localStorage.setItem(
            `udyog_rejoin:${roomName}`,
            JSON.stringify({ name: userName, whatsapp: whatsappRef.current || "", ts: Date.now() })
          );
      } catch (_) {}
      return;
    }
    // No name from URL — try session storage before redirecting to /join.
    try {
      const raw = sessionStorage.getItem(`udyog:sess:${roomName}`);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.userName) {
          setUserName(cached.userName);
          if (cached.whatsapp) whatsappRef.current = cached.whatsapp;
        }
      }
    } catch (_) {}
  }, [roomName, userName]);

  // Strip name/wa from URL bar so participants never see personal params
  useEffect(() => {
    if (params.get("name") || params.get("wa")) {
      const cleanUrl = `${window.location.pathname}${window.location.hash || ""}`;
      window.history.replaceState({}, "", cleanUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user === null) return;
    // If URL claims host but user isn't authenticated, send to login first
    if (params.get("host") === "1" && (!user || user.role !== "admin")) {
      const back = `/meet/${roomName}?host=1`;
      nav(`/login?next=${encodeURIComponent(back)}`, { replace: true });
      return;
    }
    if (userName.trim()) return;
    if (user && user.name) {
      setUserName(user.name);
      return;
    }
    // Mid-class reload / stray navigation back to this URL: this tab already
    // knows who they are, so resume the class instead of asking again.
    try {
      const raw = sessionStorage.getItem(`udyog:sess:${roomName}`);
      const cached = raw ? JSON.parse(raw) : null;
      if (cached?.userName) {
        setUserName(cached.userName);
        if (cached.whatsapp) whatsappRef.current = cached.whatsapp;
        return;
      }
    } catch (_) {}
    // Genuinely new visitor — route to the join page for THIS meeting so the
    // host's per-meeting form (none/name/whatsapp/both) is enforced fresh.
    nav(`/join/${roomName}`, { replace: true });
  }, [user, userName, roomName, nav, params]);

  // Bumped when the connection drops on its own (phone slept, network blip,
  // Chrome froze the tab in the background). Re-fetches a fresh token and
  // reconnects instead of telling the student they left.
  const [rejoinNonce, setRejoinNonce] = useState(0);
  // True while we are hopping from a dead media server to the standby.
  const [switching, setSwitching] = useState(false);

  // The connection dropped and the backend now points at a different media
  // server — abandon the dead socket immediately instead of waiting for
  // LiveKit to give up on it. This is what keeps a mid-class VPS death down
  // to a two-second blink for students.
  const handleMediaSwitch = useCallback(() => {
    setSwitching(true);
    setStatus("joining");
    setCreds(null);
    setRejoinNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!userName.trim()) return;
    let disposed = false;
    (async () => {
      try {
        setStatus("joining");
        const [{ data }, ownerCheck] = await Promise.all([
          // The number travels with every token request: it's what identifies a
          // student for the lock, the waiting room and a block — names get
          // retyped, numbers don't.
          api.post(`/meetings/${roomName}/join-token`, {
            user_name: userName,
            whatsapp: whatsappRef.current || undefined,
          }),
          // If the current user is an authenticated admin AND owns this meeting,
          // this call succeeds. We then force host mode client-side even if the
          // token endpoint couldn't identify us (e.g. tab restored, cookie
          // dropped, joined via /join URL).
          api.get(`/meetings/${roomName}`).then((r) => r.data).catch(() => null),
        ]);
        if (disposed) return;
        if (data.provider !== "livekit") {
          setStatus("error");
          setErrMsg("Video provider is not configured for LiveKit. Contact the host.");
          return;
        }
        const isAuthedAdmin = !!(user && user.role === "admin");
        const forcedHost =
          (!!ownerCheck && ownerCheck.room_name === roomName) ||
          (params.get("host") === "1" && isAuthedAdmin);
        setCreds({ ...data, is_host: data.is_host || forcedHost });
        setSwitching(false);
        if (params.get("host") === "1" && !isAuthedAdmin) {
          // URL claims host but no admin session — send them to login.
          const back = `/meet/${roomName}?host=1`;
          nav(`/login?next=${encodeURIComponent(back)}`, { replace: true });
          return;
        }
      } catch (e) {
        if (disposed) return;
        setStatus("error");
        const code = e?.response?.status;
        if (code === 423) setErrMsg("This meeting hasn't started yet. Please come back at the scheduled time.");
        else if (code === 410) setErrMsg("This meeting has ended.");
        else setErrMsg(formatApiError(e) || "Could not load the meeting.");
      }
    })();
    return () => {
      disposed = true;
    };
  }, [userName, roomName, rejoinNonce]);

  // BACK-BUTTON GUARD — installed the moment the meeting page mounts (before
  // the connection is even ready) and re-seeded after every pop, tab restore
  // and return-to-foreground. Only the red Leave button can end a class.
  useEffect(() => {
    const seed = () => {
      try {
        if (!window.history.state || !window.history.state.udyogGuard) {
          window.history.pushState({ udyogGuard: true }, "");
        }
      } catch (_) {}
    };
    seed();
    try {
      sessionStorage.setItem("udyog_active_class", window.location.pathname + window.location.search);
    } catch (_) {}
    const onPop = () => {
      seed();
      window.dispatchEvent(new CustomEvent("udyog-back"));
    };
    const onRestore = () => seed();
    window.addEventListener("popstate", onPop);
    window.addEventListener("pageshow", onRestore);
    document.addEventListener("visibilitychange", onRestore);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("pageshow", onRestore);
      document.removeEventListener("visibilitychange", onRestore);
      // NOTE: udyog_active_class is deliberately NOT cleared here. Unmounting
      // is exactly the moment a stray navigation happens, and the Goodbye page
      // needs this value to bounce the student back into class. Only an
      // explicit Leave clears it.
    };
  }, []);

  const [meetingTitle, setMeetingTitle] = useState("");

  // Browser tab title stays as "Udyog Classes" on every meeting page — the
  // meeting name is only rendered inside the page header, never in the URL bar.
  useEffect(() => {
    let cancelled = false;
    document.title = "Udyog Classes";
    (async () => {
      try {
        const { data } = await api.get(`/public/meetings/${roomName}`);
        if (!cancelled && data?.title) {
          setMeetingTitle(data.title);
        }
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [roomName]);

  if (status === "error" || status === "left") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#0a0a0a] text-white">
        <div className="text-center max-w-md px-6">
          <UdyogLogo size={44} className="justify-center mb-6" />
          {status === "error" ? (
            <>
              <h2 className="font-display text-xl font-bold mb-2 text-red-400">Can&apos;t join meeting</h2>
              <p className="text-neutral-400 text-sm mb-4" data-testid="meeting-error">{errMsg}</p>
              <Button
                onClick={() => nav(`/join/${roomName}`)}
                className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
              >
                Try again
              </Button>
            </>
          ) : (
            <>
              <h2 className="font-display text-xl font-bold mb-2">You left the class</h2>
              <p className="text-neutral-400 text-[13px] mb-6">
                The class may still be running. You can jump straight back in.
              </p>
              <div className="flex flex-col gap-2.5 max-w-[280px] mx-auto">
                <Button
                  onClick={() => {
                    setStatus("joining");
                    setCreds(null);
                    startMeetingSession();
                    setRejoinNonce((n) => n + 1);
                  }}
                  className="h-12 rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 hover:from-emerald-300 hover:to-emerald-500 text-black font-semibold text-[12px] uppercase tracking-[0.18em]"
                  data-testid="left-rejoin-btn"
                >
                  Go back to the class
                </Button>
                <Button
                  onClick={() => nav("/goodbye")}
                  className="h-11 rounded-full bg-transparent ring-1 ring-white/[0.14] hover:bg-white/[0.06] text-white/80 text-[11px] uppercase tracking-[0.16em]"
                  data-testid="left-continue-btn"
                >
                  Continue
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!creds) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#0a0a0a] text-white">
        <div className="text-center">
          <UdyogLogo size={44} className="justify-center mb-6" />
          <div
            className="flex items-center justify-center gap-3 text-neutral-400"
            data-testid={rejoinNonce > 0 ? "reconnect-banner" : "preparing-banner"}
          >
            <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
            {switching
              ? "One moment — moving you to the backup server…"
              : rejoinNonce > 0
              ? "Reconnecting… you have NOT left the class"
              : "Preparing your meeting…"}
          </div>
          {rejoinNonce > 0 && (
            <p className="mt-3 text-[11.5px] text-emerald-300/70">
              Your seat is held. This takes a few seconds.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#0a0a0a] text-white">
      <LiveKitRoom
        serverUrl={creds.livekit_url}
        token={creds.token}
        connect
        audio={false}
        video={false}
        options={{
          adaptiveStream: false,
          dynacast: true,
          // A student tapping through to their UPI app fires `pagehide` on
          // Android. LiveKit's default is to drop the connection there, which
          // kicked them out of the class and took the "send your payment
          // screenshot" button with it. Never disconnect on our behalf.
          disconnectOnPageLeave: false,
          // Muting must never tear the mic track down — rebuilding it would
          // reload the whole DeepFilterNet3 pipeline on the next unmute.
          stopLocalTrackOnUnpublish: false,
          // Broadcast-grade mic pipeline.
          //   * echoCancellation ON  — Chrome AEC3 is industry best, keep it.
          //   * noiseSuppression OFF — DeepFilterNet3 (in StudioMicProcessor)
          //                            does this job orders of magnitude better.
          //                            Browser NS destroys spectral cues DFN3
          //                            needs, so we hand DFN3 a raw signal.
          //   * autoGainControl OFF  — mic distance should determine loudness
          //                            (physics). AGC amplifies every small
          //                            background sound to voice level.
          //   * googHighpassFilter OFF — DFN3 + our 80 Hz biquad do this.
          //   * googTypingNoiseDetection OFF — DFN3 removes typing itself
          //                                    without the heuristic mute
          //                                    that occasionally chops voice.
          audioCaptureDefaults: {
            autoGainControl: false,
            echoCancellation: true,
            noiseSuppression: false,
            channelCount: 1,
            sampleRate: 48000,
            sampleSize: 16,
            googEchoCancellation: true,
            googEchoCancellation2: true,
            googAutoGainControl: false,
            googAutoGainControl2: false,
            googNoiseSuppression: false,
            googNoiseSuppression2: false,
            googHighpassFilter: false,
            googTypingNoiseDetection: false,
            googAudioMirroring: false,
            googExperimentalEchoCancellation: true,
            googExperimentalAutoGainControl: false,
            googExperimentalNoiseSuppression: false,
          },
          publishDefaults: {
            simulcast: true,
            videoSimulcastLayers: [
              { width: 320, height: 180, encoding: { maxBitrate: 150_000, maxFramerate: 15 } },
              { width: 640, height: 360, encoding: { maxBitrate: 400_000, maxFramerate: 24 } },
            ],
            screenShareEncoding: { maxBitrate: 5_000_000, maxFramerate: 15, priority: 'high' },
            // Three layers so a student on a weak link is stepped down by the
            // SFU instead of buffering the full-HD slide.
            screenShareSimulcastLayers: [
              { width: 960, height: 540, encoding: { maxBitrate: 500_000, maxFramerate: 15 } },
              { width: 1280, height: 720, encoding: { maxBitrate: 1_200_000, maxFramerate: 20 } },
            ],
            // 48 kHz Opus + built-in RED redundancy so a lost packet doesn't
            // become a lost word. Same trick Google Meet ships.
            audioPreset: {
              maxBitrate: 40_000,
              priority: "high",
            },
            red: true,
            dtx: true,
            forceStereo: false,
          },
        }}
        onDisconnected={() => {
          // Only an explicit "Leave" ends the class. Anything else is a
          // dropped socket — reconnect quietly.
          if (getMeetingState().userExplicitlyLeft) {
            setStatus("left");
            return;
          }
          setStatus("joining");
          setCreds(null);
          setTimeout(() => setRejoinNonce((n) => n + 1), 300);
        }}
        onError={(e) => {
          setStatus("error");
          setErrMsg(e?.message || "Connection error");
        }}
        style={{ width: "100%", height: "100%" }}
      >
        <MediaFailoverGuard currentUrl={creds.livekit_url} onSwitch={handleMediaSwitch} />
        <MeetingRoom
          roomName={roomName}
          userName={userName}
          userId={creds.user_id}
          isHost={creds.is_host}
          initialCoHost={params.get("cohost") === "1"}
          whatsapp={whatsappRef.current}
          meetingTitle={meetingTitle}
        />
        <RoomAudioRenderer />
      </LiveKitRoom>
    </div>
  );
}

// ------------------------------------------------------------------
// Custom Udyog meeting UI
// ------------------------------------------------------------------
/**
 * Watches for the moment the media server stops answering. The instant
 * LiveKit says "reconnecting", we ask the backend which server is live now;
 * if it has moved to the standby we re-join there straight away instead of
 * sitting through LiveKit's retry window on a machine that is gone.
 */
function MediaFailoverGuard({ currentUrl, onSwitch }) {
  const state = useConnectionState();
  useEffect(() => {
    if (state !== ConnectionState.Reconnecting) return;
    let stop = false;
    let tries = 0;
    const tick = async () => {
      if (stop) return;
      try {
        const { data } = await api.get("/media/active", { params: { force: 1 } });
        if (!stop && data?.url && data.url !== currentUrl) {
          onSwitch();
          return;
        }
      } catch (_) {}
      tries += 1;
      if (!stop && tries < 30) setTimeout(tick, 1000);
    };
    const t = setTimeout(tick, 250);
    return () => {
      stop = true;
      clearTimeout(t);
    };
  }, [state, currentUrl, onSwitch]);
  return null;
}

function MeetingRoom({ roomName, userName, userId, isHost: initialIsHost, initialCoHost = false, whatsapp, meetingTitle }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const connectionState = useConnectionState();
  const room = useRoomContext();
  const participants = useParticipants();
  const activeSpeakers = useSpeakingParticipants();
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  // QA hook: lets automated screenshots publish a synthetic screen-share track.
  useEffect(() => {
    if (room) window.__udyogRoom = room;
  }, [room]);

  // Warm the exit screens a few seconds into the class so leaving never shows
  // a half-painted page.
  useEffect(() => {
    const t = setTimeout(() => {
      preloadImage("/brand/screens/left@2x.webp");
      preloadImage("/brand/screens/ended@2x.webp");
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  const [isHost, setIsHost] = useState(!!initialIsHost);
  // Animal avatars are shuffled per class — same face all class long, a new one
  // next class. Set before anything renders an avatar.
  setAvatarScope(roomName);
  useEffect(() => {
    setIsHost((v) => v || !!initialIsHost);
  }, [initialIsHost]);

  const [showChat, setShowChat] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [showHostPanel, setShowHostPanel] = useState(false);
  const [coHosts, setCoHosts] = useState(new Set());
  // Broadcast-wide "pin" — when set, every client focuses that identity.
  const [pinnedIdentity, setPinnedIdentity] = useState(null);
  // Last person heard talking — owns the stage when nobody is pinned.
  const [dominantIdentity, setDominantIdentity] = useState(null);
  // Host publish quality — the knob that decides whether 600 students fit.
  // Forced to Max Quality once (v2): classes taught on the old Data Saver /
  // Standard presets looked blurry to students, and Max still fits 150
  // students inside the 200 Mbps line.
  const [quality, setQuality] = useState(() => {
    try {
      if (!localStorage.getItem("udyog_quality_v2")) {
        localStorage.setItem("udyog_quality_v2", "1");
        localStorage.setItem("udyog_quality", "sharp");
        return "sharp";
      }
      return localStorage.getItem("udyog_quality") || "sharp";
    } catch (_) {
      return "sharp";
    }
  });
  // What this student wants to RECEIVE. 1080p by default; adaptive stream
  // still protects them automatically on a bad link.
  const [viewQuality, setViewQuality] = useState(() => {
    try {
      return localStorage.getItem("udyog_view_quality") || "1080p";
    } catch (_) {
      return "1080p";
    }
  });

  // Student data saver — drops incoming cameras, never the voice or the slide.
  const [lowData, setLowData] = useState(false);
  const lowDataAuto = useRef(false);

  // "You're still in class" card shown when the student presses Back.
  const [backSheet, setBackSheet] = useState(false);
  const [askNotif, setAskNotif] = useState(false);
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get("qaNotif") === "1") {
        setAskNotif(true);
        return;
      }
      if (!("Notification" in window)) return;
      if (Notification.permission !== "default") return;
      if (localStorage.getItem("udyog_notif_asked") === "1") return;
      const t = setTimeout(() => setAskNotif(true), 6000);
      return () => clearTimeout(t);
    } catch (_) {}
  }, []);
  const [installable, setInstallable] = useState(false);
  const [fsOn, setFsOn] = useState(false);
  // Armed for exactly one re-entry attempt after we lose fullscreen.
  const fsRetryRef = useRef(true);
  useEffect(() => onInstallabilityChange(setInstallable), []);
  useEffect(() => {
    const sync = () => setFsOn(pwaIsFullscreen() || isStandalone());
    sync();
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);
  // Tab-title pulse + "class is still running" notification while backgrounded.
  useEffect(() => {
    startStillHere(meetingTitle || "");
    return () => stopStillHere();
  }, [meetingTitle]);

  // Keep the phone awake for the whole class.
  useEffect(() => {
    let lock = null;
    let alive = true;
    requestWakeLock().then((l) => {
      if (alive) lock = l;
      else l?.release?.().catch(() => {});
    });
    const reacquire = async () => {
      if (document.visibilityState === "visible" && !lock) lock = await requestWakeLock();
    };
    document.addEventListener("visibilitychange", reacquire);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", reacquire);
      lock?.release?.().catch(() => {});
    };
  }, []);

  // Live reactions — float up a narrow lane on the left, never over the slide.
  const [reactionsOff, setReactionsOff] = useState(false);
  const [flyingReactions, setFlyingReactions] = useState([]);
  const pushReaction = useCallback((emoji, name) => {
    const id = `${Date.now()}-${Math.random()}`;
    setFlyingReactions((prev) => [...prev.slice(-12), { id, emoji, name, offset: Math.round(Math.random() * 22) }]);
    setTimeout(() => setFlyingReactions((prev) => prev.filter((r) => r.id !== id)), 3400);
  }, []);
  const localIdentity = room?.localParticipant?.identity;
  const isCoHost = !!(localIdentity && coHosts.has(localIdentity)) || initialCoHost;

  // Host-controlled crop of the shared screen. The host frames it once and
  // every student sees exactly that — no automatic detection anywhere.
  // Declared up here because the data-channel handler and the host heartbeat
  // below both touch it.
  const [shareCrop, setShareCrop] = useState(null);
  const [cropEditing, setCropEditing] = useState(false);
  // Intrinsic aspect of the incoming share + aspect of what's visible after
  // cropping. A portrait view fills the stage edge to edge, so the presenter
  // pill and chat bubbles get their own black gutter instead of sitting on it.
  const [videoAspect, setVideoAspect] = useState(0);
  const [shareAspect, setShareAspect] = useState(0);
  // Framings the host saved under their own names — one tap puts one live.
  const [savedCrops, setSavedCrops] = useState(() => loadSavedCrops());
  const handleShareMeta = useCallback((intrinsic, visible) => {
    if (intrinsic > 0) setVideoAspect((p) => (Math.abs(p - intrinsic) > 0.02 ? intrinsic : p));
    if (visible > 0) setShareAspect((p) => (Math.abs(p - visible) > 0.04 ? visible : p));
  }, []);
  // Shared content fills the screen by default (no black bars). One tap
  // switches between filling and seeing the whole slide.
  //   auto  → fill, unless filling would hide more than a quarter of the slide
  //   fill  → always edge-to-edge, whatever gets cut
  //   fit   → always the whole slide
  const [shareFitMode, setShareFitMode] = useState(() => {
    try {
      return localStorage.getItem("udyog_share_fit") || "auto";
    } catch (_) {
      return "auto";
    }
  });
  // Whoever is host/co-host in THIS browser gets the brand logo on their card,
  // never an animal — including someone promoted mid-class, whose token still
  // says "student" until we push the updated role to everyone else.
  useEffect(() => {
    setLocalHostRole(isHost || isCoHost);
  }, [isHost, isCoHost]);
  // Session heartbeat — host publishes a beat every 3 s. If nothing arrives
  // for 12 s, the room's client-side treats the host as "away". Co-hosts see
  // an amber "You're the acting host" banner so they know to step in.
  const [hostAway, setHostAway] = useState(false);
  const lastHostBeatRef = useRef(Date.now());
  const [chatLocked, setChatLocked] = useState(false);
  const [shareLocked, setShareLocked] = useState(false);
  const [locked, setLocked] = useState(false);
  const [waitingRoom, setWaitingRoom] = useState(false);
  // Host-controlled "WhatsApp us" button on every student's screen.
  const [waBtn, setWaBtn] = useState(false);
  // Sync the waiting-room toggle from the server on mount so a reload restores it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/meetings/${roomName}`);
        if (cancelled) return;
        if (typeof data?.waiting_room === "boolean") {
          setWaitingRoom(data.waiting_room);
        }
        if (typeof data?.wa_button === "boolean") setWaBtn(data.wa_button);
        // A host-wide pin lives on the server, so a reload / late join / the
        // cloud recorder all land on the same locked stage.
        if (data?.pinned_identity) setPinnedIdentity(data.pinned_identity);
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [roomName]);
  const [micsMuted, setMicsMuted] = useState(false); // host-level toggle
  const [camsOff, setCamsOff] = useState(false); // host-level toggle
  // Participant-side enforcement — when the host mutes / turns off cams for
  // everyone, we set these flags and block the local toggle button from
  // turning the device back on until the host releases the lock.
  const [micForcedOff, setMicForcedOff] = useState(false);
  const [camForcedOff, setCamForcedOff] = useState(false);
  const [lobby, setLobby] = useState([]);
  const [activePoll, setActivePoll] = useState(null);
  const [pollAnswer, setPollAnswer] = useState(null); // participant's follow-up card
  const [pollDraft, setPollDraft] = useState({
    question: "",
    banner_url: null,
    duration_sec: 90,
    options: [
      { key: "yes", label: "Yes", follow_up_text: "", follow_up_link: "", follow_up_button: "" },
      { key: "no", label: "No", follow_up_text: "", follow_up_link: "", follow_up_button: "" },
    ],
  });
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatText, setChatText] = useState("");
  const [visibleBubbles, setVisibleBubbles] = useState([]); // floating chat bubbles
  const bubbleSeen = useRef(new Set());
  const [broadcastText, setBroadcastText] = useState("");
  const [broadcastLink, setBroadcastLink] = useState("");
  const [broadcastLabel, setBroadcastLabel] = useState("Open link");
  const [broadcastPay, setBroadcastPay] = useState(false);
  // The checkout must survive a tab reload / an app switch, otherwise a student
  // who paid in their UPI app comes back to a meeting with no way to confirm.
  const [payOpen, setPayOpen] = useState(
    () => sessionStorage.getItem(`udyog_pay_open_${roomName}`) === "1"
  );
  useEffect(() => {
    if (payOpen) sessionStorage.setItem(`udyog_pay_open_${roomName}`, "1");
    else sessionStorage.removeItem(`udyog_pay_open_${roomName}`);
  }, [payOpen, roomName]);
  const [broadcastBanner, setBroadcastBanner] = useState(null);
  const [broadcastDuration, setBroadcastDuration] = useState(25);
  const [poppedBroadcast, setPoppedBroadcast] = useState(null);
  // A pop-up must arrive COMPLETE. We hold it back until its banner is
  // downloaded and decoded (1.5s at most) so the card never unfolds line by
  // line while the image paints.
  const broadcastReady = useImageReady(poppedBroadcast?.banner_url, 1500);
  const pollReady = useImageReady(activePoll?.banner_url, 1500);
  const followupReady = useImageReady(pollAnswer?.banner_url, 1500);
  const [unread, setUnread] = useState(0);
  const seenBroadcasts = useRef(new Set());
  const answeredPolls = useRef(new Set()); // poll IDs the user already answered — never re-show
  const dismissedBroadcasts = useRef(new Set());
  const [raiseHandDisabled, setRaiseHandDisabled] = useState(false);
  // In-class WhatsApp collection
  const [askWaOpen, setAskWaOpen] = useState(false);
  const [askWaMsg, setAskWaMsg] = useState("");
  const [waAlreadyCaptured, setWaAlreadyCaptured] = useState(false);
  const [askWaComposerOpen, setAskWaComposerOpen] = useState(false);
  const [askWaDraft, setAskWaDraft] = useState("");
  // Cloud recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingPaused, setRecordingPaused] = useState(false);
  const [recordingBusy, setRecordingBusy] = useState(false);
  const [recordingStartedAt, setRecordingStartedAt] = useState(null);
  const lastChatTs = useRef("");
  const lastBcastTs = useRef("");
  const joinReportedRef = useRef(false);
  const lastEnforcement = useRef({
    mic: false,
    cam: false,
    chat: false,
    share: false,
    hand: false,
    lock: false,
  }); // participant-side memory of the last host command it acted on

  // Floating chat bubbles — new messages appear as bubbles over the meeting
  // for 3 seconds; pinned messages stay until unpinned.
  useEffect(() => {
    if (!chatMsgs.length) return;
    // Compute additions OUTSIDE the setState updater — React 18 StrictMode
    // double-invokes updaters, and mutating bubbleSeen inside would cause the
    // second invocation to skip the addition, dropping the bubble.
    const additions = [];
    for (const m of chatMsgs) {
      if (!bubbleSeen.current.has(m.id) || m.pinned) {
        bubbleSeen.current.add(m.id);
        additions.push({ ...m });
      }
    }
    setVisibleBubbles((prev) => {
      const known = new Set(prev.map((b) => b.id));
      const newOnes = additions.filter((a) => !known.has(a.id));
      const next = newOnes.length ? [...prev, ...newOnes] : prev;
      // Reflect the latest pin state on any bubble already visible
      return next.map((b) => {
        const latest = chatMsgs.find((x) => x.id === b.id);
        return latest ? { ...b, pinned: latest.pinned, text: latest.text } : b;
      }).slice(-6); // cap
    });
  }, [chatMsgs]);

  // Time-out non-pinned bubbles after 3 s
  useEffect(() => {
    const nonPinned = visibleBubbles.filter((b) => !b.pinned);
    if (nonPinned.length === 0) return;
    const timers = nonPinned.map((b) =>
      setTimeout(() => {
        setVisibleBubbles((prev) => prev.filter((x) => x.id !== b.id || x.pinned));
      }, 3000)
    );
    return () => timers.forEach(clearTimeout);
  }, [visibleBubbles]);

  // Report join / leave to backend for attendance
  useEffect(() => {
    if (connectionState === ConnectionState.Connected && !joinReportedRef.current) {
      joinReportedRef.current = true;
      api
        .post(`/meetings/${roomName}/events`, {
          event: "joined",
          session_id: userId,
          user_name: userName,
          whatsapp: whatsapp || undefined,
        })
        .catch(() => {});
    }
  }, [connectionState, roomName, userId, userName, whatsapp]);

  // Check whether this participant already has a WhatsApp on file so we can
  // skip the in-class popup for them. Runs once per session.
  useEffect(() => {
    if (!roomName || !userId) return;
    if (whatsapp) {
      setWaAlreadyCaptured(true);
      return;
    }
    api
      .get(`/meetings/${roomName}/wa-status/${userId}`)
      .then(({ data }) => {
        if (data?.captured) setWaAlreadyCaptured(true);
      })
      .catch(() => {});
  }, [roomName, userId, whatsapp]);

  // Cloud-recording status sync — every client polls once on mount so the REC
  // indicator shows on late joiners too. The `recording-started/stopped`
  // data-channel commands keep it in sync after that.
  useEffect(() => {
    if (!roomName) return;
    api
      .get(`/meetings/${roomName}/recording/status`)
      .then(({ data }) => {
        if (data?.recording) {
          setIsRecording(true);
          setRecordingStartedAt(data.started_at || null);
        } else if (data?.paused) {
          setRecordingPaused(true);
        }
      })
      .catch(() => {});
  }, [roomName]);

  useEffect(() => {
    const reportLeft = () => {
      if (!joinReportedRef.current) return;
      navigator.sendBeacon?.(
        `${process.env.REACT_APP_BACKEND_URL}/api/meetings/${roomName}/events`,
        new Blob(
          [JSON.stringify({ session_id: userId, user_name: userName, event: "left" })],
          { type: "application/json" }
        )
      );
    };
    // `pagehide` (not `beforeunload`) is the reliable terminal event on mobile.
    // A hidden/backgrounded page is NOT a leave, so nothing is reported on
    // visibilitychange.
    const onPageHide = (e) => {
      if (e.persisted) return; // going into the back/forward cache — still in the meeting
      reportLeft();
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      // Only an explicit Leave Call reports attendance on unmount. Route
      // changes / remounts must never look like a leave.
      if (getMeetingState().userExplicitlyLeft) reportLeft();
    };
  }, [roomName, userId, userName]);

  // Listen for data messages (chat + broadcast) — real-time via LiveKit
  useEffect(() => {
    if (!room) return;
    const onData = (payload, participant, _kind, topic) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg?.type === "chat" && msg.msg) addChatMessage(msg.msg);
        else if (msg?.type === "broadcast" && msg.msg) showBroadcastPopup(msg.msg);
        else if (msg?.type === "cmd" && !room.localParticipant?.isLocal === false) {
          // Host issued a soft command — participants comply on their end.
          // We only fire user-visible toasts on STATE FLIPS so the host
          // heartbeat (every 4s) doesn't spam participants with duplicates.
          if (msg.cmd === "mute-mic") {
            if (!isHost && !isCoHost) {
              room.localParticipant?.setMicrophoneEnabled(false).catch(() => {});
              if (!lastEnforcement.current.mic) {
                lastEnforcement.current.mic = true;
                setMicForcedOff(true);
                toast("Host muted everyone", { description: "Mic will unlock when the host allows it" });
              }
            }
          } else if (msg.cmd === "turn-off-cam") {
            if (!isHost && !isCoHost) {
              room.localParticipant?.setCameraEnabled(false).catch(() => {});
              if (!lastEnforcement.current.cam) {
                lastEnforcement.current.cam = true;
                setCamForcedOff(true);
                toast("Host turned off everyone's camera");
              }
            }
          } else if (msg.cmd === "ask-unmute") {
            if (!isHost && lastEnforcement.current.mic) {
              lastEnforcement.current.mic = false;
              setMicForcedOff(false);
              toast("Host has unlocked mics — tap the mic icon when ready");
            }
          } else if (msg.cmd === "ask-video-on") {
            if (!isHost && lastEnforcement.current.cam) {
              lastEnforcement.current.cam = false;
              setCamForcedOff(false);
              toast("Host has unlocked cameras — tap the camera icon when ready");
            }
          } else if (msg.cmd === "chat-off") {
            if (!isHost && !lastEnforcement.current.chat) {
              lastEnforcement.current.chat = true;
              setChatLocked(true);
              toast("Host disabled chat");
            }
          } else if (msg.cmd === "wa-btn-on") {
            setWaBtn(true);
          } else if (msg.cmd === "wa-btn-off") {
            setWaBtn(false);
          } else if (msg.cmd === "quality" && msg.quality) {
            setQuality(msg.quality);
          } else if (msg.cmd === "reactions-off") {
            setReactionsOff(true);
          } else if (msg.cmd === "reactions-on") {
            setReactionsOff(false);
          } else if (msg.cmd === "chat-on") {
            if (!isHost && lastEnforcement.current.chat) {
              lastEnforcement.current.chat = false;
              setChatLocked(false);
              toast("Host enabled chat");
            }
          } else if (msg.cmd === "share-off") {
            if (!isHost && !lastEnforcement.current.share) {
              lastEnforcement.current.share = true;
              setShareLocked(true);
              toast("Host disabled screen sharing");
            }
          } else if (msg.cmd === "share-on") {
            if (!isHost && lastEnforcement.current.share) {
              lastEnforcement.current.share = false;
              setShareLocked(false);
              toast("Host enabled screen sharing");
            }
          } else if (msg.cmd === "lock") {
            if (!isHost && !lastEnforcement.current.lock) {
              lastEnforcement.current.lock = true;
              setLocked(true);
              toast("Host locked the meeting — no new joiners");
            }
          } else if (msg.cmd === "unlock") {
            if (!isHost && lastEnforcement.current.lock) {
              lastEnforcement.current.lock = false;
              setLocked(false);
              toast("Host unlocked the meeting");
            }
          } else if (msg.cmd === "host-beat") {
            lastHostBeatRef.current = Date.now();
            if (hostAway) setHostAway(false);
          } else if (msg.cmd === "share-crop") {
            setShareCrop(msg.crop && !isFullCrop(msg.crop) ? msg.crop : null);
          } else if (msg.cmd === "pin" && msg.identity) {
            setPinnedIdentity(msg.identity);
          } else if (msg.cmd === "unpin") {
            setPinnedIdentity(null);
          } else if (msg.cmd === "cohost-set" && Array.isArray(msg.identities)) {
            setCoHosts(new Set(msg.identities));
          } else if (msg.cmd === "mute-target" && msg.identity && room?.localParticipant?.identity === msg.identity) {
            room.localParticipant?.setMicrophoneEnabled(false).catch(() => {});
            toast("Muted by the host");
          } else if (msg.cmd === "cam-off-target" && msg.identity && room?.localParticipant?.identity === msg.identity) {
            room.localParticipant?.setCameraEnabled(false).catch(() => {});
            toast("Camera turned off by the host");
          } else if (msg.cmd === "kick-target" && msg.identity && room?.localParticipant?.identity === msg.identity) {
            toast("You were removed from this class.");
            setTimeout(() => {
              markUserLeft();
              try { const _p = document.exitFullscreen?.(); if (_p && typeof _p.catch === "function") _p.catch(() => {}); } catch (_) {}
              room?.disconnect();
              nav("/goodbye", { replace: true });
            }, 1200);
          } else if (msg.cmd === "end-meeting") {
            toast("The host has ended the meeting");
            setTimeout(() => {
              markUserLeft();
              try { const _p = document.exitFullscreen?.(); if (_p && typeof _p.catch === "function") _p.catch(() => {}); } catch (_) {}
              room?.disconnect();
              // ?ended=1 → the "Thanks for joining!" screen, not "you left".
              nav("/goodbye?ended=1", { replace: true });
            }, 1500);
          } else if (msg.cmd === "ask-whatsapp") {
            if (isHost || isCoHost) return; // host doesn't see their own popup
            if (waAlreadyCaptured) return; // already have their number
            setAskWaMsg((msg.text || "").toString().slice(0, 500));
            setAskWaOpen(true);
          } else if (msg.cmd === "recording-started") {
            setIsRecording(true);
            setRecordingStartedAt(msg.started_at || new Date().toISOString());
          } else if (msg.cmd === "recording-stopped") {
            setIsRecording(false);
            setRecordingStartedAt(null);
          }
        } else if (msg?.type === "hand" && msg.name) {
          pushReaction("\u270B", msg.name);
          if (isHost || isCoHost) toast(`\u270B ${msg.name} raised a hand`, { duration: 6000 });
        } else if (msg?.type === "reaction" && msg.emoji) {
          pushReaction(msg.emoji, msg.name);
        } else if (msg?.type === "chat-del" && msg.id) {
          setChatMsgs((prev) => prev.filter((x) => x.id !== msg.id));
          setVisibleBubbles((prev) => prev.filter((x) => x.id !== msg.id));
        } else if (msg?.type === "poll" && msg.poll && !isHost) {
          if (!answeredPolls.current.has(msg.poll.id)) setActivePoll(msg.poll);
        } else if (msg?.type === "cmd-raise-hand" && typeof msg.disabled === "boolean") {
          setRaiseHandDisabled(msg.disabled);
          if (!isHost && lastEnforcement.current.hand !== msg.disabled) {
            lastEnforcement.current.hand = msg.disabled;
            toast(msg.disabled ? "Raise-hand has been turned off by the host" : "Raise-hand is enabled again");
          }
        } else if (msg?.type === "chat-pin" && msg.id) {
          setChatMsgs((prev) => prev.map((x) => (x.id === msg.id ? { ...x, pinned: msg.pinned } : x)));
        } else if (msg?.type === "kick" && msg.user_name) {
          if (userName === msg.user_name) {
            toast("You have been removed from the meeting by the host");
            setTimeout(() => {
              markUserLeft();
              try { const _p = document.exitFullscreen?.(); if (_p && typeof _p.catch === "function") _p.catch(() => {}); } catch (_) {}
              room?.disconnect();
              nav("/goodbye");
            }, 1200);
          }
        }
      } catch (_) {
        /* ignore malformed */
      }
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => room.off(RoomEvent.DataReceived, onData);
  }, [room]);

  const addChatMessage = useCallback((m) => {
    setChatMsgs((prev) => (prev.find((x) => x.id === m.id) ? prev : [...prev, m]));
    setUnread((u) => u + 1);
  }, []);

  // Announcements and questions are seen ONCE. Kept in localStorage so closing
  // the browser and coming back never re-pops something already shown.
  const seenKey = `udyog_seen_cards_${roomName}`;
  const rememberSeen = useCallback(
    (id) => {
      try {
        const list = JSON.parse(localStorage.getItem(seenKey) || "[]");
        if (!list.includes(id)) {
          localStorage.setItem(seenKey, JSON.stringify([...list.slice(-80), id]));
        }
      } catch (_) {}
    },
    [seenKey]
  );
  useEffect(() => {
    try {
      JSON.parse(localStorage.getItem(seenKey) || "[]").forEach((id) => {
        seenBroadcasts.current.add(id);
        answeredPolls.current.add(id);
      });
    } catch (_) {}
  }, [seenKey]);

  const showBroadcastPopup = useCallback((m) => {
    if (!m || seenBroadcasts.current.has(m.id) || dismissedBroadcasts.current.has(m.id)) return;
    // A card addressed to one student is only that student's business.
    if (m.to_user_name && m.to_user_name.trim().toLowerCase() !== userName.trim().toLowerCase()) {
      seenBroadcasts.current.add(m.id);
      return;
    }
    // A card is a live moment. If its time on screen has already passed it must
    // never pop — not on a reconnect, not for someone who joined late.
    if (m.expires_at && new Date(m.expires_at).getTime() <= Date.now()) {
      seenBroadcasts.current.add(m.id);
      rememberSeen(m.id);
      return;
    }
    seenBroadcasts.current.add(m.id);
    rememberSeen(m.id);
    setPoppedBroadcast(m);
  }, [rememberSeen, userName]);

  const dismissBroadcast = useCallback(() => {
    setPoppedBroadcast((current) => {
      if (current) dismissedBroadcasts.current.add(current.id);
      return null;
    });
  }, []);

  // Auto-dismiss when the card's own lifetime is up (host-set, default 25s).
  useEffect(() => {
    if (!poppedBroadcast) return;
    const ms = poppedBroadcast.expires_at
      ? new Date(poppedBroadcast.expires_at).getTime() - Date.now()
      : (poppedBroadcast.duration_sec || 25) * 1000;
    const t = setTimeout(() => dismissBroadcast(), Math.max(1500, ms));
    return () => clearTimeout(t);
  }, [poppedBroadcast, dismissBroadcast]);

  // Poll chat + broadcasts (backup path + persistence)
  useEffect(() => {
    if (!roomName) return;
    const poll = async () => {
      try {
        const [c, b] = await Promise.all([
          api.get(`/meetings/${roomName}/chat`, {
            params: lastChatTs.current ? { since: lastChatTs.current } : {},
          }),
          api.get(`/meetings/${roomName}/broadcasts`, {
            params: lastBcastTs.current ? { since: lastBcastTs.current } : {},
          }),
        ]);
        c.data
          .filter((m) => m.kind === "chat")
          .forEach((m) => {
            addChatMessage(m);
            lastChatTs.current = m.created_at;
          });
        b.data.forEach((m) => {
          showBroadcastPopup(m);
          lastBcastTs.current = m.created_at;
        });
      } catch (_) {
        /* poll error */
      }
    };
    poll();
    const t = setInterval(poll, 2500);
    return () => clearInterval(t);
  }, [roomName, addChatMessage, showBroadcastPopup]);

  const publishData = useCallback(
    async (obj) => {
      try {
        if (!room?.localParticipant) return;
        await room.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify(obj)),
          { reliable: true }
        );
      } catch (_) {
        /* not connected yet */
      }
    },
    [room]
  );

  // Host heartbeat — every 4s re-broadcast enforcement state so any late
  // joiners immediately fall in line with the current mute/cam/lock rules.
  useEffect(() => {
    if (!isHost) return;
    const beat = () => {
      if (micsMuted) publishData({ type: "cmd", cmd: "mute-mic" });
      if (camsOff) publishData({ type: "cmd", cmd: "turn-off-cam" });
      if (chatLocked) publishData({ type: "cmd", cmd: "chat-off" });
      if (shareLocked) publishData({ type: "cmd", cmd: "share-off" });
      if (reactionsOff) publishData({ type: "cmd", cmd: "reactions-off" });
      if (locked) publishData({ type: "cmd", cmd: "lock" });
      if (raiseHandDisabled) publishData({ type: "cmd-raise-hand", disabled: true });
      // Late joiners mid-share must land on the same framing as everyone else.
      // Nothing to send when the crop is baked into the capture itself.
      if (shareCrop)
        publishData({ type: "cmd", cmd: "share-crop", crop: shareCrop });
    };
    beat();
    const t = setInterval(beat, 4000);
    return () => clearInterval(t);
  }, [isHost, micsMuted, camsOff, chatLocked, shareLocked, locked, raiseHandDisabled, reactionsOff, shareCrop, publishData]);

  // Host: poll lobby list
  useEffect(() => {
    if (!isHost) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/meetings/${roomName}/lobby`);
        setLobby(r.data || []);
      } catch (_) {}
    }, 3000);
    return () => clearInterval(t);
  }, [roomName, isHost]);

  // Everyone: poll active-poll to render Q&A card
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/meetings/${roomName}/polls/active`);
        if (r.data && r.data.id && !isHost) {
          // Skip questions already answered/seen, and any whose time is up.
          if (answeredPolls.current.has(r.data.id)) return;
          if (r.data.expires_at && new Date(r.data.expires_at).getTime() <= Date.now()) {
            answeredPolls.current.add(r.data.id);
            rememberSeen(r.data.id);
            return;
          }
          rememberSeen(r.data.id);
          setActivePoll((prev) => (prev?.id === r.data.id ? prev : r.data));
        }
      } catch (_) {}
    }, 2500);
    return () => clearInterval(t);
  }, [roomName, isHost, rememberSeen]);

  // A question closes itself when its time on screen is over.
  useEffect(() => {
    if (!activePoll?.expires_at) return;
    const ms = new Date(activePoll.expires_at).getTime() - Date.now();
    const t = setTimeout(() => {
      answeredPolls.current.add(activePoll.id);
      setActivePoll(null);
    }, Math.max(1500, ms));
    return () => clearTimeout(t);
  }, [activePoll]);

  const admitFromLobby = async (user_name, admit) => {
    try {
      await api.post(`/meetings/${roomName}/lobby/decide`, { user_name, admit });
      setLobby((prev) => prev.filter((x) => x.user_name !== user_name));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const admitAllLobby = async () => {
    const targets = [...lobby];
    if (targets.length === 0) return;
    // Optimistically clear the list.
    setLobby([]);
    for (const l of targets) {
      try {
        await api.post(`/meetings/${roomName}/lobby/decide`, { user_name: l.user_name, admit: true });
      } catch (_) {}
    }
    toast.success(`Admitted ${targets.length} ${targets.length === 1 ? "person" : "people"}`);
  };

  const admitTrustedLobby = async () => {
    // "Trusted" = anyone who supplied a valid 10+ digit WhatsApp number.
    // Everyone else stays in the lobby for the host to review.
    const trusted = lobby.filter((l) => (l.whatsapp || "").replace(/\D/g, "").length >= 10);
    if (trusted.length === 0) {
      toast("No trusted numbers to admit");
      return;
    }
    setLobby((prev) => prev.filter((l) => (l.whatsapp || "").replace(/\D/g, "").length < 10));
    for (const l of trusted) {
      try {
        await api.post(`/meetings/${roomName}/lobby/decide`, { user_name: l.user_name, admit: true });
      } catch (_) {}
    }
    toast.success(`Admitted ${trusted.length} trusted ${trusted.length === 1 ? "number" : "numbers"}`);
  };

  const [hostPoll, setHostPoll] = useState(null); // {id, question, options} — most recent poll launched by host
  const [pollTally, setPollTally] = useState({ counts: {}, total: 0, rows: [] });
  const [openTallyKey, setOpenTallyKey] = useState(null);

  const launchPoll = async () => {
    if (!pollDraft.question.trim()) return;
    try {
      const { data } = await api.post(`/meetings/${roomName}/polls`, pollDraft);
      publishData({ type: "poll", poll: data });
      setHostPoll(data);
      setPollTally({ counts: {}, total: 0, rows: [] });
      toast.success("Question sent to everyone");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Live question tally — visible to host + co-host only. Polls the
  // results endpoint every 2 s while a question is active. Nothing is ever
  // sent to participants — they see only their own follow-up card.
  useEffect(() => {
    if (!(isHost || isCoHost) || !hostPoll?.id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/meetings/${roomName}/polls/${hostPoll.id}/results`);
        if (cancelled) return;
        const counts = {};
        (data.answers || []).forEach((a) => {
          counts[a.choice] = (counts[a.choice] || 0) + 1;
        });
        setPollTally({ counts, total: (data.answers || []).length, rows: data.answers || [] });
      } catch (_) {}
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [isHost, isCoHost, hostPoll?.id, roomName]);

  const answerPoll = async (choice) => {
    try {
      const pollId = activePoll.id;
      answeredPolls.current.add(pollId);
      rememberSeen(pollId);
      const { data } = await api.post(`/meetings/${roomName}/polls/${pollId}/answer`, {
        user_name: userName,
        choice,
      });
      setActivePoll(null);
      if (
        data.follow_up &&
        (data.follow_up.follow_up_text || data.follow_up.follow_up_link || data.follow_up.follow_up_pay)
      ) {
        setPollAnswer({ ...data.follow_up, poll_id: pollId });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDeleteChat = async (m) => {
    if (!confirm(`Delete this message from ${m.user_name}?`)) return;
    try {
      await api.delete(`/meetings/${roomName}/chat/${m.id}`, { data: { user_name: userName } });
      setChatMsgs((prev) => prev.filter((x) => x.id !== m.id));
      setVisibleBubbles((prev) => prev.filter((x) => x.id !== m.id));
      publishData({ type: "chat-del", id: m.id });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onPinChat = async (m) => {
    try {
      const { data } = await api.post(`/meetings/${roomName}/chat/${m.id}/pin`);
      setChatMsgs((prev) => prev.map((x) => (x.id === m.id ? { ...x, pinned: data.pinned } : x)));
      publishData({ type: "chat-pin", id: m.id, pinned: data.pinned });
      toast.success(data.pinned ? "Pinned to top" : "Unpinned");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onBanUser = async (m) => {
    if (!confirm(`Ban ${m.user_name} from this meeting? They can't rejoin.`)) return;
    try {
      await api.post(`/meetings/${roomName}/ban`, { user_name: m.user_name });
      publishData({ type: "kick", user_name: m.user_name });
      toast.success(`${m.user_name} has been banned`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendChat = async (e) => {
    e.preventDefault();
    const text = chatText.trim();
    if (!text) return;
    setChatText("");
    try {
      const { data } = await api.post(`/meetings/${roomName}/chat`, {
        user_name: userName,
        text,
      });
      addChatMessage(data);
      publishData({ type: "chat", msg: data });
    } catch (e2) {
      toast.error(formatApiError(e2));
    }
  };

  const sendBroadcast = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const text = broadcastText.trim();
    if (!text) return;
    try {
      const { data } = await api.post(`/meetings/${roomName}/broadcast`, {
        text,
        link: broadcastLink.trim() || null,
        button_label: broadcastLabel.trim() || null,
        banner_url: broadcastBanner || null,
        duration_sec: broadcastDuration || 25,
        pay_button: broadcastPay,
      });
      publishData({ type: "broadcast", msg: data });
      toast.success("Broadcast sent");
      setBroadcastText("");
      setBroadcastLink("");
      setBroadcastBanner(null);
      setShowHostPanel(false);
    } catch (e2) {
      const code = e2?.response?.status;
      if (code === 401 || code === 403) {
        toast.error("Session expired — please sign in again to broadcast", {
          action: {
            label: "Sign in",
            onClick: () => nav(`/login?next=${encodeURIComponent(`/meet/${roomName}?host=1`)}`),
          },
        });
      } else {
        toast.error(formatApiError(e2));
      }
    }
  };

  // Host fires the "Ask for WhatsApp" popup on every participant that
  // doesn't have a number on file yet.
  const askForWhatsApp = () => {
    const text = askWaDraft.trim();
    publishData({ type: "cmd", cmd: "ask-whatsapp", text });
    toast.success("WhatsApp request sent — participants will see the popup");
    setAskWaComposerOpen(false);
    setAskWaDraft("");
    setShowHostPanel(false);
  };

  // Participant submits their WhatsApp number via the in-class popup.
  const submitMyWhatsApp = async (rawNumber) => {
    const digits = (rawNumber || "").replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Enter a valid 10-digit WhatsApp number");
      return false;
    }
    try {
      await api.post(`/meetings/${roomName}/wa-submissions`, {
        session_id: userId,
        user_name: userName,
        whatsapp: digits,
      });
      setWaAlreadyCaptured(true);
      setAskWaOpen(false);
      toast.success("Thanks — your number is saved");
      return true;
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't submit your number");
      return false;
    }
  };

  // Cloud recording — LiveKit Egress
  const startRecording = async () => {
    if (recordingBusy) return;
    setRecordingBusy(true);
    try {
      const { data } = await api.post(`/meetings/${roomName}/recording/start`, {});
      const started = data?.started_at || new Date().toISOString();
      setIsRecording(true);
      setRecordingStartedAt(started);
      publishData({ type: "cmd", cmd: "recording-started", started_at: started });
      toast.success(data?.reused ? "Recording already in progress" : "Recording started");
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 503) {
        toast.error("Cloud recording isn't switched on yet", {
          description:
            detail || "The recorder service (LiveKit Egress) needs to be running on your server.",
        });
      } else if (code === 409) {
        toast.error("Wait for participants to join first", {
          description: detail || "Recording can only start once the meeting room is live.",
        });
      } else {
        toast.error("Cloud recording isn't switched on yet", {
          description: detail
            ? `Recorder said: ${detail}`
            : formatApiError(e) || "Couldn't reach the recorder service.",
        });
      }
    } finally {
      setRecordingBusy(false);
    }
  };
  const stopRecording = async () => {
    if (recordingBusy) return;
    setRecordingBusy(true);
    // Flip the UI the instant they ask — the recorder still needs a couple of
    // seconds to flush the MP4 to the bucket, and staring at a live REC dot
    // during that made it feel like the stop hadn't registered.
    setIsRecording(false);
    setRecordingPaused(false);
    setRecordingStartedAt(null);
    publishData({ type: "cmd", cmd: "recording-stopped" });
    try {
      await api.post(`/meetings/${roomName}/recording/stop`, {});
      toast.success("Recording stopped", { description: "Writing the MP4 to your bucket — a few seconds." });
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't stop recording");
    } finally {
      setRecordingBusy(false);
    }
  };

  // LiveKit's recorder has no true pause, so a pause closes the current part
  // and a resume opens the next one. Nothing is lost, no dead air recorded.
  const pauseRecording = async () => {
    if (recordingBusy) return;
    setRecordingBusy(true);
    setIsRecording(false);
    setRecordingPaused(true);
    publishData({ type: "cmd", cmd: "recording-stopped" });
    try {
      const { data } = await api.post(`/meetings/${roomName}/recording/pause`, {});
      toast.success(`Paused — part ${data?.part || 1} saved`, {
        description: "Hit resume and the next part starts a fresh file.",
      });
    } catch (e) {
      setRecordingPaused(false);
      setIsRecording(true);
      toast.error(formatApiError(e) || "Couldn't pause the recording");
    } finally {
      setRecordingBusy(false);
    }
  };

  const resumeRecording = async () => {
    if (recordingBusy) return;
    setRecordingBusy(true);
    try {
      const { data } = await api.post(`/meetings/${roomName}/recording/resume`, {});
      const started = data?.started_at || new Date().toISOString();
      setIsRecording(true);
      setRecordingPaused(false);
      setRecordingStartedAt(started);
      publishData({ type: "cmd", cmd: "recording-started", started_at: started });
      toast.success(`Recording again — part ${data?.part || 2}`);
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't resume the recording");
    } finally {
      setRecordingBusy(false);
    }
  };

  const [templates, setTemplates] = useState([]);
  const loadTemplates = useCallback(() => {
    if (!isHost) return;
    api
      .get("/templates")
      .then(({ data }) => setTemplates(data?.templates || []))
      .catch(() => {});
  }, [isHost]);
  useEffect(() => {
    if (showHostPanel) loadTemplates();
  }, [showHostPanel, loadTemplates]);

  // Payment nudge — who opened the fees checkout and never confirmed.
  const [payPending, setPayPending] = useState(null);
  const [nudgeText, setNudgeText] = useState("");
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const loadPayPending = useCallback(() => {
    if (!isHost) return;
    api
      .get(`/meetings/${roomName}/pay/pending`)
      .then(({ data }) => setPayPending(data))
      .catch(() => {});
  }, [isHost, roomName]);
  useEffect(() => {
    if (!showHostPanel) return;
    loadPayPending();
    const t = setInterval(loadPayPending, 15000);
    return () => clearInterval(t);
  }, [showHostPanel, loadPayPending]);

  const sendPayNudge = async () => {
    setNudgeBusy(true);
    try {
      const { data } = await api.post(`/meetings/${roomName}/pay/nudge`, {
        text: nudgeText.trim() || null,
        duration_sec: 30,
        only_in_room: true,
      });
      (data.cards || []).forEach((c) => publishData({ type: "broadcast", msg: c }));
      toast.success(`Nudged ${data.sent} ${data.sent === 1 ? "student" : "students"} privately`);
      setNudgeText("");
      loadPayPending();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNudgeBusy(false);
    }
  };

  const applyBroadcastTemplate = (t) => {
    setBroadcastText(t.text || "");
    setBroadcastLink(t.link || "");
    setBroadcastLabel(t.button_label || "Open link");
    setBroadcastBanner(t.banner_url || null);
    setBroadcastDuration(t.duration_sec || 25);
    toast(`Loaded "${t.title}" — tweak it or send as is`);
  };

  const applyQuestionTemplate = (t) => {
    setPollDraft({
      question: t.text || t.title || "",
      banner_url: t.banner_url || null,
      duration_sec: t.duration_sec || 90,
      options: (t.options || []).map((o) => ({
        key: o.key,
        label: o.label || "",
        follow_up_text: o.follow_up_text || "",
        follow_up_link: o.follow_up_link || "",
        follow_up_button: o.follow_up_button || "",
        follow_up_pay: !!o.follow_up_pay,
      })),
    });
    toast(`Loaded "${t.title}"`);
  };

  const saveAsTemplate = async (kind) => {
    const payload =
      kind === "broadcast"
        ? {
            kind,
            title: (broadcastText.trim().slice(0, 60) || "Announcement"),
            text: broadcastText.trim(),
            link: broadcastLink.trim() || null,
            button_label: broadcastLabel.trim() || null,
            banner_url: broadcastBanner || null,
            duration_sec: broadcastDuration || 25,
            options: [],
          }
        : {
            kind,
            title: (pollDraft.question.trim().slice(0, 60) || "Question"),
            text: pollDraft.question.trim(),
            banner_url: pollDraft.banner_url || null,
            duration_sec: pollDraft.duration_sec || 90,
            options: pollDraft.options,
          };
    if (!payload.text) {
      toast.error("Write it first, then save it as a template");
      return;
    }
    try {
      await api.post("/templates", payload);
      toast.success("Saved to your templates");
      loadTemplates();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // A portrait share fills the stage, so reserve a black strip at the bottom
  // for the presenter pill and chat bubbles rather than letting them cover the
  // content. Only while the controls are up — they all fade together.
  const portraitShare = shareAspect > 0 && shareAspect < 1.05;

  const pinnedRef = useRef(null);
  useEffect(() => {
    pinnedRef.current = pinnedIdentity;
  }, [pinnedIdentity]);

  // Whoever is running the class owns the stage by default: the host, or the
  // first co-host once the host is gone. Nobody ever looks at an empty grid.
  const hostIdentity = useMemo(
    () => participants.find((p) => isHostParticipant(p))?.identity || null,
    [participants]
  );
  const firstCoHost = useMemo(() => {
    const ids = Array.from(coHosts);
    return ids.find((id) => participants.some((p) => p.identity === id)) || null;
  }, [coHosts, participants]);
  const stageOwner = hostIdentity || firstCoHost;
  const stageOwnerRef = useRef(null);
  useEffect(() => {
    stageOwnerRef.current = stageOwner;
  }, [stageOwner]);

  // Auto-pin the stage owner. Re-runs whenever the owner changes (host leaves →
  // co-host takes the stage; host returns → the stage comes back to them) and
  // whenever the pinned person has left the class.
  const lastOwnerRef = useRef(null);
  useEffect(() => {
    if (!stageOwner) return;
    const ownerChanged = lastOwnerRef.current !== stageOwner;
    const pinnedGone =
      !pinnedIdentity ||
      (participants.length > 0 && !participants.some((p) => p.identity === pinnedIdentity));
    if (ownerChanged || pinnedGone) {
      lastOwnerRef.current = stageOwner;
      if (pinnedIdentity !== stageOwner) {
        setPinnedIdentity(stageOwner);
        // Persist so late joiners and the cloud recorder land on the same
        // stage. Only the host owns the server copy.
        if (isHost) {
          api.post(`/meetings/${roomName}/pin`, { identity: stageOwner }).catch(() => {});
        }
      }
    }
  }, [stageOwner, pinnedIdentity, participants, isHost, roomName]);

  // One entry point for every pin / unpin: local stage, live broadcast to the
  // class, and the server copy that late joiners + the recorder read.
  const applyPin = useCallback(
    (id, label) => {
      const target = id || stageOwnerRef.current || null;
      setPinnedIdentity(target);
      publishData(
        target ? { type: "cmd", cmd: "pin", identity: target } : { type: "cmd", cmd: "unpin" }
      );
      if (isHost) {
        api.post(`/meetings/${roomName}/pin`, { identity: target }).catch(() => {});
      }
      if (label) toast(label);
    },
    [publishData, isHost, roomName]
  );

  // A pin is for the whole class until it's explicitly removed. The pin command
  // is a one-off broadcast, so anyone joining, reloading or reconnecting later
  // (including the cloud recorder) would miss it. The host re-announces the
  // current pin whenever someone connects and on a slow heartbeat.
  useEffect(() => {
    if (!room || !(isHost || isCoHost)) return;
    const announce = () => {
      const id = pinnedRef.current;
      publishData(id ? { type: "cmd", cmd: "pin", identity: id } : { type: "cmd", cmd: "unpin" });
    };
    const onJoin = () => setTimeout(announce, 1200);
    room.on("participantConnected", onJoin);
    const timer = setInterval(announce, 15000);
    return () => {
      room.off("participantConnected", onJoin);
      clearInterval(timer);
    };
  }, [room, isHost, isCoHost, publishData]);

  const wantFullscreenRef = useRef(false);

  // Keep `fsOn` honest. It used to drift out of sync (minimise the browser and
  // Android drops fullscreen without telling React), which made EVERY tap on
  // the stage fire another fullscreen request — so taps were spent on the
  // fullscreen transition instead of bringing the controls back.
  useEffect(() => {
    const sync = () =>
      setFsOn(
        !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        )
      );
    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const goFullscreen = useCallback(() => {
    const el = document.documentElement;
    const isFs =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement;
    if (!isFs) {
      wantFullscreenRef.current = true;
      // Some browsers (mobile Safari, in-app WebViews) don't expose the
      // Fullscreen API; fall back to browser-native full window (F11-style).
      const req =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.webkitRequestFullScreen ||
        el.msRequestFullscreen;
      if (typeof req === "function") {
        Promise.resolve(req.call(el, { navigationUI: "hide" })).catch(() =>
          toast("Full-screen isn't supported on this browser", {
            description: "Try opening the meeting in Chrome or Safari on desktop.",
          })
        );
      } else {
        toast("Full-screen isn't supported on this browser", {
          description: "Try opening the meeting in Chrome or Safari on desktop.",
        });
      }
    } else {
      wantFullscreenRef.current = false;
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen;
      if (typeof exit === "function") exit.call(document);
    }
  }, []);

  // Android's Back press drops you out of full screen and out of the page. When
  // you come back, full screen is gone — but re-requesting it needs a gesture,
  // so we arm it for your very next tap and the class is immersive again.
  useEffect(() => {
    const inFullscreen = () =>
      !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
    const reenter = () => {
      if (!wantFullscreenRef.current || inFullscreen()) return;
      const el = document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
      if (typeof req === "function") Promise.resolve(req.call(el, { navigationUI: "hide" })).catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      fsRetryRef.current = true;
      if (!wantFullscreenRef.current || inFullscreen()) return;
      window.addEventListener("pointerdown", reenter, { once: true, capture: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pointerdown", reenter, true);
    };
  }, []);

  // The lock has to live on the SERVER, not just in the browsers that happen to
  // be in the room — otherwise a newcomer with the link walks straight in.
  const persistLock = async (next) => {
    try {
      await api.patch(`/meetings/${roomName}/settings`, { locked: next });
    } catch (_) {
      toast.error("Couldn't save the lock — please try again");
    }
  };

  // ...and the host's own toggle must show the truth after a reload.
  useEffect(() => {
    if (!isHost) return;
    api
      .get(`/public/meetings/${roomName}`)
      .then(({ data }) => setLocked(!!data?.locked))
      .catch(() => {});
  }, [isHost, roomName]);

  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const leave = () => {
    try {
      sessionStorage.removeItem("udyog_active_class");
    } catch (_) {}
    // Always confirm — host gets End vs Leave, participants get a simple confirm.
    setShowLeaveDialog(true);
  };
  const leaveMyself = () => {
    setShowLeaveDialog(false);
    markUserLeft(); // hard stop: no PiP after an explicit leave
    try {
      // So "Join the class again" always has a real destination, and the class
      // survives them closing the tab entirely. Their details are parked too,
      // so rejoining never asks them to type anything again.
      localStorage.setItem("udyog_last_join", `/join/${roomName}`);
      if (!isHost && !isCoHost)
        localStorage.setItem(
          `udyog_rejoin:${roomName}`,
          JSON.stringify({ name: userName, whatsapp: whatsapp || "", ts: Date.now() })
        );
    } catch (_) {}
    try {
      const p = document.exitFullscreen?.();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch (_) {}
    room?.disconnect();
    // `replace` matters: without it, the browser's back gesture from the
    // goodbye screen dropped them straight back into the class they just left.
    nav(isHost ? "/admin" : "/goodbye", { replace: true });
  };
  const endForEveryone = async () => {
    setShowLeaveDialog(false);
    markUserLeft();
    publishData({ type: "cmd", cmd: "end-meeting" });
    try {
      await api.delete(`/meetings/${roomName}`);
    } catch (_) {}
    toast.success("Meeting ended for everyone");
    setTimeout(() => {
      room?.disconnect();
      nav("/admin");
    }, 800);
  };

  // Host emits a heartbeat every 3 s so co-hosts know the real host is
  // still connected. If it stops for 12 s, co-hosts see the acting-host
  // banner. Non-hosts skip this entirely.
  useEffect(() => {
    if (!isHost) return;
    const id = setInterval(() => {
      publishData({ type: "cmd", cmd: "host-beat" });
    }, 3000);
    return () => clearInterval(id);
  }, [isHost, publishData]);

  // Co-host side — poll last-seen timestamp. If > 12 s of silence, flag
  // the host as away.
  useEffect(() => {
    if (isHost) return;
    if (!isCoHost) return;
    const id = setInterval(() => {
      const idle = Date.now() - lastHostBeatRef.current;
      setHostAway(idle > 12000);
    }, 2000);
    return () => clearInterval(id);
  }, [isHost, isCoHost]);

  // Host dropped off → the first co-host automatically runs the class (they
  // already own the stage via stageOwner). The class never ends. When the
  // host's heartbeat returns they silently take charge again.
  const [actingHost, setActingHost] = useState(false);
  useEffect(() => {
    if (isHost) return;
    const iAmFirst = !!firstCoHost && firstCoHost === localIdentity;
    if (hostAway && iAmFirst && !actingHost) {
      setActingHost(true);
      setLocalHostRole(true);
      toast("You're running the class now — the host dropped off");
    } else if (actingHost && (!hostAway || !iAmFirst)) {
      setActingHost(false);
      setLocalHostRole(isCoHost);
      toast("The host is back");
    }
  }, [hostAway, firstCoHost, localIdentity, isHost, isCoHost, actingHost]);
  // floating window YouTube / Google Meet use — it survives minimising the
  // browser or switching apps. We pick the largest playing <video> element
  // (screen share > active camera) and pop it out.
  const enterNativePip = useCallback(async () => {
    const armed = isFloatingArmed();
    if (armed) {
      disarmFloating();
      toast("Floating window off", { description: "The class won't pop out when you leave" });
      return false;
    }
    const res = await armFloating();
    if (res.ok) {
      toast.success("Floating window on", {
        description: "The class keeps playing when you minimise or go back",
      });
      return true;
    }
    toast("Floating window not available", {
      description:
        res.error === "unsupported"
          ? "This browser doesn't support Picture-in-Picture"
          : "Your browser blocked it — tap the button again",
    });
    return false;
  }, []);

  // Keep the meeting session flagged active for the lifecycle manager and tag
  // rendered <video> elements (installed-PWA auto-PiP hint).
  useEffect(() => {
    startMeetingSession();
    tagVideosForAutoPip();
    const id = setInterval(tagVideosForAutoPip, 2500);
    return () => {
      clearInterval(id);
      endMeetingSession();
    };
  }, []);

  // Intercept the browser back-button. If a drawer / modal is open, close
  // that first. Otherwise trigger native browser Picture-in-Picture so the
  // meeting continues in a floating OS window (survives the user closing
  // the tab / going home / task-switching).
  const [showEveryone, setShowEveryone] = useState(false);
  useEffect(() => {
    const onPop = () => {
      if (backSheet) {
        setBackSheet(false);
      } else if (showEveryone) {
        setShowEveryone(false);
      } else if (showChat) {
        setShowChat(false);
      } else if (showHostPanel) {
        setShowHostPanel(false);
      } else if (showRoster) {
        setShowRoster(false);
      } else if (pollAnswer) {
        setPollAnswer(null);
      } else if (activePoll && !isHost) {
        // Poll modal blocks input — don't let back leave the meeting.
      } else if (poppedBroadcast) {
        setPoppedBroadcast(null);
      } else {
        // No modal open — the user is navigating away from the meeting page.
        // The history guard keeps the meeting mounted (so the connection is
        // untouched) and the lifecycle manager tries to pop the video into a
        // floating window. If the browser refuses (no user activation) the
        // request is queued and retried on the next tap — never a leave.
        handleNavigationAway("popstate");
        // And show the "you're still in class" card. Its buttons give us a
        // real user gesture, which is the only way a browser lets us open a
        // floating window.
        setBackSheet(true);
      }
    };
    window.addEventListener("udyog-back", onPop);
    return () => window.removeEventListener("udyog-back", onPop);
  }, [showChat, showHostPanel, showRoster, pollAnswer, activePoll, isHost, poppedBroadcast, backSheet, showEveryone]);

  // Auto-PiP on background / restore on foreground is owned entirely by the
  // lifecycle manager (see lib/meetingPip.js) — page visibility never affects
  // the meeting connection.

  // Split tracks: screen shares (focus) vs cameras (thumbnails)
  const screenTracks = useMemo(() => tracks.filter((t) => t.source === Track.Source.ScreenShare), [tracks]);
  const cameraTracks = useMemo(() => tracks.filter((t) => t.source === Track.Source.Camera), [tracks]);
  const isSharing = screenTracks.length > 0;
  const iAmPresenting = useMemo(() => screenTracks.some((t) => t.participant?.isLocal), [screenTracks]);

  // The moment the host starts sharing, every student goes full screen so the
  // slide gets the whole phone. Chrome only grants full screen off a recent
  // tap, so when it refuses we put up a one-tap pill instead of failing
  // silently.
  const [fsPrompt, setFsPrompt] = useState(false);
  useEffect(() => {
    if (!isSharing || iAmPresenting || pwaIsFullscreen() || !isFullscreenEligible()) {
      setFsPrompt(false);
      return;
    }
    let alive = true;
    let hide = 0;
    goFullscreenNow(true).then((ok) => {
      if (!alive) return;
      setFsOn(!!ok);
      setFsPrompt(!ok);
      if (!ok) hide = setTimeout(() => alive && setFsPrompt(false), 20000);
    });
    return () => {
      alive = false;
      clearTimeout(hide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharing, iAmPresenting]);

  // Every new share starts on the full screen — a crop from the last share
  // would frame the wrong thing.
  const shareKey = screenTracks[0]?.publication?.trackSid || "";
  useEffect(() => {
    setShareCrop(null);
    setCropEditing(false);
    setVideoAspect(0);
    setShareAspect(0);
  }, [shareKey]);

  const applyCrop = (next) => {
    setShareCrop(next);
    setCropEditing(false);
    setSavedCrops(loadSavedCrops());
    publishData({ type: "cmd", cmd: "share-crop", crop: next });
    toast(next ? "Everyone is now seeing your crop" : "Back to the full screen for everyone");
  };

  useEffect(() => {
    const speaking = activeSpeakers.find((p) => !!p);
    if (speaking) setDominantIdentity(speaking.identity);
  }, [activeSpeakers]);

  const openChat = () => {
    setShowChat((v) => {
      if (!v) setUnread(0);
      return !v;
    });
  };

  // Focus mode — during screen share, auto-hide the top bar + bottom dock
  // after 4 seconds of no user tap. Any tap on the stage brings the chrome
  // back for another 4 seconds. When NOT sharing, chrome stays visible.
  const [chromeVisible, setChromeVisible] = useState(true);
  const [viewportEpoch, setViewportEpoch] = useState(0);
  // Real measured aspect of the box the slide sits in — guessing it from the
  // window was wrong whenever the chat panel or the header changed the box.
  const [stageAspect, setStageAspect] = useState(0);
  const shareBoxObs = useRef(null);
  const shareBoxRef = useCallback((el) => {
    shareBoxObs.current?.disconnect();
    shareBoxObs.current = null;
    if (!el) return;
    const read = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        const a = r.width / r.height;
        setStageAspect((p) => (Math.abs(p - a) > 0.02 ? a : p));
      }
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    shareBoxObs.current = ro;
  }, []);
  // How the shared slide sits in the stage. Filling removes the black bars,
  // but it must never hide a meaningful part of the slide — past a quarter of
  // the picture we fall back to showing all of it.
  const shareFit = useMemo(() => {
    if (shareFitMode === "fit") return "contain";
    if (shareFitMode === "fill") return "cover";
    const stageA = stageAspect || (window.innerWidth || 1) / Math.max(1, window.innerHeight || 1);
    if (shareAspect > 0) {
      const loss = 1 - Math.min(shareAspect, stageA) / Math.max(shareAspect, stageA);
      if (loss > 0.25) return "contain";
    }
    return "cover";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareFitMode, shareAspect, stageAspect, viewportEpoch]);
  const toggleShareFill = () => {
    const next = shareFit === "cover" ? "fit" : "fill";
    setShareFitMode(next);
    try {
      localStorage.setItem("udyog_share_fit", next);
    } catch (_) {}
  };
  const stageScrollRef = useRef(null);
  // Host controls popover — lives in the top-right of the header.
  const [showHostToggles, setShowHostToggles] = useState(false);
  useEffect(() => {
    if (!showHostToggles) return;
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      if (
        e.type === "pointerdown" &&
        e.target?.closest?.('[data-testid="host-toggles"], [data-testid="host-controls-open-btn"]')
      )
        return;
      setShowHostToggles(false);
    };
    const t = setTimeout(() => {
      document.addEventListener("pointerdown", close);
      document.addEventListener("keydown", close);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [showHostToggles]);
  // Landscape / fullscreen fill toggle for shared content — contain (letterbox
  // to preserve slides' aspect) vs cover (crop-to-fill for immersive view).
  // Google-Meet-style paging: swipe the stage left to reach the tiles page,
  // swipe right on the tiles page to come back.
  const swipeRef = useRef(null);
  const onStageTouchStart = (e) => {
    if (e.touches.length !== 1) {
      swipeRef.current = null;
      return;
    }
    const t = e.touches[0];
    swipeRef.current = { x: t.clientX, y: t.clientY, at: Date.now() };
  };
  const onStageTouchMove = (e) => {
    if (e.touches.length > 1) swipeRef.current = null; // pinch, not a page swipe
  };
  const onStageTouchEnd = (e) => {
    const s = swipeRef.current;
    swipeRef.current = null;
    const t = e.changedTouches?.[0];
    if (!s || !t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (dx > -64 || Math.abs(dx) < Math.abs(dy) * 2 || Date.now() - s.at > 700) return;
    // A pinch-zoomed slide owns the gesture — don't steal its pan.
    const z = e.target?.closest?.("[data-zoom]");
    if (z && parseFloat(z.getAttribute("data-zoom") || "1") > 1.01) return;
    setShowEveryone(true);
  };
  // Finding one student among 600 needs a search box, not a scroll.
  const [rosterQuery, setRosterQuery] = useState("");
  const rosterShown = useMemo(() => {
    const q = rosterQuery.trim().toLowerCase();
    if (!q) return participants;
    return participants.filter((p) => (p.name || p.identity).toLowerCase().includes(q));
  }, [participants, rosterQuery]);
  const [shortLandscape, setShortLandscape] = useState(false);
  // Data-saver mode — auto-enabled when the local participant's connection
  // quality drops to "poor" for 5+ seconds. Camera tiles get hidden; audio
  // continues seamlessly. User can override with the banner button.
  const [dataSaver, setDataSaver] = useState(false);
  const poorSinceRef = useRef(0);
  const chromeTimerRef = useRef(null);
  // Controls only auto-hide for VIEWERS on touch devices while someone else is
  // sharing. The presenter (and any desktop / mouse user) keeps the dock
  // pinned — hunting for a vanishing button mid-presentation is unusable.
  // Watching someone else's slide? Everything fades after 4s so the content is
  // clean — on desktop a mouse move brings it straight back. The PRESENTER
  // always keeps the controls pinned; hunting a vanishing button while
  // teaching is unusable.
  // Anything the student must act on pins the controls open — a vanished dock
  // behind a broadcast card is how people get stuck.
  const keepChromePinned =
    !isSharing || iAmPresenting || !!poppedBroadcast || !!activePoll || payOpen;
  const bumpChrome = useCallback(() => {
    setChromeVisible(true);
    if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current);
    if (keepChromePinned) return;
    chromeTimerRef.current = setTimeout(() => {
      setChromeVisible(false);
    }, 4000);
  }, [keepChromePinned]);
  useEffect(() => {
    if (keepChromePinned) {
      setChromeVisible(true);
      if (chromeTimerRef.current) {
        clearTimeout(chromeTimerRef.current);
        chromeTimerRef.current = null;
      }
    } else {
      bumpChrome();
    }
    return () => {
      if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current);
    };
  }, [keepChromePinned, bumpChrome]);

  // Poll local connection quality every 2s. If "poor" for >5s consecutively,
  // auto-enable data-saver (hides camera tiles, keeps audio only). Restores
  // automatically when quality recovers to good/excellent.
  useEffect(() => {
    if (!room?.localParticipant) return;
    const id = setInterval(() => {
      const q = room.localParticipant.connectionQuality;
      const now = Date.now();
      const bad = q === "poor" || q === "lost";
      if (bad) {
        if (!poorSinceRef.current) poorSinceRef.current = now;
        if (now - poorSinceRef.current > 5000 && !dataSaver) {
          setDataSaver(true);
          toast("Slow network — switched to audio only", { description: "Tap the banner to re-enable video" });
        }
      } else {
        poorSinceRef.current = 0;
        if (dataSaver && (q === "excellent" || q === "good")) {
          setDataSaver(false);
          toast.success("Network recovered — video back on");
        }
      }
    }, 2000);
    return () => clearInterval(id);
  }, [room, dataSaver]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const apply = () => {
      const landscape = window.innerWidth > window.innerHeight;
      setShortLandscape(landscape && window.innerHeight <= 520);
    };
    // Every rotation starts clean: any zoom/pan on the shared slide is reset
    // and the stage is scrolled back to the top, so content can never be left
    // stranded off-screen in a black void.
    const onRotate = () => {
      apply();
      setViewportEpoch((n) => n + 1);
      if (stageScrollRef.current) stageScrollRef.current.scrollTop = 0;
      setTimeout(() => {
        apply();
        if (stageScrollRef.current) stageScrollRef.current.scrollTop = 0;
      }, 350);
      bumpChrome();
    };
    apply();
    window.addEventListener("resize", onRotate);
    window.addEventListener("orientationchange", onRotate);
    return () => {
      window.removeEventListener("resize", onRotate);
      window.removeEventListener("orientationchange", onRotate);
    };
  }, [bumpChrome]);

  const gridCols =
    cameraTracks.length <= 1
      ? "grid-cols-1"
      : cameraTracks.length === 2
      ? "grid-cols-1 md:grid-cols-2"
      : cameraTracks.length <= 4
      ? "grid-cols-2"
      : cameraTracks.length <= 9
      ? "grid-cols-2 md:grid-cols-3"
      : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

  const chatBody = (
    <>
        <div className="flex-1 min-h-0 overflow-y-auto px-3.5 py-3.5 space-y-3 no-scrollbar" data-testid="chat-messages">
          {chatMsgs.filter((m) => m.pinned).length > 0 && (
            <div className="rounded-2xl bg-emerald-500/[0.08] ring-1 ring-emerald-400/25 p-3.5 space-y-2" data-testid="chat-pinned-card">
              <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.2em] text-emerald-300/90">
                <Pin className="w-3 h-3" /> Pinned by host
              </div>
              {chatMsgs.filter((m) => m.pinned).map((m) => (
                <ChatBubble key={`p-${m.id}`} m={m} isHost={isHost} userName={userName} roomName={roomName} pinned onDelete={onDeleteChat} onPin={onPinChat} onBan={onBanUser} />
              ))}
            </div>
          )}
          {chatMsgs.filter((m) => !m.pinned).length === 0 && chatMsgs.filter((m) => m.pinned).length === 0 ? (
            <div className="text-neutral-500 text-sm text-center pt-10">No messages yet.</div>
          ) : (
            chatMsgs.filter((m) => !m.pinned).map((m) => (
              <ChatBubble key={m.id} m={m} isHost={isHost} userName={userName} roomName={roomName} onDelete={onDeleteChat} onPin={onPinChat} onBan={onBanUser} />
            ))
          )}
        </div>
        {chatLocked && !isHost && !isCoHost ? (
          <div className="p-3 border-t border-white/[0.06]" data-testid="chat-readonly-note">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.03] ring-1 ring-white/[0.07] text-[11.5px] text-neutral-400">
              <Lock className="w-3.5 h-3.5 shrink-0" /> Only the host can post here
            </div>
          </div>
        ) : (
          <form onSubmit={sendChat} className="p-3 border-t border-white/[0.06] flex items-center gap-2" data-testid="chat-form">
            <input
              value={chatText}
              onChange={(e) => setChatText(e.target.value)}
              placeholder="Message everyone…"
              maxLength={1000}
              className="flex-1 min-w-0 px-4 py-2.5 rounded-full bg-white/[0.04] ring-1 ring-white/[0.08] text-[12px] text-white placeholder-white/30 outline-none focus:ring-emerald-400/60 transition-shadow"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={!chatText.trim()}
              className="w-10 h-10 shrink-0 grid place-items-center rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black shadow-[0_8px_26px_-8px_rgba(16,185,129,0.9)] transition-colors"
              aria-label="Send"
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        )}
    </>
  );

  const sendReaction = useCallback(
    (emoji) => {
      if (reactionsOff && !isHost && !isCoHost) {
        toast("Reactions are turned off by the host");
        return;
      }
      const who = room?.localParticipant?.name || "Someone";
      pushReaction(emoji, who);
      publishData({ type: "reaction", emoji, name: who });
    },
    [reactionsOff, isHost, isCoHost, room, pushReaction, publishData]
  );

  const changeQuality = useCallback(
    async (key) => {
      setQuality(key);
      try {
        localStorage.setItem("udyog_quality", key);
      } catch (_) {}
      const { preset, applied } = await applyPublishQuality(room, key);
      publishData({ type: "cmd", cmd: "quality", quality: key });
      toast.success(`${preset.label} — ${preset.perStudent} per student`, {
        description: applied ? preset.hint : "Will apply as soon as you share or turn your camera on",
      });
    },
    [room, publishData]
  );

  useEffect(() => {
    if (!room) return;
    const t = setTimeout(() => applyPublishQuality(room, quality), 1200);
    return () => clearTimeout(t);
  }, [room, quality, iAmPresenting, cameraTracks.length]);

  // Weak-network watchdog: if the local link goes bad, save the class by
  // dropping incoming video instead of letting audio stutter.
  useEffect(() => {
    if (!room) return;
    const onQuality = (q, p) => {
      if (!p?.isLocal) return;
      if ((q === "poor" || q === "lost") && !lowDataAuto.current && !lowData) {
        lowDataAuto.current = true;
        setLowData(true);
        toast("Weak network — switched to audio + slide only", {
          description: "Your teacher's voice and screen keep running. Turn it off from More.",
          duration: 7000,
        });
      }
    };
    room.on("connectionQualityChanged", onQuality);
    return () => room.off("connectionQualityChanged", onQuality);
  }, [room, lowData]);

  useEffect(() => {
    if (!room) return;
    setLowDataMode(room, lowData);
    if (lowData) room.localParticipant?.setCameraEnabled(false).catch(() => {});
  }, [room, lowData, participants.length]);

  const changeViewQuality = useCallback(
    (key) => {
      setViewQuality(key);
      try {
        localStorage.setItem("udyog_view_quality", key);
      } catch (_) {}
      const { preset, capped } = applyViewQuality(room, key, participants.length);
      toast.success(`Watching in ${preset.label}`, {
        description: capped
          ? "Big class right now — this keeps the slide smooth for everyone"
          : preset.hint,
      });
    },
    [room, participants.length]
  );

  useEffect(() => {
    if (!room || lowData) return;
    applyViewQuality(room, viewQuality, participants.length);
    const onSub = () => applyViewQuality(room, viewQuality, participants.length);
    room.on("trackSubscribed", onSub);
    return () => room.off("trackSubscribed", onSub);
  }, [room, viewQuality, lowData, participants.length]);

  // A very crowded class gives the slide the whole pipe: incoming cameras are
  // dropped so the thing students actually need — the teacher's screen and
  // voice — never stutters. Nobody is ever refused entry for being late.
  useEffect(() => {
    if (!room || isHost) return;
    dropRemoteCameras(room, participants.length > SLIDE_ONLY_FROM);
  }, [room, isHost, participants.length]);

  // Tell the teacher when the room got big enough to step the slide down, so
  // a quality change is never a mystery.
  const tierRef = useRef("");
  useEffect(() => {
    if (!isHost) return;
    const tier = qualityCeiling(participants.length);
    if (!tierRef.current) {
      tierRef.current = tier;
      return;
    }
    if (tier === tierRef.current) return;
    tierRef.current = tier;
    toast(`${participants.length} in the room — slide now at ${tier}`, {
      description: "Quality adjusts on its own, so no student is ever turned away.",
      duration: 6000,
    });
  }, [isHost, participants.length]);

  const raiseHand = useCallback(() => {
    const who = room?.localParticipant?.name || "Someone";
    pushReaction("\u270B", who);
    publishData({ type: "hand", name: who });
    toast.success("Hand raised — the teacher can see it");
  }, [room, pushReaction, publishData]);

  // Host on/off switches, mirrored into the compact dock popover.
  const hostToggles = (isHost || isCoHost)
    ? [
        {
          key: "mute-all",
          label: "Mute everyone",
          icon: MicOff,
          active: micsMuted,
          onChange: (next) => {
            setMicsMuted(next);
            publishData({ type: "cmd", cmd: next ? "mute-mic" : "ask-unmute" });
            toast.success(next ? "Everyone muted — mics locked" : "Everyone can unmute now");
          },
        },
        {
          key: "cams-off",
          label: "All cameras off",
          icon: VideoOff,
          active: camsOff,
          onChange: (next) => {
            setCamsOff(next);
            publishData({ type: "cmd", cmd: next ? "turn-off-cam" : "ask-video-on" });
            toast.success(next ? "All cameras locked off" : "Cameras can be turned back on");
          },
        },
        {
          key: "chat-lock",
          label: "Students can chat",
          icon: MessageSquare,
          active: !chatLocked,
          onChange: (next) => {
            setChatLocked(!next);
            publishData({ type: "cmd", cmd: next ? "chat-on" : "chat-off" });
            toast.success(next ? "Chat re-enabled" : "Chat disabled — students can still read");
          },
        },
        {
          key: "reactions",
          label: "Live reactions",
          icon: Smile,
          active: !reactionsOff,
          onChange: (next) => {
            setReactionsOff(!next);
            publishData({ type: "cmd", cmd: next ? "reactions-on" : "reactions-off" });
            toast.success(next ? "Reactions enabled" : "Reactions turned off for everyone");
          },
        },
        {
          key: "share-lock",
          label: "Students can share",
          icon: MonitorUp,
          active: !shareLocked,
          onChange: (next) => {
            setShareLocked(!next);
            publishData({ type: "cmd", cmd: next ? "share-on" : "share-off" });
            toast.success(next ? "Screen sharing enabled" : "Screen sharing disabled");
          },
        },
        {
          key: "waiting-room",
          label: "Waiting room",
          icon: DoorOpen,
          active: waitingRoom,
          onChange: async (next) => {
            setWaitingRoom(next);
            try {
              await api.patch(`/meetings/${roomName}/settings`, { waiting_room: next });
              toast.success(next ? "Waiting room ON" : "Waiting room OFF");
            } catch (e) {
              setWaitingRoom(!next);
              toast.error("Couldn't update waiting-room setting");
            }
          },
        },
        {
          key: "lock",
          label: "Lock class (new students can't join)",
          icon: Lock,
          active: locked,
          onChange: (next) => {
            setLocked(next);
            publishData({ type: "cmd", cmd: next ? "lock" : "unlock" });
            persistLock(next);
            toast.success(
              next
                ? "Class locked — new students can't join, anyone already in can reconnect"
                : "Class unlocked"
            );
          },
        },
      ]
    : [];

  return (
    <div className="absolute inset-0 flex flex-col w-full h-[100dvh] bg-[#050505]">
      {/* Ambient background — subtle emerald orbs behind everything. Hidden
          while a screen is shared: the glow reads as a green edge around the
          slide, and the class should see pure black around the content. */}
      {!isSharing && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute -top-32 -left-20 w-[500px] h-[500px] rounded-full opacity-[0.08] blur-[110px]"
            style={{ background: "radial-gradient(closest-side, #10b981, transparent)" }}
          />
          <div
            className="absolute -bottom-40 -right-24 w-[560px] h-[560px] rounded-full opacity-[0.07] blur-[130px]"
            style={{ background: "radial-gradient(closest-side, #34d399, transparent)" }}
          />
        </div>
      )}

      {/* Top bar — premium glass strip */}
      <header className={`z-30 h-14 md:h-16 [@media(max-height:520px)]:h-11 shrink-0 px-3 md:px-6 flex items-center justify-between bg-gradient-to-b from-black/70 via-black/50 to-black/20 backdrop-blur-2xl transition-opacity duration-300 ${isSharing ? "absolute inset-x-0 top-0" : "relative"} ${chromeVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        {/* Hairline emerald stroke at the bottom of the header */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
        <div className="flex items-center gap-3">
          <UdyogLogo size={34} />
          <span className="hidden sm:inline-flex items-center gap-2 text-[10px] font-medium tracking-widest uppercase text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1">
            <span className="relative flex w-1.5 h-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            {connectionState === ConnectionState.Connected
              ? "Live"
              : connectionState === ConnectionState.Connecting
              ? "Connecting"
              : connectionState === ConnectionState.Reconnecting
              ? "Reconnecting"
              : "Offline"}
          </span>
        </div>

        {/* Meeting title centered — visible to everyone once joined */}
        {meetingTitle && (
          <div
            className="hidden sm:block absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 max-w-[45%] text-center"
            data-testid="meeting-title-header"
          >
            <div className="font-display text-sm md:text-base font-semibold text-white/90 truncate tracking-tight">
              {meetingTitle}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {isHost && isRecording && (
            <span
              className="inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full bg-[#1a1c1b] text-[10.5px] tracking-[0.14em] text-white/70"
              data-testid="rec-indicator"
            >
              <span className="relative flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-70" />
                <span className="relative w-2 h-2 rounded-full bg-red-500" />
              </span>
              REC
            </span>
          )}
          {(isHost || isCoHost) && (
            <div className="relative">
              <button
                onClick={() => setShowHostToggles((v) => !v)}
                className={`h-9 px-3 rounded-full inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold transition-colors ${
                  showHostToggles
                    ? "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/50"
                    : "bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/40 hover:bg-emerald-500/20"
                }`}
                data-testid="host-controls-open-btn"
                title="Host controls"
              >
                <ShieldCheck className="w-4 h-4" strokeWidth={1.9} />
                <span className="hidden sm:inline">Host</span>
              </button>
              <AnimatePresence>
                {showHostToggles && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ duration: 0.16 }}
                    className="absolute right-0 top-full mt-3 w-72 sm:w-80 max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain rounded-[24px] bg-[#0a0c0b] ring-1 ring-white/[0.12] shadow-[0_30px_80px_-20px_rgba(0,0,0,1)] p-2.5 z-50"
                    data-testid="host-toggles"
                  >
                    <HostTogglesCard
                      quality={quality}
                      onQuality={changeQuality}
                      hostToggles={hostToggles}
                      isRecording={isRecording}
                      recordingBusy={recordingBusy}
                      onRecording={() =>
                        isRecording
                          ? stopRecording()
                          : recordingPaused
                          ? resumeRecording()
                          : startRecording()
                      }
                      onOpenPanel={() => {
                        setShowHostToggles(false);
                        setShowHostPanel(true);
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
          {!isHost && user && user.role === "admin" && (
            <button
              onClick={async () => {
                try {
                  const { data } = await api.get(`/meetings/${roomName}`);
                  if (data?.room_name === roomName) {
                    setIsHost(true);
                    toast.success("You're now the host");
                  } else {
                    toast.error("This meeting isn't yours to host");
                  }
                } catch {
                  toast.error("Sign in as the meeting owner to claim host");
                }
              }}
              className="text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-full px-3 py-1"
              data-testid="claim-host-btn"
            >
              Claim host
            </button>
          )}
          {!fsOn && isFullscreenEligible() && (
            <button
              onClick={() => goFullscreenNow().then((ok) => ok && setFsOn(true))}
              className="h-9 px-3 rounded-full ring-1 ring-emerald-400/35 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-[10px] uppercase tracking-widest font-semibold inline-flex items-center gap-1.5 transition-colors"
              data-testid="go-fullscreen-btn"
              title="Hide the browser bar"
            >
              <Maximize2 className="w-3.5 h-3.5" strokeWidth={2} />
              <span className="hidden sm:inline">Full screen</span>
            </button>
          )}
          <button
            onClick={leave}
            className="h-9 w-9 rounded-full bg-red-600 hover:bg-red-500 text-white inline-flex items-center justify-center transition-colors"
            data-testid="header-leave-btn"
            aria-label="Leave meeting"
            title="Leave meeting"
          >
            <Phone className="w-4 h-4 rotate-[135deg] fill-white" strokeWidth={2} />
          </button>
        </div>
      </header>

      {fsPrompt && (
        <button
          onClick={() =>
            goFullscreenNow(true).then((ok) => {
              setFsOn(!!ok);
              setFsPrompt(false);
            })
          }
          className="fixed left-1/2 -translate-x-1/2 top-[116px] [@media(max-height:520px)]:top-[96px] z-[60] h-11 px-5 rounded-full bg-emerald-500 text-black text-[10.5px] font-bold uppercase tracking-[0.18em] inline-flex items-center gap-2 shadow-[0_18px_40px_-16px_rgba(16,185,129,0.9)]"
          data-testid="share-fullscreen-prompt"
        >
          <Maximize2 className="w-4 h-4" strokeWidth={2.5} />
          Tap for full screen
        </button>
      )}

      {/* Stage */}
      <main
        className="relative z-10 flex-1 relative overflow-hidden"
        onClick={() => {
          bumpChrome();
          // A tap is first and foremost "show me the controls". Only reclaim
          // fullscreen on the first tap after we actually lost it, so taps are
          // never swallowed by repeated fullscreen requests.
          if (!fsOn && fsRetryRef.current) {
            fsRetryRef.current = false;
            goFullscreenNow().then((ok) => ok && setFsOn(true));
          }
        }}
        onMouseMove={bumpChrome}
        onTouchStart={() => {
          bumpChrome();
          if (!fsOn && fsRetryRef.current && isFullscreenEligible()) {
            fsRetryRef.current = false;
            goFullscreenNow().then((ok) => ok && setFsOn(true));
          }
        }}
      >
        {/* Floating chat bubbles — appear over the video for 3s, or stay pinned */}
        <div
          className={`pointer-events-none absolute left-4 md:left-6 z-30 flex flex-col gap-2 ${
            shortLandscape && isSharing
              ? "bottom-2 max-w-[260px]"
              : isSharing
              ? // Sit above the presenter chip while a slide is up, otherwise a
                // bubble covers "X is presenting".
                "bottom-[172px] max-w-[85%] sm:max-w-md"
              : "bottom-24 max-w-[85%] sm:max-w-md"
          }`}
          data-testid="chat-bubbles-layer"
        >
          <AnimatePresence initial={false}>
            {visibleBubbles.map((b) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 12, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ type: "spring", damping: 22, stiffness: 240 }}
                onClick={() => {
                  setShowChat(true);
                  bumpChrome();
                }}
                role="button"
                className={`pointer-events-auto cursor-pointer rounded-2xl border shadow-lg px-4 py-2.5 transition-transform duration-200 hover:scale-[1.02] active:scale-[0.99] ${
                  b.pinned
                    ? // Solid, not translucent: over a bright slide a 10%
                      // emerald wash turned the whole bubble into a purple
                      // smudge and the text stopped being readable.
                      "bg-[#06110d] border-emerald-400/70 shadow-[0_12px_34px_-10px_rgba(16,185,129,0.55)]"
                    : "bg-[#0b0b0b] border-white/20 shadow-[0_12px_34px_-14px_rgba(0,0,0,0.9)]"
                }`}
                data-testid={`chat-bubble-${b.id}`}
              >
                <div className="flex items-baseline gap-2 mb-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300">
                    {b.user_name || "Guest"}
                  </span>
                  {b.pinned && (
                    <span className="text-[9px] uppercase tracking-widest text-emerald-300 border border-emerald-400/70 bg-emerald-400/10 rounded-full px-1.5 py-0.5">
                      Pinned
                    </span>
                  )}
                </div>
                <div
                  className={`text-sm text-white leading-snug break-words ${
                    portraitShare && isSharing
                      ? // Keep it to one line so the bubble stays inside the
                        // gutter and never creeps over a portrait slide.
                        "line-clamp-1"
                      : "whitespace-pre-wrap"
                  }`}
                >
                  {b.text}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {connectionState !== ConnectionState.Connected && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0a] z-10">
            <div className="text-center">
              <UdyogLogo size={40} className="justify-center mb-4" />
              <div className="flex items-center justify-center gap-3 text-neutral-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                {connectionState === ConnectionState.Reconnecting ? "Reconnecting…" : "Connecting to room…"}
              </div>
            </div>
          </div>
        )}
        <div
          ref={stageScrollRef}
          className={`absolute inset-0 no-scrollbar transition-[padding] duration-300 ${showChat ? "lg:pr-[372px]" : ""} ${
            isSharing
              ? // A shared screen is a FIXED, edge-to-edge stage. It must never
                // scroll: a taller-than-viewport scroll box used to let the
                // slide drift and let the user scroll into pure black.
                // The bottom inset keeps the slide (and the presenter chip +
                // fullscreen button) clear of the dock instead of hiding part
                // of it behind the bar. It follows the dock: when the controls
                // fade out the slide grows into the space (300ms, matching the
                // dock's own fade) so nothing is ever wasted.
                "overflow-hidden p-0"
              : `overflow-y-auto ${
                  pinnedIdentity
                    ? "pt-3 pb-40 [@media(max-height:520px)]:pb-24 px-2 sm:px-3"
                    : "pt-3 pb-40 [@media(max-height:520px)]:pb-24 [@media(max-height:520px)]:pt-1 px-4 md:px-6 [@media(max-height:520px)]:px-2"
                }`
          }`}
          data-testid="video-grid"
          onTouchStart={onStageTouchStart}
          onTouchMove={onStageTouchMove}
          onTouchEnd={onStageTouchEnd}
        >
          {isSharing ? (
            // Screen share focus mode — content takes the whole stage,
            // edge-to-edge on every viewport (portrait AND landscape).
            <div className="h-full flex flex-col">
              <div className="flex-1 min-h-0 grid grid-cols-1">
                {screenTracks.map((t) => (
                  <div
                    key={`share-${t.participant.identity}`}
                    className="relative w-full h-full overflow-hidden bg-black"
                    onDoubleClick={goFullscreen}
                    data-testid={`share-tile-${t.participant.identity}`}
                  >
                    {/* The slide lives in the space ABOVE the dock, and that
                        space is reserved permanently — so it is optically
                        centred, never slides under the control bar, and never
                        moves when the controls fade. */}
                    <div
                      ref={cropEditing ? undefined : shareBoxRef}
                      className={`absolute ${
                        cropEditing
                          ? "inset-x-3 top-10 bottom-[190px] [@media(max-height:520px)]:bottom-[140px]"
                          : "inset-0"
                      }`}
                    >
                      {cropEditing ? (
                        <>
                          <CropFrame
                            crop={null}
                            onMeta={handleShareMeta}
                            className="absolute inset-0"
                          >
                            <VideoTrack
                              trackRef={t}
                              className="absolute inset-0 w-full h-full"
                              style={{ objectFit: "contain", objectPosition: "center" }}
                            />
                          </CropFrame>
                          <CropEditor
                            initial={shareCrop}
                            videoAspect={videoAspect}
                            onApply={applyCrop}
                            onCancel={() => {
                              setCropEditing(false);
                              // They may have saved a framing without applying
                              // it — the one-tap row must show it right away.
                              setSavedCrops(loadSavedCrops());
                            }}
                          />
                        </>
                      ) : (
                        <ZoomPan
                          className="absolute inset-0"
                          testid={`share-zoom-${t.participant.identity}`}
                          resetSignal={viewportEpoch}
                        >
                          <CropFrame
                            crop={shareCrop}
                            onMeta={handleShareMeta}
                            className="absolute inset-0"
                          >
                            <VideoTrack
                              trackRef={t}
                              className="absolute inset-0 w-full h-full"
                              style={{ objectFit: shareFit, objectPosition: "center" }}
                            />
                          </CropFrame>
                        </ZoomPan>
                      )}
                    </div>
                    {/* Brand mark on the slide — the host's uploaded logo when
                        set, otherwise the Udyog mark in focus mode only. */}
                    <ShareWatermark chromeVisible={chromeVisible} />
                    {/* Crop + fullscreen sit at the TOP-right of the slide:
                        the bottom-right corner belongs to the reactions button
                        and the two were close enough to mis-tap. */}
                    <div
                      className={`absolute top-[64px] md:top-[72px] [@media(max-height:520px)]:top-[52px] right-2 z-40 flex items-center gap-2 transition-opacity duration-300 ${
                        chromeVisible && !cropEditing ? "opacity-100" : "opacity-0 pointer-events-none"
                      }`}
                    >
                      {(isHost || isCoHost) && (
                        <button
                          onClick={() => setCropEditing(true)}
                          className={`h-9 px-3 grid place-items-center rounded-full text-[9.5px] uppercase tracking-[0.14em] font-semibold ring-1 transition-colors ${
                            shareCrop
                              ? "bg-emerald-500/90 text-black ring-emerald-300/50"
                              : "bg-[#0b0b0b] text-white/60 ring-white/20"
                          }`}
                          data-testid="share-crop-btn"
                          title="Choose exactly what the class sees"
                        >
                          {shareCrop ? "Cropped · edit" : "Crop"}
                        </button>
                      )}
                      {(isHost || isCoHost) && shareCrop && (
                        <button
                          onClick={() => applyCrop(null)}
                          className="h-9 px-3 grid place-items-center rounded-full bg-[#0b0b0b] text-white/60 ring-1 ring-white/20 text-[9.5px] uppercase tracking-[0.14em] font-semibold"
                          data-testid="share-crop-reset"
                          title="Show the full screen again"
                        >
                          Full
                        </button>
                      )}
                      <button
                        onClick={toggleShareFill}
                        className={`h-9 px-3 grid place-items-center rounded-full text-[9.5px] uppercase tracking-[0.14em] font-semibold ring-1 transition-colors ${
                          shareFit === "cover"
                            ? "bg-[#0b0b0b] text-white/60 ring-white/20"
                            : "bg-emerald-500/90 text-black ring-emerald-300/50"
                        }`}
                        data-testid="share-fit-btn"
                        title={shareFit === "cover" ? "Show the whole slide" : "Fill the screen"}
                      >
                        {shareFit === "cover" ? "Whole slide" : "Fill screen"}
                      </button>
                      <button
                        onClick={() => goFullscreenNow(true).then((ok) => ok && setFsOn(true))}
                        className="h-9 w-9 grid place-items-center rounded-full bg-[#0b0b0b] ring-1 ring-emerald-400/40 text-emerald-300 hover:bg-black transition-colors"
                        data-testid="share-fullscreen-btn"
                        aria-label="Full screen"
                        title="Full screen"
                      >
                        <Maximize2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                    {/* Saved framings — one tap puts one live for the class,
                        no dragging, no editor. */}
                    {(isHost || isCoHost) && !cropEditing && savedCrops.length > 0 && (
                      <div
                        className={`absolute top-[112px] md:top-[120px] [@media(max-height:520px)]:top-[96px] right-2 z-40 max-w-[70%] flex items-center gap-1.5 overflow-x-auto transition-opacity duration-300 ${
                          chromeVisible ? "opacity-100" : "opacity-0 pointer-events-none"
                        }`}
                        data-testid="share-crop-quick-row"
                      >
                        {savedCrops.map((sc) => {
                          const live =
                            shareCrop &&
                            Math.abs(shareCrop.x - sc.crop.x) < 0.004 &&
                            Math.abs(shareCrop.y - sc.crop.y) < 0.004 &&
                            Math.abs(shareCrop.w - sc.crop.w) < 0.004 &&
                            Math.abs(shareCrop.h - sc.crop.h) < 0.004;
                          return (
                            <button
                              key={sc.id}
                              onClick={() => applyCrop(sc.crop)}
                              className={`shrink-0 h-8 px-3 rounded-full text-[9.5px] uppercase tracking-[0.14em] font-semibold ring-1 transition-colors ${
                                live
                                  ? "bg-emerald-500/90 text-black ring-emerald-300/50"
                                  : "bg-[#0b0b0b]/90 text-white/65 ring-white/20"
                              }`}
                              data-testid={`share-crop-quick-${sc.name.toLowerCase()}`}
                              title={`Show everyone your "${sc.name}" framing`}
                            >
                              {sc.name}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Presenter chip — top-left of the stage, so the control
                        bar can never cover "X is presenting". */}
                    <div
                      className={`absolute top-2 left-2 right-2 flex items-center gap-2 pointer-events-none transition-opacity duration-300 ${
                        chromeVisible ? "opacity-100" : "opacity-0"
                      }`}
                      data-testid="share-overlay-row"
                    >
                      <div className="pointer-events-auto flex items-center gap-2 pl-2 pr-3.5 py-1.5 rounded-full bg-[#0b0b0b] ring-1 ring-white/[0.18] shadow-[0_12px_34px_-14px_rgba(0,0,0,0.9)]">
                        <span className="relative flex h-6 w-6 shrink-0 items-center justify-center">
                          <span className="absolute inset-0 rounded-full bg-emerald-500/25 animate-ping" />
                          <span className="relative h-6 w-6 rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/50 grid place-items-center">
                            <MonitorUp className="h-3 w-3 text-emerald-300" strokeWidth={2.2} />
                          </span>
                        </span>
                        <span className="text-[12px] leading-none">
                          <span className="text-white font-medium">
                            {t.participant.name || t.participant.identity}
                          </span>
                          <span className="text-white/60"> is presenting</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : cameraTracks.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-neutral-500">
              <VideoIcon className="w-10 h-10 mb-3 text-neutral-700" />
              <div className="text-sm">Waiting for participants…</div>
            </div>
          ) : (
            (() => {
              // Meet/Zoom-style: show a small set of active-camera + host
              // tiles, then a single "+N more" summary tile for everyone
              // else. When a participant is pinned host-wide, that
              // participant fills the stage alone.
              const MAX_TILES = 4;
              // If someone is pinned, put them first and show only their tile.
              const pinnedFirst = pinnedIdentity
                ? [...cameraTracks].sort((a, b) => {
                    if (a.participant.identity === pinnedIdentity) return -1;
                    if (b.participant.identity === pinnedIdentity) return 1;
                    return 0;
                  })
                : cameraTracks;
              // Priority sort (only when nothing is pinned): the person
              // currently talking owns the stage, then anyone with a live
              // camera. Matches Zoom's active-speaker view.
              const score = (t) =>
                (t.participant.identity === dominantIdentity ? 4 : 0) +
                (t.participant.isSpeaking ? 3 : 0) +
                (t.publication ? 2 : 0);
              const sorted = pinnedIdentity
                ? pinnedFirst.slice(0, 1)
                : [...pinnedFirst].sort((a, b) => score(b) - score(a));
              const visible = pinnedIdentity ? sorted : sorted.slice(0, MAX_TILES);
              const extra = pinnedIdentity
                ? 0
                : Math.max(0, participants.length - visible.length);
              return (
                <div className={pinnedIdentity ? "h-full flex flex-col" : "h-full flex flex-col gap-3 md:gap-4"}>
                  {dataSaver ? (
                    <div className="col-span-full h-full flex flex-col items-center justify-center gap-3 text-center px-6">
                      <UdyogLogo size={40} />
                      <div className="text-lg font-display font-semibold text-white">Data saver mode</div>
                      <div className="text-sm text-neutral-400 max-w-xs">
                        Slow network detected. Video is paused — audio keeps flowing so you don't miss the class.
                      </div>
                      <button
                        onClick={() => setDataSaver(false)}
                        className="mt-2 rounded-full px-4 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-xs uppercase tracking-widest font-semibold hover:bg-emerald-500/20 transition-colors"
                        data-testid="data-saver-off-btn"
                      >
                        Turn video back on
                      </button>
                    </div>
                  ) : pinnedIdentity ? (
                    <div className="flex-1 min-h-0">
                      {visible.map((t) => (
                        <ParticipantTile
                          key={`${t.participant.identity}-${t.source}`}
                          trackRef={t}
                          large
                          pinInfo={
                            (isHost || isCoHost) && pinnedIdentity !== stageOwner
                              ? { pinned: true, onUnpin: () => applyPin(null, "Back to the host") }
                              : { pinned: true }
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    /* Single big stage — no small participant cards at the
                       bottom; everyone else lives in the People list. */
                    <div className="flex-1 min-h-0">
                      <ParticipantTile
                        key={`${visible[0].participant.identity}-${visible[0].source}`}
                        trackRef={visible[0]}
                        large
                        pinInfo={(isHost || isCoHost) ? {
                          pinned: false,
                          onPin: () => {
                            const id = visible[0].participant.identity;
                            applyPin(id, `Pinned ${visible[0].participant.name || id} for everyone`);
                          },
                        } : null}
                      />
                    </div>
                  )}
                </div>
              );
            })()
          )}
        </div>

        {/* Floating LIVE POLL pill — visible to host / co-host only when a
            poll is running. Tap it to jump straight into the Host Controls
            drawer's tally section. Lets the host check responses without
            leaving the meeting. */}
        {hostPoll && (isHost || isCoHost) && (
          <motion.button
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: chromeVisible ? 1 : 0, y: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            onClick={() => setShowHostPanel(true)}
            className="fixed top-16 md:top-20 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 rounded-full pl-2 pr-3 py-1.5 bg-black/80 backdrop-blur-2xl border border-emerald-500/40 shadow-[0_0_40px_-10px_rgba(16,185,129,0.55)] hover:bg-emerald-500/10 transition-colors"
            data-testid="host-poll-shortcut"
            title="Question responses (only you can see)"
          >
            <span className="relative flex w-2 h-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
              Question · {pollTally.total} {pollTally.total === 1 ? "response" : "responses"}
            </span>
          </motion.button>
        )}

        {/* Data-saver banner — auto-shows on poor bandwidth. Tapping it
            re-enables video. */}
        {dataSaver && (
          <motion.button
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => {
              setDataSaver(false);
              toast("Video re-enabled");
            }}
            className="fixed top-16 md:top-20 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 rounded-full pl-2 pr-3 py-1.5 bg-black/85 backdrop-blur-2xl border border-amber-500/50 shadow-[0_0_40px_-10px_rgba(245,158,11,0.55)] hover:bg-amber-500/10 transition-colors"
            data-testid="data-saver-banner"
          >
            <span className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
              Data saver · audio only · tap for video
            </span>
          </motion.button>
        )}

        {/* Acting-host banner — appears to co-hosts when the real host has
            been silent (disconnected / crashed) for 12+ seconds. */}
        {hostAway && isCoHost && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed top-16 md:top-20 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 rounded-full pl-2 pr-3 py-1.5 bg-black/80 backdrop-blur-2xl border border-amber-500/50 shadow-[0_0_40px_-10px_rgba(245,158,11,0.55)]"
            data-testid="acting-host-banner"
          >
            <span className="relative flex w-2 h-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
            </span>
            <span className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
              You're the acting host · host away
            </span>
          </motion.div>
        )}

        {/* Fees checkout — direct UPI, opened from a broadcast or an answer */}
        <PayCheckout
          open={payOpen}
          onClose={() => setPayOpen(false)}
          roomName={roomName}
          userName={userName}
          whatsapp={whatsapp}
          classTitle={meetingTitle}
          inMeeting
        />

        {/* Host-controlled "WhatsApp us" button — students see it live, the
            host sees the same thing tagged as a preview. */}
        {waBtn && (
          <WhatsappUs
            roomName={roomName}
            userName={userName}
            whatsapp={whatsapp}
            preview={isHost}
          />
        )}

        {/* Tiles page — lives inside <main> (z-10 stacking context) at z-19 so
            it sits above the stage but under the control dock (z-20). */}
        <EveryoneGrid
          open={showEveryone}
          onClose={() => setShowEveryone(false)}
          participants={participants}
          cameraTracks={cameraTracks}
          pinnedIdentity={pinnedIdentity}
          coHosts={coHosts}
          chatOpen={showChat}
        />

        {/* Tiles page affordance — hidden on purpose: the host doesn't want any
            label on the class screen. Swiping the stage left still opens the
            tile wall. */}
        {false && !showEveryone && (
          <button
            onClick={() => setShowEveryone(true)}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-30 pl-2 pr-1.5 py-3 rounded-l-2xl bg-black/55 backdrop-blur-md ring-1 ring-white/10 border-r-0 text-white/70 hover:text-white hover:bg-black/75 active:scale-95 transition-all duration-200 flex flex-col items-center gap-1.5 ${
              chromeVisible ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            data-testid="stage-tiles-edge-btn"
            aria-label="See everyone in tiles"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2.2} />
            <span
              className="text-[9px] uppercase tracking-[0.18em] text-emerald-300/90"
              style={{ writingMode: "vertical-rl" }}
            >
              Tiles
            </span>
          </button>
        )}

        {/* Bottom control dock */}
        <ControlDock
          onLeave={leave}          chatUnread={unread}
          onOpenChat={openChat}
          isHost={isHost}
          isCoHost={isCoHost}
          onOpenHostPanel={() => setShowHostPanel(true)}
          hostToggles={hostToggles}
          onOpenRoster={() => setShowRoster(true)}
          onOpenEveryone={() => setShowEveryone(true)}
          participantCount={participants.length}
          onFullscreen={goFullscreen}
          raiseHandDisabled={raiseHandDisabled}
          micForcedOff={micForcedOff}
          camForcedOff={camForcedOff}
          shareLocked={shareLocked}
          chatLocked={chatLocked}
          onEnterPip={enterNativePip}
          onReact={sendReaction}
          onRaiseHand={raiseHand}
          viewQuality={viewQuality}
          onViewQuality={changeViewQuality}
          lowData={lowData}
          onToggleLowData={() => {
            lowDataAuto.current = true;
            setLowData((v) => {
              toast(v ? "Full quality restored" : "Low data mode on — audio + slide only");
              return !v;
            });
          }}
          reactionsOff={reactionsOff && !isHost && !isCoHost}
          hidden={!chromeVisible}
        />



        {/* One-time, contextual ask — so the reminder can actually reach them
            while they are away from the tab. */}
        <AnimatePresence>
          {askNotif && (
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="absolute top-3 left-0 right-0 mx-auto w-fit max-w-[94%] z-40 flex items-center gap-2.5 pl-4 pr-2 py-2 rounded-full bg-[#0a0c0b]/95 ring-1 ring-emerald-400/25 shadow-[0_18px_50px_-20px_rgba(0,0,0,1)]"
              data-testid="notif-optin"
            >
              <span className="text-[11.5px] text-white/75 whitespace-nowrap">
                Remind me if I leave the class
              </span>
              <button
                onClick={async () => {
                  localStorage.setItem("udyog_notif_asked", "1");
                  setAskNotif(false);
                  const r = await askNotificationPermission();
                  if (r === "granted") toast.success("Reminders on — we'll ping you if you leave the tab");
                }}
                className="h-8 px-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-[10px] font-semibold uppercase tracking-widest"
                data-testid="notif-optin-allow"
              >
                Allow
              </button>
              <button
                onClick={() => {
                  localStorage.setItem("udyog_notif_asked", "1");
                  setAskNotif(false);
                }}
                className="w-8 h-8 grid place-items-center rounded-full text-white/40 hover:text-white hover:bg-white/[0.06]"
                data-testid="notif-optin-dismiss"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* "You're still in class" — Back press lands here, never a silent exit */}
        <AnimatePresence>
          {backSheet && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-xl grid place-items-center px-6"
              data-testid="still-in-class-sheet"
            >
              <motion.div
                initial={{ scale: 0.94, y: 16 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 12 }}
                transition={{ type: "spring", damping: 24, stiffness: 260 }}
                className="w-full max-w-sm rounded-[30px] bg-[#0b0d0c] ring-1 ring-emerald-500/25 p-6 text-center shadow-[0_40px_100px_-30px_rgba(0,0,0,1)]"
              >
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/35 grid place-items-center">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                </div>
                <div className="mt-4 text-[10px] uppercase tracking-[0.26em] text-emerald-400/80">Still connected</div>
                <h3 className="font-display text-2xl mt-1.5">You're still in class</h3>
                <p className="text-[12.5px] text-white/45 mt-2">
                  You didn't leave — the class is still running. Only the red button ends it.
                </p>
                <div className="mt-6 space-y-2.5">
                  <button
                    onClick={() => setBackSheet(false)}
                    className="w-full h-12 rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 text-black text-[12px] font-semibold uppercase tracking-[0.18em]"
                    data-testid="sheet-back-to-class"
                  >
                    Back to class
                  </button>
                  <button
                    onClick={() => {
                      setBackSheet(false);
                      leave();
                    }}
                    data-testid="sheet-leave-class"
                    className="w-full h-12 rounded-full ring-1 ring-red-500/30 text-red-400/90 hover:text-red-300 hover:bg-red-500/10 text-[12px] uppercase tracking-[0.16em] transition-colors"
                  >
                    Leave the class
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {lowData && (
          <button
            onClick={() => {
              lowDataAuto.current = true;
              setLowData(false);
              toast("Full quality restored");
            }}
            className="absolute top-3 left-3 z-40 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/80 ring-1 ring-amber-400/40 text-amber-300 text-[10px] uppercase tracking-widest"
            data-testid="low-data-chip"
            title="Tap to restore full quality"
          >
            <WifiOff className="w-3.5 h-3.5" /> Low data mode
          </button>
        )}

        {/* Live reactions — narrow lane on the left edge, clear of the slide */}
        <div
          className="pointer-events-none absolute left-1 sm:left-3 bottom-40 [@media(max-height:520px)]:bottom-14 w-14 h-56 sm:h-72 z-30 overflow-hidden"
          data-testid="reactions-lane"
        >
          <AnimatePresence>
            {flyingReactions.map((r) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 40, scale: 0.6 }}
                animate={{ opacity: [0, 1, 1, 0], y: -190, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 3.2, ease: "easeOut" }}
                className="absolute bottom-0 flex flex-col items-center"
                style={{ left: r.offset }}
              >
                <span className="text-[26px] leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">{r.emoji}</span>
                <span className="mt-0.5 max-w-[52px] truncate text-[8px] uppercase tracking-widest text-white/45">
                  {r.name}
                </span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Chat — docked panel on desktop, exactly like the approved design */}
        <AnimatePresence>
          {showChat && (
            <motion.aside
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 24 }}
              transition={{ type: "spring", damping: 24, stiffness: 260 }}
              className="hidden lg:flex flex-col absolute right-6 top-4 bottom-40 w-[348px] z-30 rounded-[26px] bg-[#0b0d0c]/80 backdrop-blur-2xl ring-1 ring-white/[0.07] shadow-[0_24px_70px_-30px_rgba(0,0,0,0.95)] overflow-hidden"
              data-testid="chat-panel"
            >
              <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-emerald-400" strokeWidth={1.8} />
                  <span className="text-[12.5px] text-white/90">Chat</span>
                </div>
                <div className="flex items-center gap-2">
                  {chatLocked && !isHost && !isCoHost && (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] ring-1 ring-white/10 text-[9.5px] uppercase tracking-[0.16em] text-white/45">
                      <Lock className="w-3 h-3" /> Read only
                    </span>
                  )}
                  <button
                    onClick={() => setShowChat(false)}
                    className="w-8 h-8 grid place-items-center rounded-full text-white/50 hover:text-white hover:bg-white/[0.06] transition-colors"
                    aria-label="Close chat"
                    data-testid="chat-panel-close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {chatBody}
            </motion.aside>
          )}
        </AnimatePresence>
      </main>

      {/* Roster drawer */}
      <SideDrawer open={showRoster} onClose={() => setShowRoster(false)} title={`People · ${participants.length}`} testid="roster-drawer">
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 pb-10 space-y-2">
          <button
            onClick={() => {
              setShowRoster(false);
              setShowEveryone(true);
            }}
            className="w-full mb-1 h-11 rounded-2xl bg-emerald-500/[0.09] ring-1 ring-emerald-400/30 text-emerald-200 text-[12px] uppercase tracking-[0.14em] font-semibold flex items-center justify-center gap-2 hover:bg-emerald-500/[0.16] transition-colors"
            data-testid="open-everyone-grid"
          >
            <LayoutGrid className="w-4 h-4" /> See everyone in tiles
          </button>
          <div className="relative mb-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              value={rosterQuery}
              onChange={(e) => setRosterQuery(e.target.value)}
              placeholder="Search a student by name…"
              className="w-full h-11 pl-10 pr-10 rounded-2xl bg-white/[0.05] ring-1 ring-white/[0.08] focus:ring-emerald-400/40 text-[13px] text-white placeholder:text-white/25 outline-none"
              data-testid="roster-search"
            />
            {rosterQuery && (
              <button
                onClick={() => setRosterQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-full text-white/40 hover:text-white hover:bg-white/[0.08]"
                data-testid="roster-search-clear"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {rosterQuery.trim() && (
            <div className="text-[10.5px] text-white/35 ml-1 pb-1" data-testid="roster-search-count">
              {rosterShown.length} of {participants.length} match “{rosterQuery.trim()}”
            </div>
          )}
          {rosterShown.length === 0 && (
            <div className="pt-10 text-center text-[13px] text-white/30" data-testid="roster-no-match">
              Nobody here by that name.
            </div>
          )}
          {rosterShown.map((p) => (
            <ParticipantRow
              key={p.identity}
              participant={p}
              canModerate={isHost || isCoHost}
              hostCanPromote={isHost}
              isPinned={pinnedIdentity === p.identity}
              isCoHost={coHosts.has(p.identity)}
              onPin={() => {
                if (pinnedIdentity === p.identity) {
                  applyPin(null, "Back to the host");
                } else {
                  applyPin(p.identity, `Pinned ${p.name || p.identity} for everyone`);
                }
              }}
              onToggleCoHost={() => {
                setCoHosts((prev) => {
                  const next = new Set(prev);
                  const already = next.has(p.identity);
                  if (already) next.delete(p.identity);
                  else if (next.size < 3) next.add(p.identity);
                  else {
                    toast.error("Max 3 co-hosts");
                    return prev;
                  }
                  publishData({ type: "cmd", cmd: "cohost-set", identities: Array.from(next) });
                  toast(already ? `${p.name || p.identity} is no longer a co-host` : `${p.name || p.identity} is now a co-host`);
                  return next;
                });
              }}
              onMute={() => {
                publishData({ type: "cmd", cmd: "mute-target", identity: p.identity });
                toast(`Muted ${p.name || p.identity}`);
              }}
              onCamOff={() => {
                publishData({ type: "cmd", cmd: "cam-off-target", identity: p.identity });
                toast(`Turned off camera for ${p.name || p.identity}`);
              }}
              onKick={() => {
                if (
                  !confirm(
                    `Remove ${p.name || p.identity} from this class and block them from rejoining?`
                  )
                )
                  return;
                publishData({ type: "cmd", cmd: "kick-target", identity: p.identity });
                // The block lives on the server, keyed on their WhatsApp number
                // (the backend looks it up from this class's roster), so a new
                // name won't get them back in.
                api
                  .post(`/meetings/${roomName}/ban`, { user_name: p.name || p.identity })
                  .then(({ data }) =>
                    toast.success(
                      data?.banned_numbers?.length
                        ? `Removed ${p.name} — their WhatsApp number is blocked for this class`
                        : `Removed ${p.name} — blocked by name for this class`
                    )
                  )
                  .catch(() => toast.error("Removed, but the block didn't save — please try again"));
              }}
            />
          ))}
        </div>
      </SideDrawer>

      {/* Chat — drawer on phones and tablets */}
      <div className="lg:hidden">
        <SideDrawer open={showChat} onClose={() => setShowChat(false)} title="Chat" testid="chat-drawer">
          {chatBody}
        </SideDrawer>
      </div>

      {/* Host controls panel */}
      {isHost && (
        <SideDrawer open={showHostPanel} onClose={() => setShowHostPanel(false)} title="Host controls" testid="host-panel" wide>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 pb-10 space-y-6">
            {/* ---- ROOM CONTROLS (toggles first — primary moderation) ---- */}
            <section>
              <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Room controls</div>
              <div className="flex flex-col gap-2">
                <HostToggleRow
                  testid="host-mute-all"
                  onLabel="Mute everyone"
                  offLabel="Mute everyone"
                  active={micsMuted}
                  icon={micsMuted ? Mic : MicOff}
                  onChange={(next) => {
                    setMicsMuted(next);
                    publishData({ type: "cmd", cmd: next ? "mute-mic" : "ask-unmute" });
                    toast.success(next ? "Everyone muted — mics locked" : "Everyone can unmute now");
                  }}
                />
                <HostToggleRow
                  testid="host-stop-video-all"
                  onLabel="Turn off all cameras"
                  offLabel="Turn off all cameras"
                  active={camsOff}
                  icon={camsOff ? VideoIcon : VideoOff}
                  onChange={(next) => {
                    setCamsOff(next);
                    publishData({ type: "cmd", cmd: next ? "turn-off-cam" : "ask-video-on" });
                    toast.success(next ? "All cameras locked off" : "Cameras can be turned back on");
                  }}
                />
                <HostToggleRow
                  testid="host-toggle-chat"
                  onLabel="Disable chat"
                  offLabel="Disable chat"
                  active={chatLocked}
                  icon={MessageCircle}
                  onChange={(next) => {
                    setChatLocked(next);
                    publishData({ type: "cmd", cmd: next ? "chat-off" : "chat-on" });
                    toast.success(next ? "Chat disabled for participants" : "Chat re-enabled");
                  }}
                />
                <HostToggleRow
                  testid="host-toggle-share"
                  onLabel="Disable screen sharing"
                  offLabel="Disable screen sharing"
                  active={shareLocked}
                  icon={MonitorUp}
                  onChange={(next) => {
                    setShareLocked(next);
                    publishData({ type: "cmd", cmd: next ? "share-off" : "share-on" });
                    toast.success(next ? "Screen sharing disabled" : "Screen sharing enabled");
                  }}
                />
                <HostToggleRow
                  testid="host-toggle-waiting-room"
                  onLabel="Waiting room (new joiners wait)"
                  offLabel="Waiting room (new joiners wait)"
                  active={waitingRoom}
                  icon={Users}
                  onChange={async (next) => {
                    setWaitingRoom(next);
                    try {
                      await api.patch(`/meetings/${roomName}/settings`, { waiting_room: next });
                      toast.success(
                        next
                          ? "Waiting room ON — new joiners will queue for admission"
                          : "Waiting room OFF — new joiners enter directly"
                      );
                    } catch (e) {
                      setWaitingRoom(!next);
                      toast.error("Couldn't update waiting-room setting");
                    }
                  }}
                />
                <HostToggleRow
                  testid="host-toggle-wa-button"
                  onLabel="“WhatsApp us” button for students"
                  offLabel="“WhatsApp us” button for students"
                  active={waBtn}
                  icon={MessageCircle}
                  onChange={async (next) => {
                    setWaBtn(next);
                    publishData({ type: "cmd", cmd: next ? "wa-btn-on" : "wa-btn-off" });
                    try {
                      await api.patch(`/meetings/${roomName}/settings`, { wa_button: next });
                      toast.success(
                        next
                          ? "“WhatsApp us” button is now on every student's screen"
                          : "“WhatsApp us” button hidden"
                      );
                    } catch (e) {
                      setWaBtn(!next);
                      toast.error("Couldn't update the WhatsApp button");
                    }
                  }}
                />
                <HostToggleRow
                  testid="host-lock"
                  onLabel="Lock class (new students can't join)"
                  offLabel="Lock class (new students can't join)"
                  active={locked}
                  icon={Users}
                  onChange={(next) => {
                    setLocked(next);
                    publishData({ type: "cmd", cmd: next ? "lock" : "unlock" });
                    persistLock(next);
                    toast.success(
                      next
                        ? "Class locked — new students can't join, anyone already in can reconnect"
                        : "Class unlocked"
                    );
                  }}
                />
                <button
                  onClick={async () => {
                    if (!confirm("End the meeting for all participants?")) return;
                    publishData({ type: "cmd", cmd: "end-meeting" });
                    try {
                      await api.delete(`/meetings/${roomName}`);
                    } catch (_) {}
                    toast.success("Meeting ended");
                    setTimeout(() => nav("/admin"), 800);
                  }}
                  className="mt-1 w-full h-11 rounded-xl border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-300 font-display font-semibold text-sm flex items-center justify-center gap-2"
                  data-testid="host-end-all"
                >
                  <PhoneOff className="w-4 h-4" /> End for everyone
                </button>
              </div>
            </section>

            {/* ---- CLOUD RECORDING (records the meeting to MP4 on your VPS) ---- */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-emerald-400">Cloud recording</div>
                {isRecording && (
                  <span className="text-[10px] uppercase tracking-widest text-red-400 font-bold flex items-center gap-1.5" data-testid="host-recording-indicator">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.9)]" />
                    Recording
                  </span>
                )}
              </div>
              <p className="text-xs text-neutral-500 mb-2">
                {isRecording
                  ? "Recording the full class. Pause closes the current part; resume starts the next one."
                  : recordingPaused
                  ? "Paused. The part you already recorded is saved — resume to start the next part."
                  : "Start a full class recording. The MP4 lands in your cloud bucket."}
              </p>
              {!isRecording ? (
                <button
                  onClick={recordingPaused ? resumeRecording : startRecording}
                  disabled={recordingBusy}
                  className="w-full h-11 rounded-full bg-red-500/90 hover:bg-red-500 text-white font-display font-semibold tracking-tight shadow-[0_10px_30px_-10px_rgba(239,68,68,0.55)] flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid={recordingPaused ? "host-recording-resume" : "host-recording-start"}
                >
                  <span className="w-3 h-3 rounded-full bg-white" />
                  {recordingBusy
                    ? recordingPaused
                      ? "Resuming…"
                      : "Starting…"
                    : recordingPaused
                    ? "Resume recording"
                    : "Start recording"}
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={pauseRecording}
                    disabled={recordingBusy}
                    className="flex-1 h-11 rounded-full bg-white/[0.05] hover:bg-white/[0.10] border border-amber-500/40 text-amber-300 font-display font-semibold tracking-tight flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="host-recording-pause"
                  >
                    <span className="flex gap-[3px]">
                      <span className="w-[3px] h-3 bg-amber-300 rounded-sm" />
                      <span className="w-[3px] h-3 bg-amber-300 rounded-sm" />
                    </span>
                    {recordingBusy ? "Pausing…" : "Pause"}
                  </button>
                  <button
                    onClick={stopRecording}
                    disabled={recordingBusy}
                    className="flex-1 h-11 rounded-full bg-white/[0.05] hover:bg-white/[0.10] border border-red-500/40 text-red-300 font-display font-semibold tracking-tight flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="host-recording-stop"
                  >
                    <span className="w-3 h-3 rounded-sm bg-red-400" />
                    {recordingBusy ? "Stopping…" : "Stop"}
                  </button>
                </div>
              )}
              {recordingPaused && !isRecording && (
                <div className="mt-2 text-[10.5px] text-amber-300/70" data-testid="host-recording-paused-note">
                  Paused — nothing is being recorded right now.
                </div>
              )}
            </section>

            {/* Broadcast section */}
            <section>
              <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Broadcast</div>
              <p className="text-xs text-neutral-500 mb-2">One-tap message + link. A full-screen card appears on every participant's device.</p>
              {templates.filter((t) => t.kind === "broadcast").length > 0 && (
                <div className="mb-3" data-testid="broadcast-templates">
                  <div className="text-[10px] uppercase tracking-widest text-white/35 mb-1.5">Saved announcements</div>
                  <div className="flex flex-wrap gap-1.5">
                    {templates
                      .filter((t) => t.kind === "broadcast")
                      .map((t) => (
                        <button
                          key={t.id}
                          onClick={() => applyBroadcastTemplate(t)}
                          className="h-8 px-3 rounded-full bg-white/[0.05] hover:bg-emerald-500/15 ring-1 ring-white/10 hover:ring-emerald-400/40 text-[11px] text-white/70 hover:text-emerald-200 transition-colors"
                          data-testid={`broadcast-template-${t.id}`}
                        >
                          {t.title}
                        </button>
                      ))}
                  </div>
                </div>
              )}
              {broadcastBanner && (
                <div className="relative mb-2">
                  <img
                    src={broadcastBanner}
                    alt=""
                    className="w-full h-24 object-cover rounded-lg ring-1 ring-white/10"
                    data-testid="host-broadcast-banner"
                  />
                  <button
                    onClick={() => setBroadcastBanner(null)}
                    className="absolute top-1.5 right-1.5 h-7 w-7 grid place-items-center rounded-full bg-black/70 text-white/70 hover:text-white"
                    data-testid="host-broadcast-banner-remove"
                    aria-label="Remove banner"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <textarea
                value={broadcastText}
                onChange={(e) => setBroadcastText(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Meeting starts now."
                className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none mb-2"
                data-testid="host-broadcast-text"
              />
              <Input
                type="url"
                value={broadcastLink}
                onChange={(e) => setBroadcastLink(e.target.value)}
                placeholder="https://... (optional)"
                className="bg-neutral-950 border-neutral-800 focus-visible:ring-emerald-500 h-10 mb-2"
                data-testid="host-broadcast-link"
              />
              <Input
                value={broadcastLabel}
                onChange={(e) => setBroadcastLabel(e.target.value)}
                placeholder="Button label (e.g. Register now)"
                maxLength={60}
                className="bg-neutral-950 border-neutral-800 focus-visible:ring-emerald-500 h-10 mb-2"
                data-testid="host-broadcast-label"
              />
              <button
                onClick={() => setBroadcastPay((v) => !v)}
                className={`w-full h-10 mb-2 rounded-lg text-[10.5px] uppercase tracking-[0.14em] transition-colors ${
                  broadcastPay
                    ? "bg-yellow-300 text-black font-semibold"
                    : "ring-1 ring-white/10 text-white/45 hover:text-white"
                }`}
                data-testid="host-broadcast-pay-toggle"
              >
                {broadcastPay ? "✓ Pay Your Fees button attached" : "Attach “Pay Your Fees” button"}
              </button>
              <Button
                onClick={(e) => sendBroadcast(e)}
                disabled={!broadcastText.trim()}
                className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-11 udyog-glow"
                data-testid="host-broadcast-send"
              >
                <Send className="w-4 h-4 mr-2" /> Send to everyone
              </Button>
              <button
                onClick={() => saveAsTemplate("broadcast")}
                className="w-full mt-1.5 h-9 rounded-full ring-1 ring-white/10 text-[10.5px] uppercase tracking-[0.16em] text-white/45 hover:text-white hover:ring-emerald-400/30 transition-colors"
                data-testid="broadcast-save-template"
              >
                Save as template
              </button>
            </section>

            {/* Payment nudge — a private card to everyone who opened the
                checkout and never finished paying. */}
            <section data-testid="host-nudge-section">
              <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Payment nudge</div>
              <p className="text-xs text-neutral-500 mb-2">
                Sends each non-payer their own private card — their name, their seat, and the fees
                checkout one tap away. Nobody else sees it.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="flex-1 h-10 px-3 rounded-lg bg-neutral-950 ring-1 ring-white/10 flex items-center text-[11.5px] text-white/60"
                  data-testid="host-nudge-count"
                >
                  {payPending
                    ? payPending.total === 0
                      ? "Nobody has opened the checkout yet"
                      : `${payPending.in_room} in the room now · ${payPending.total} opened, unpaid`
                    : "Checking…"}
                </span>
                <button
                  onClick={loadPayPending}
                  className="h-10 px-3 rounded-lg ring-1 ring-white/10 text-[10.5px] uppercase tracking-[0.14em] text-white/45 hover:text-white transition-colors"
                  data-testid="host-nudge-refresh"
                >
                  Refresh
                </button>
              </div>
              {!!payPending?.pending?.length && (
                <div
                  className="mb-2 max-h-24 overflow-y-auto rounded-lg ring-1 ring-white/[0.07] divide-y divide-white/[0.05]"
                  data-testid="host-nudge-list"
                >
                  {payPending.pending.map((p) => (
                    <div key={p.payment_id} className="px-3 py-1.5 flex items-center gap-2 text-[11.5px]">
                      <span className={`w-1.5 h-1.5 rounded-full ${p.in_room ? "bg-emerald-400" : "bg-white/20"}`} />
                      <span className="text-white/75 truncate">{p.user_name}</span>
                      <span className="text-white/30">#{p.seat_no}</span>
                      <span className="ml-auto text-white/30">
                        {p.nudges ? `nudged ${p.nudges}×` : p.stage === "opened" ? "just looked" : "started paying"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <textarea
                value={nudgeText}
                onChange={(e) => setNudgeText(e.target.value)}
                rows={2}
                maxLength={600}
                placeholder="Leave blank for: “Ravi, your place is still open at ₹499 — finish the payment now to confirm your admission.”"
                className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none mb-2"
                data-testid="host-nudge-text"
              />
              <Button
                onClick={sendPayNudge}
                disabled={nudgeBusy || !payPending?.in_room}
                className="w-full rounded-full bg-yellow-300 hover:bg-yellow-200 text-black font-semibold h-11 disabled:opacity-40"
                data-testid="host-nudge-send"
              >
                <Send className="w-4 h-4 mr-2" />
                {nudgeBusy
                  ? "Sending…"
                  : payPending?.in_room
                  ? `Nudge ${payPending.in_room} non-payer${payPending.in_room === 1 ? "" : "s"}`
                  : "No non-payers in the room"}
              </Button>
            </section>

            {/* Ask for WhatsApp — collects numbers mid-class from users who
                don't already have one on file. */}
            <section>
              <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Ask for WhatsApp</div>
              <p className="text-xs text-neutral-500 mb-2">
                Show a full-screen popup asking every participant (who hasn't given a number yet) for their WhatsApp. Great mid-class hook.
              </p>
              {!askWaComposerOpen ? (
                <button
                  onClick={() => setAskWaComposerOpen(true)}
                  className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-display font-semibold tracking-tight flex items-center justify-center gap-2 shadow-[0_10px_30px_-10px_rgba(16,185,129,0.55)]"
                  data-testid="host-ask-whatsapp-open"
                >
                  <Send className="w-4 h-4" /> Ask everyone for WhatsApp
                </button>
              ) : (
                <div className="space-y-2">
                  <textarea
                    value={askWaDraft}
                    onChange={(e) => setAskWaDraft(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="Get the class recording + notes on WhatsApp 👇"
                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none"
                    data-testid="host-ask-whatsapp-message"
                  />
                  <p className="text-[11px] text-neutral-500">Optional — leave blank to use the default line above.</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setAskWaComposerOpen(false); setAskWaDraft(""); }}
                      className="flex-1 h-10 rounded-full border border-white/10 bg-white/[0.03] text-neutral-300 hover:bg-white/[0.08] text-sm font-display font-medium"
                      data-testid="host-ask-whatsapp-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={askForWhatsApp}
                      className="flex-1 h-10 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-display font-semibold shadow-[0_10px_30px_-10px_rgba(16,185,129,0.55)]"
                      data-testid="host-ask-whatsapp-send"
                    >
                      <Send className="inline w-4 h-4 mr-1" /> Send popup
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Waiting room */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-emerald-400">Waiting room</div>
                <div className="text-[11px] text-neutral-500">{lobby.length} pending</div>
              </div>
              {lobby.length > 0 && (
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={admitAllLobby}
                    className="flex-1 h-9 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-display font-semibold tracking-wider uppercase transition-colors shadow-[0_8px_24px_-8px_rgba(16,185,129,0.6)]"
                    data-testid="lobby-admit-all"
                  >
                    Admit all
                  </button>
                  <button
                    onClick={admitTrustedLobby}
                    className="flex-1 h-9 rounded-full bg-white/[0.03] hover:bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-display font-semibold tracking-wider uppercase transition-colors"
                    data-testid="lobby-admit-trusted"
                    title="Admit anyone whose WhatsApp is valid (10+ digits)"
                  >
                    Admit trusted
                  </button>
                </div>
              )}
              {lobby.length === 0 ? (
                <div className="text-xs text-neutral-500 py-2">No one is waiting.</div>
              ) : (
                <div className="space-y-2">
                  {lobby.map((l) => (
                    <div key={l.user_name} className="flex items-center justify-between px-3 py-2 rounded-lg border border-neutral-800 bg-neutral-950" data-testid={`lobby-row-${l.user_name}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{l.user_name}</div>
                        {l.whatsapp && <div className="text-[11px] text-neutral-500 truncate">{l.whatsapp}</div>}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => admitFromLobby(l.user_name, true)}
                          className="text-[10px] uppercase text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-full px-2 py-1"
                          data-testid={`lobby-admit-${l.user_name}`}
                        >
                          Admit
                        </button>
                        <button
                          onClick={() => admitFromLobby(l.user_name, false)}
                          className="text-[10px] uppercase text-red-300 border border-red-500/40 hover:bg-red-500/10 rounded-full px-2 py-1"
                          data-testid={`lobby-reject-${l.user_name}`}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Question composer (host-only responses) */}
            <section>
              <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Ask a question</div>
              <p className="text-xs text-neutral-500 mb-2">Send a Yes / No / Maybe question. Each choice can trigger its own follow-up card on the participant's screen.</p>
              {templates.filter((t) => t.kind === "question").length > 0 && (
                <div className="mb-3" data-testid="question-templates">
                  <div className="text-[10px] uppercase tracking-widest text-white/35 mb-1.5">Saved questions</div>
                  <div className="flex flex-wrap gap-1.5">
                    {templates
                      .filter((t) => t.kind === "question")
                      .map((t) => (
                        <button
                          key={t.id}
                          onClick={() => applyQuestionTemplate(t)}
                          className="h-8 px-3 rounded-full bg-white/[0.05] hover:bg-emerald-500/15 ring-1 ring-white/10 hover:ring-emerald-400/40 text-[11px] text-white/70 hover:text-emerald-200 transition-colors"
                          data-testid={`question-template-${t.id}`}
                        >
                          {t.title}
                        </button>
                      ))}
                  </div>
                </div>
              )}
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3 flex items-center gap-1.5">
                <span className="inline-block w-1 h-1 rounded-full bg-emerald-400" />
                Responses are private — only you and co-hosts can see them
              </div>
              <Input
                value={pollDraft.question}
                onChange={(e) => setPollDraft({ ...pollDraft, question: e.target.value })}
                placeholder="Do you want to register in our course?"
                className="bg-neutral-950 border-neutral-800 focus-visible:ring-emerald-500 h-10 mb-2"
                data-testid="poll-question-input"
              />
              <div className="space-y-2">
                {pollDraft.options.map((o, i) => (
                  <div key={o.key} className="rounded-lg border border-neutral-800 bg-neutral-950 p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Input
                        value={o.label}
                        onChange={(e) => {
                          const next = [...pollDraft.options];
                          next[i] = { ...o, label: e.target.value };
                          setPollDraft({ ...pollDraft, options: next });
                        }}
                        placeholder="Option label"
                        className="bg-neutral-900 border-neutral-800 h-8 text-xs"
                        data-testid={`poll-option-label-${o.key}`}
                      />
                    </div>
                    <textarea
                      value={o.follow_up_text}
                      onChange={(e) => {
                        const next = [...pollDraft.options];
                        next[i] = { ...o, follow_up_text: e.target.value };
                        setPollDraft({ ...pollDraft, options: next });
                      }}
                      rows={2}
                      placeholder="Follow-up message if they pick this…"
                      className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-1 text-xs resize-none mb-1"
                      data-testid={`poll-option-followup-${o.key}`}
                    />
                    <Input
                      type="url"
                      value={o.follow_up_link}
                      onChange={(e) => {
                        const next = [...pollDraft.options];
                        next[i] = { ...o, follow_up_link: e.target.value };
                        setPollDraft({ ...pollDraft, options: next });
                      }}
                      placeholder="https://... (optional link on the card)"
                      className="bg-neutral-900 border-neutral-800 h-8 text-xs"
                      data-testid={`poll-option-link-${o.key}`}
                    />
                    <button
                      onClick={() => {
                        const next = [...pollDraft.options];
                        next[i] = { ...o, follow_up_pay: !o.follow_up_pay };
                        setPollDraft({ ...pollDraft, options: next });
                      }}
                      className={`w-full mt-1 h-8 rounded text-[10px] uppercase tracking-[0.14em] transition-colors ${
                        o.follow_up_pay
                          ? "bg-yellow-300 text-black font-semibold"
                          : "ring-1 ring-white/10 text-white/40 hover:text-white"
                      }`}
                      data-testid={`poll-option-pay-${o.key}`}
                    >
                      {o.follow_up_pay ? "✓ Opens fees checkout" : "Open fees checkout on this answer"}
                    </button>
                  </div>
                ))}
              </div>
              <Button
                onClick={launchPoll}
                disabled={!pollDraft.question.trim()}
                className="w-full mt-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-10"
                data-testid="poll-send-btn"
              >
                <Send className="w-4 h-4 mr-2" /> Send question
              </Button>
              <button
                onClick={() => saveAsTemplate("question")}
                className="w-full mt-1.5 h-9 rounded-full ring-1 ring-white/10 text-[10.5px] uppercase tracking-[0.16em] text-white/45 hover:text-white hover:ring-emerald-400/30 transition-colors"
                data-testid="question-save-template"
              >
                Save as template
              </button>

              {/* Live question responses — host-only view of streaming
                  counts. Never sent to participants. */}
              {hostPoll && (
                <div
                  className="mt-4 rounded-2xl border border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.04] to-transparent p-4"
                  data-testid="host-poll-tally"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] text-emerald-400 font-bold">QUESTION&nbsp;RESPONSES</div>
                      <div className="text-sm font-display font-semibold text-white mt-0.5 leading-tight">
                        {hostPoll.question}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <div className="font-display text-2xl font-extrabold text-white leading-none">
                        {pollTally.total}
                      </div>
                      <div className="text-[10px] tracking-widest text-neutral-500 uppercase">Responses</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {hostPoll.options.map((o, idx) => {
                      const c = pollTally.counts[o.key] || 0;
                      const pct = pollTally.total > 0 ? Math.round((c / pollTally.total) * 100) : 0;
                      const who = pollTally.rows.filter((r) => r.choice === o.key);
                      const open = openTallyKey === o.key;
                      return (
                        <div key={o.key} data-testid={`host-poll-tally-row-${o.key}`}>
                          <div className="flex items-center justify-between text-xs mb-1.5">
                            <button
                              onClick={() => setOpenTallyKey(open ? null : o.key)}
                              className="text-white font-medium hover:text-emerald-300 transition-colors"
                              data-testid={`host-poll-who-${o.key}`}
                            >
                              {o.label} {c > 0 && <span className="text-white/35">· who?</span>}
                            </button>
                            <span className="text-emerald-300 font-mono tabular-nums font-semibold">
                              {c} · {pct}%
                            </span>
                          </div>
                          <div className="relative h-3 rounded-full bg-white/[0.04] overflow-hidden border border-white/5">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${pct}%` }}
                              transition={{ type: "spring", damping: 24, stiffness: 200 }}
                              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-300 shadow-[0_0_18px_rgba(16,185,129,0.55)]"
                            />
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${pct}%` }}
                              transition={{ type: "spring", damping: 24, stiffness: 200 }}
                              className="absolute inset-y-0 left-0 opacity-70"
                            >
                              <div className="udyog-shimmer-btn absolute inset-0 rounded-full" />
                            </motion.div>
                          </div>
                          {open && (
                            <div
                              className="mt-2 rounded-xl bg-black/50 ring-1 ring-white/[0.06] p-2.5"
                              data-testid={`host-poll-who-list-${o.key}`}
                            >
                              {who.length === 0 ? (
                                <div className="text-[11px] text-white/30">Nobody picked this yet.</div>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[9.5px] uppercase tracking-[0.18em] text-emerald-400/80">
                                      {who.length} {who.length === 1 ? "person" : "people"}
                                    </span>
                                    <button
                                      onClick={async () => {
                                        const nums = who.filter((w) => w.whatsapp).map((w) => `91${w.whatsapp}`);
                                        if (nums.length === 0) {
                                          toast("No numbers on file for this answer");
                                          return;
                                        }
                                        const ok = await copyText(nums.join("\n"));
                                        if (ok)
                                          toast.success(
                                            nums.length === 1 ? "Number copied" : `${nums.length} numbers copied`
                                          );
                                        else toast.error("Couldn't copy — long-press to select instead");
                                      }}
                                      className="text-[10px] text-emerald-300 hover:underline"
                                      data-testid={`host-poll-copy-${o.key}`}
                                    >
                                      Copy numbers
                                    </button>
                                  </div>
                                  <div className="space-y-1">
                                    {who.map((w, wi) => (
                                      <div key={wi} className="flex items-center gap-2 text-[11.5px]">
                                        <span className="text-white/75 truncate flex-1">{w.user_name}</span>
                                        {w.whatsapp ? (
                                          <a
                                            href={`https://wa.me/91${w.whatsapp}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-emerald-300 tabular-nums hover:underline"
                                          >
                                            {w.whatsapp}
                                          </a>
                                        ) : (
                                          <span className="text-white/20">no number</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setHostPoll(null)}
                    className="mt-3 text-[11px] uppercase tracking-widest text-neutral-500 hover:text-neutral-300"
                    data-testid="host-poll-tally-clear"
                  >
                    Hide results
                  </button>
                </div>
              )}
            </section>

            {/* Co-hosts */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-emerald-400">Co-hosts</div>
                <div className="text-[11px] text-neutral-500">{coHosts.size} / 3</div>
              </div>
              <p className="text-xs text-neutral-500 mb-2">Tap a participant to promote them (up to 3).</p>
              <div className="space-y-1">
                {participants
                  .filter((p) => !p.isLocal)
                  .map((p) => {
                    const isCo = coHosts.has(p.identity);
                    return (
                      <button
                        key={p.identity}
                        onClick={() => {
                          setCoHosts((prev) => {
                            const next = new Set(prev);
                            if (next.has(p.identity)) next.delete(p.identity);
                            else if (next.size < 3) next.add(p.identity);
                            else {
                              toast.error("Max 3 co-hosts");
                              return prev;
                            }
                            // Broadcast the whole updated set — every client
                            // hears "cohost-set" and updates local privileges.
                            publishData({ type: "cmd", cmd: "cohost-set", identities: Array.from(next) });
                            return next;
                          });
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors text-sm ${
                          isCo ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-neutral-950 border-neutral-800 hover:bg-white/5"
                        }`}
                        data-testid={`host-cohost-${p.identity}`}
                      >
                        <span className="truncate">{p.name || p.identity}</span>
                        <span className="text-[10px] uppercase tracking-widest">{isCo ? "Co-host" : "Promote"}</span>
                      </button>
                    );
                  })}
                {participants.filter((p) => !p.isLocal).length === 0 && (
                  <div className="text-xs text-neutral-500 py-3 text-center">No one else has joined yet.</div>
                )}
              </div>
            </section>
          </div>
        </SideDrawer>
      )}

      {/* Leave / End-for-everyone dialog — hosts get 3 options, participants get a simple confirm */}
      <AnimatePresence>
        {showLeaveDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-xl px-4"
            onClick={() => setShowLeaveDialog(false)}
            data-testid="leave-dialog"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.97 }}
              transition={{ type: "spring", damping: 22, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-md w-full rounded-3xl border border-white/10 bg-gradient-to-b from-neutral-950 to-black p-7 shadow-2xl"
            >
              <div className="text-[10px] tracking-[0.4em] text-emerald-400 font-bold mb-2">
                LEAVE&nbsp;MEETING
              </div>
              <h3 className="font-display text-2xl font-extrabold text-white leading-tight mb-2">
                {isHost ? "What do you want to do?" : "Leave the meeting?"}
              </h3>
              <p className="text-sm text-neutral-400 mb-6">
                {isHost
                  ? "As the host you can leave alone and the meeting keeps running, or end the meeting for everyone right now."
                  : "You're about to leave the class. Are you sure?"}
              </p>
              <div className="grid gap-3">
                {isHost && (
                  <button
                    onClick={endForEveryone}
                    className="w-full h-12 rounded-full bg-red-500/90 hover:bg-red-500 text-white font-display font-semibold tracking-tight shadow-[0_10px_30px_-10px_rgba(239,68,68,0.6)]"
                    data-testid="leave-end-for-all-btn"
                  >
                    End for everyone
                  </button>
                )}
                <button
                  onClick={leaveMyself}
                  className={`w-full h-12 rounded-full font-display font-semibold ${
                    isHost
                      ? "border border-white/10 bg-white/[0.03] text-white hover:bg-white/[0.08]"
                      : "bg-red-500/90 hover:bg-red-500 text-white shadow-[0_10px_30px_-10px_rgba(239,68,68,0.6)]"
                  }`}
                  data-testid="leave-only-me-btn"
                >
                  {isHost ? "Leave — meeting stays live" : "Yes, leave now"}
                </button>
                <button
                  onClick={() => setShowLeaveDialog(false)}
                  className="w-full h-11 rounded-full border border-white/10 bg-white/[0.03] text-neutral-200 hover:bg-white/[0.08] font-display font-medium"
                  data-testid="leave-cancel-btn"
                >
                  {isHost ? "Cancel" : "No, stay in the meeting"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Participant poll modal */}
      <AnimatePresence>
        {activePoll && !isHost && pollReady && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed inset-0 z-[55] flex items-center justify-center bg-black/95 px-4"
            data-testid="poll-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              style={{ willChange: "transform, opacity" }}
              className="relative max-w-lg w-full"
            >

              <div className="relative rounded-[28px] border border-emerald-500/50 bg-neutral-950 p-8 md:p-10 shadow-[0_35px_80px_-20px_rgba(16,185,129,0.45)]">
                <button
                  onClick={() => {
                    answeredPolls.current.add(activePoll.id);
                    setActivePoll(null);
                  }}
                  className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/50 hover:text-white transition-colors"
                  data-testid="poll-close-x"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
                {activePoll.banner_url && (
                  <img
                    src={activePoll.banner_url}
                    alt=""
                    className="w-full h-40 md:h-48 object-cover rounded-2xl ring-1 ring-white/10 mb-6"
                    decoding="sync" fetchPriority="high"
                    data-testid="poll-banner"
                  />
                )}
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 shrink-0" />
                  <div className="text-[10px] tracking-[0.4em] text-emerald-400 font-bold">HOST&nbsp;IS&nbsp;ASKING</div>
                </div>
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="font-display text-3xl md:text-4xl font-extrabold mb-8 leading-[1.05] tracking-tight text-white"
                  data-testid="poll-question-text"
                >
                  {activePoll.question}
                </motion.h3>
                <div className="grid gap-3">
                  {activePoll.options.map((o, idx) => (
                    <motion.button
                      key={o.key ?? o.label ?? idx}
                      onClick={() => answerPoll(o.key)}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={
                        idx === 0
                          ? "udyog-shimmer-btn w-full h-14 rounded-full text-black font-display font-bold text-base tracking-tight shadow-[0_10px_35px_-10px_rgba(16,185,129,0.6)]"
                          : "w-full h-14 rounded-full bg-black text-white ring-1 ring-white/40 font-display font-bold text-base tracking-tight hover:bg-neutral-900 transition-colors"
                      }
                      data-testid={`poll-answer-${o.key}`}
                    >
                      {o.label}
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Participant "give us your WhatsApp" popup — host-triggered mid-class */}
      <AnimatePresence>
        {askWaOpen && !isHost && !isCoHost && !waAlreadyCaptured && (
          <AskWhatsAppPopup
            message={askWaMsg}
            onSubmit={submitMyWhatsApp}
            onDismiss={() => setAskWaOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Poll follow-up card */}
      <AnimatePresence>
        {pollAnswer && followupReady && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed inset-0 z-[56] flex items-center justify-center bg-black/95 px-4"
            data-testid="poll-followup"
          >
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              style={{ willChange: "transform, opacity" }}
              className="relative max-w-xl w-full"
            >

              <div className="relative rounded-[28px] border border-emerald-500/50 bg-neutral-950 p-8 md:p-10 shadow-[0_35px_80px_-20px_rgba(16,185,129,0.45)]">
                <button
                  onClick={() => setPollAnswer(null)}
                  className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/50 hover:text-white transition-colors"
                  data-testid="poll-followup-close"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
                {pollAnswer.banner_url && (
                  <img
                    src={pollAnswer.banner_url}
                    alt=""
                    className="w-full h-40 md:h-48 object-cover rounded-2xl ring-1 ring-white/10 mb-6"
                    decoding="sync" fetchPriority="high"
                    data-testid="poll-followup-banner"
                  />
                )}
                <div className="text-[10px] tracking-[0.4em] text-emerald-400 font-bold mb-3">FROM&nbsp;THE&nbsp;HOST</div>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="font-display text-3xl md:text-4xl font-extrabold leading-[1.1] mb-8 text-white tracking-tight"
                  data-testid="poll-followup-text"
                >
                  {pollAnswer.follow_up_text || "Thanks for your response!"}
                </motion.p>
                {pollAnswer.follow_up_pay && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <button
                      onClick={() => {
                        api
                          .post(`/meetings/${roomName}/polls/${pollAnswer.poll_id}/link-open`, {
                            user_name: userName,
                            choice: pollAnswer.key,
                          })
                          .catch(() => {});
                        setPollAnswer(null);
                        setPayOpen(true);
                      }}
                      className="w-full h-14 mb-2 rounded-full bg-yellow-300 text-black font-display font-bold text-base tracking-tight shadow-[0_10px_35px_-10px_rgba(253,224,71,0.6)] transition-transform hover:scale-[1.02] active:scale-[0.99]"
                      data-testid="poll-followup-pay"
                    >
                      {pollAnswer.follow_up_button || "Pay Your Fees"}
                    </button>
                  </motion.div>
                )}
                {pollAnswer.follow_up_link && !pollAnswer.follow_up_pay && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <a
                      href={pollAnswer.follow_up_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        // Tapping the link behind an answer is tracked on the
                        // answer itself, so the host sees who actually went.
                        api
                          .post(`/meetings/${roomName}/polls/${pollAnswer.poll_id}/link-open`, {
                            user_name: userName,
                            choice: pollAnswer.key,
                          })
                          .catch(() => {});
                        setPollAnswer(null);
                      }}
                      className="block"
                    >
                      <button
                        className="udyog-shimmer-btn w-full h-14 rounded-full text-black font-display font-bold text-base tracking-tight shadow-[0_10px_35px_-10px_rgba(16,185,129,0.6)] transition-transform hover:scale-[1.02] active:scale-[0.99]"
                        data-testid="poll-followup-link"
                      >
                        {pollAnswer.follow_up_button || "Open link"}
                      </button>
                    </a>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full-screen broadcast popup. Deliberately CHEAP to paint: this lands
          on top of live video on a mid-range phone, and the old version
          (full-screen backdrop-blur + animated orbs + two blur-3xl auras +
          a spring) dropped frames every time it appeared. Opacity and
          transform only. */}
      <AnimatePresence>
        {poppedBroadcast && broadcastReady && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 px-4"
            style={{ willChange: "opacity" }}
            data-testid="broadcast-popup"
          >
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-2xl w-full"
              style={{ willChange: "transform, opacity" }}
            >
              {/* Card */}
              <div className="relative rounded-[28px] border border-emerald-500/50 bg-neutral-950 p-8 md:p-10 shadow-[0_35px_80px_-20px_rgba(16,185,129,0.45)] overflow-hidden">
                {/* Auto-dismiss countdown bar at the top */}
                <div
                  key={poppedBroadcast.id}
                  className="udyog-progress-bar absolute top-0 left-0 h-[3px] w-full bg-gradient-to-r from-emerald-400 via-emerald-300 to-emerald-500"
                  style={{ animationDuration: `${poppedBroadcast.duration_sec || 25}s` }}
                />

                <button
                  onClick={dismissBroadcast}
                  className="absolute top-4 right-4 z-10 h-9 w-9 grid place-items-center rounded-full bg-black/50 hover:bg-black/80 ring-1 ring-white/10 text-white/50 hover:text-white transition-colors"
                  data-testid="broadcast-popup-dismiss-btn"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>

                {poppedBroadcast.banner_url && (
                  <img
                    src={poppedBroadcast.banner_url}
                    alt=""
                    className="w-full h-44 md:h-52 object-cover rounded-2xl ring-1 ring-white/10 mb-7"
                    decoding="sync" fetchPriority="high"
                    data-testid="broadcast-banner"
                  />
                )}

                <div className="flex items-center gap-4 mb-7">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-black flex items-center justify-center shrink-0">
                    <Megaphone className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.4em] text-emerald-400 font-bold">
                      ANNOUNCEMENT&nbsp;·&nbsp;FROM&nbsp;HOST
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 font-medium">Udyog Classes</div>
                  </div>
                </div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="font-display text-3xl md:text-5xl font-extrabold leading-[1.05] mb-8 text-white tracking-tight"
                  data-testid="broadcast-popup-text"
                >
                  {poppedBroadcast.text}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  {poppedBroadcast.pay_button && (
                    <button
                      onClick={() => {
                        api
                          .post(`/meetings/${roomName}/broadcast-click`, {
                            broadcast_id: poppedBroadcast.id,
                            user_name: userName,
                          })
                          .catch(() => {});
                        dismissBroadcast();
                        setPayOpen(true);
                      }}
                      className="w-full h-14 rounded-full bg-yellow-300 text-black font-display font-bold text-base tracking-tight shadow-[0_10px_35px_-10px_rgba(253,224,71,0.6)] transition-transform hover:scale-[1.02] active:scale-[0.99] mb-2"
                      data-testid="broadcast-pay-btn"
                    >
                      {poppedBroadcast.button_label || "Pay Your Fees"}
                    </button>
                  )}
                  {/* One call to action only — the fees button always wins. */}
                  {poppedBroadcast.link && !poppedBroadcast.pay_button && (
                    <a
                      href={poppedBroadcast.link}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        api
                          .post(`/meetings/${roomName}/broadcast-click`, {
                            broadcast_id: poppedBroadcast.id,
                            user_name: userName,
                          })
                          .catch(() => {});
                        dismissBroadcast();
                      }}
                      className="block"
                    >
                      <button
                        className="udyog-shimmer-btn w-full h-14 rounded-full text-black font-display font-bold text-base tracking-tight shadow-[0_10px_35px_-10px_rgba(16,185,129,0.6)] transition-transform hover:scale-[1.02] active:scale-[0.99]"
                        data-testid="broadcast-popup-link-btn"
                      >
                        {poppedBroadcast.button_label || "Open link"}
                      </button>
                    </a>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ------------------------------------------------------------------
// Sub-components
// ------------------------------------------------------------------
function ParticipantTile({ trackRef, compact = false, large = false, pinInfo = null }) {
  const p = trackRef.participant;
  const isScreen = trackRef.source === Track.Source.ScreenShare;
  const hasVideo = !!trackRef.publication?.track && !trackRef.publication.isMuted;
  const isSpeaking = p.isSpeaking;
  const isLocal = p.isLocal;
  const displayName = p.name || p.identity;
  const isHostTile = isHostParticipant(p);

  // When pinned (large), the tile behaves like the shared-content tile:
  // it fills the whole stage, has the emerald premium border + soft glow,
  // and shows a "Pinned" pill in the bottom-left instead of just a name.
  const containerCls = large
    ? "relative w-full h-full overflow-hidden bg-black rounded-2xl border-[1.5px] border-emerald-500/50 shadow-[0_0_40px_rgba(16,185,129,0.18)]"
    : `relative rounded-2xl overflow-hidden bg-zinc-900 border transition-all duration-300 ease-out ${compact ? "aspect-video w-full h-full" : "aspect-video w-full"} ${
        isSpeaking ? "border-emerald-400/70 ring-2 ring-emerald-400/50 shadow-[0_0_30px_rgba(16,185,129,0.35)] scale-[1.015] z-10" : "border-white/5 scale-100"
      }`;

  return (
    <div className={containerCls} data-testid={`tile-${p.identity}`}>
      {hasVideo ? (
        <VideoTrack
          trackRef={trackRef}
          className={`absolute inset-0 w-full h-full ${large ? "object-contain" : "object-cover"}`}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
          {isHostTile ? (
            <BrandAvatar size={large ? 128 : 80} testid={`host-brand-${p.identity}`} />
          ) : (
            <img
              src={avatarUrl(displayName || p.identity)}
              alt={displayName}
              loading="lazy"
              className={`rounded-full ring-1 ring-emerald-500/40 bg-emerald-500/10 object-cover ${
                large ? "w-32 h-32" : "w-20 h-20"
              }`}
              data-testid={`avatar-${p.identity}`}
            />
          )}
        </div>
      )}

      {large ? (
        // Pinned overlay — mimics the share tile pill treatment.
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 pointer-events-none">
          <div className="text-[11px] uppercase tracking-widest text-emerald-300 bg-black/70 rounded-full px-3 py-1 border border-emerald-500/40 pointer-events-auto">
            {isLocal ? `${displayName} (you)` : displayName}
            {pinInfo?.pinned ? " · Pinned" : isSpeaking ? " · Speaking" : ""}
          </div>
          {(pinInfo?.onUnpin || pinInfo?.onPin) && (
            <button
              onClick={pinInfo.pinned ? pinInfo.onUnpin : pinInfo.onPin}
              className="pointer-events-auto h-9 px-3 rounded-full border border-emerald-500/50 bg-black/70 hover:bg-black/90 text-emerald-300 inline-flex items-center gap-1.5 transition-colors shadow-[0_4px_20px_-4px_rgba(16,185,129,0.6)]"
              data-testid={pinInfo.pinned ? "tile-unpin-btn" : "tile-pin-btn"}
              title={pinInfo.pinned ? "Unpin" : "Pin for everyone"}
            >
              <Pin className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase tracking-widest font-semibold">
                {pinInfo.pinned ? "Unpin" : "Pin"}
              </span>
            </button>
          )}
        </div>
      ) : (
        <div className="absolute inset-x-0 bottom-0 p-3 flex items-center justify-between bg-gradient-to-t from-black/80 to-transparent">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium truncate max-w-[180px]" data-testid={`tile-name-${p.identity}`}>
              {isLocal ? `${displayName} (you)` : displayName}
            </span>
            {p.isMicrophoneEnabled === false ? (
              <MicOff className="w-3.5 h-3.5 text-red-400" />
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <ConnBars quality={p.connectionQuality} />
            {isScreen && (
              <span className="text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-0.5">
                Sharing
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// 5-bar signal indicator like WhatsApp calls. LiveKit exposes
// connectionQuality as "excellent" | "good" | "poor" | "lost" | "unknown".
function ConnBars({ quality }) {
  const q = quality || "unknown";
  const level = q === "excellent" ? 5 : q === "good" ? 3 : q === "poor" ? 1 : q === "lost" ? 0 : 4;
  const colour =
    level >= 4 ? "bg-emerald-400" : level >= 2 ? "bg-amber-400" : level >= 1 ? "bg-red-400" : "bg-neutral-700";
  return (
    <div className="flex items-end gap-[2px] h-3.5" title={`Connection: ${q}`} data-testid={`conn-${q}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={`w-[3px] rounded-[1px] ${i <= level ? colour : "bg-white/15"}`}
          style={{ height: `${20 + i * 15}%` }}
        />
      ))}
    </div>
  );
}

function ParticipantRow({ participant, canModerate, hostCanPromote, isPinned, isCoHost, onPin, onToggleCoHost, onMute, onCamOff, onKick }) {
  const name = participant.name || participant.identity;
  const isYou = participant.isLocal;
  const isHostRow = isHostParticipant(participant);
  return (
    <div className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-white/5" data-testid={`roster-row-${participant.identity}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {isHostRow ? (
          <BrandAvatar size={36} testid={`roster-brand-${participant.identity}`} />
        ) : (
          <img
            src={avatarUrl(name)}
            alt={name}
            loading="lazy"
            className="w-9 h-9 rounded-full ring-1 ring-emerald-500/40 bg-emerald-500/10 object-cover shrink-0"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate flex items-center gap-1.5 flex-wrap">
            {isYou ? `${name} (you)` : name}
            {isCoHost && (
              <span className="text-[9px] uppercase tracking-widest text-emerald-300 border border-emerald-500/40 rounded-full px-1.5 py-0.5 bg-emerald-500/10">
                Co-host
              </span>
            )}
            {isPinned && (
              <span className="text-[9px] uppercase tracking-widest text-emerald-300 border border-emerald-500/40 rounded-full px-1.5 py-0.5">
                Pinned
              </span>
            )}
          </div>
          <div className="text-[11px] text-neutral-500 flex items-center gap-1.5">
            {participant.isSpeaking ? "Speaking" : "Idle"}
            <ConnBars quality={participant.connectionQuality} />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {participant.isMicrophoneEnabled ? (
          <Mic className="w-4 h-4 text-neutral-400" />
        ) : (
          <MicOff className="w-4 h-4 text-red-400" />
        )}
        {participant.isCameraEnabled ? (
          <VideoIcon className="w-4 h-4 text-neutral-400" />
        ) : (
          <VideoOff className="w-4 h-4 text-neutral-600" />
        )}

        {canModerate && !isYou && (
          <>
            <div className="w-px h-5 bg-white/10 mx-1" />
            {hostCanPromote && (
              <button
                onClick={onToggleCoHost}
                className={`h-7 px-2 rounded-full border text-[10px] uppercase tracking-widest font-semibold transition-colors ${
                  isCoHost
                    ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                    : "border-white/10 hover:border-emerald-500/40 hover:bg-emerald-500/10 text-neutral-300 hover:text-emerald-300"
                }`}
                title={isCoHost ? "Demote co-host" : "Promote to co-host"}
                data-testid={`row-cohost-${participant.identity}`}
              >
                {isCoHost ? "Demote" : "Add Co-host"}
              </button>
            )}
            <button
              onClick={onPin}
              className={`h-7 w-7 rounded-full border flex items-center justify-center transition-colors ${
                isPinned
                  ? "border-emerald-500/60 bg-emerald-500/20 text-emerald-300"
                  : "border-white/10 hover:border-emerald-500/40 hover:bg-emerald-500/10 text-neutral-400 hover:text-emerald-300"
              }`}
              title={isPinned ? "Unpin" : "Pin for everyone"}
              data-testid={`row-pin-${participant.identity}`}
            >
              <Pin className="w-3.5 h-3.5" />
            </button>
            <RowMenu
              participant={participant}
              onMute={onMute}
              onCamOff={onCamOff}
              onKick={onKick}
            />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The three-dots menu on a participant row: mute, stop video, remove.
 * Mute and Stop video only appear when that mic/camera is actually on, so the
 * host never taps something that does nothing.
 */
function RowMenu({ participant, onMute, onCamOff, onKick }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    // A tap anywhere else closes it — including on another row's menu.
    const t = setTimeout(() => document.addEventListener("click", close, { once: true }), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("click", close);
    };
  }, [open]);

  const item =
    "w-full h-10 px-3 flex items-center gap-2.5 text-[12.5px] text-left hover:bg-white/[0.06] transition-colors";

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="h-7 w-7 rounded-full border border-white/10 hover:border-white/25 text-neutral-400 hover:text-white flex items-center justify-center transition-colors"
        title="More options"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`row-menu-${participant.identity}`}
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-9 z-50 w-48 rounded-xl overflow-hidden bg-[#0b0f0d] ring-1 ring-white/10 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.9)]"
          onClick={(e) => e.stopPropagation()}
          role="menu"
          data-testid={`row-menu-panel-${participant.identity}`}
        >
          {participant.isMicrophoneEnabled && (
            <button
              onClick={() => {
                setOpen(false);
                onMute();
              }}
              className={`${item} text-amber-200`}
              data-testid={`row-mute-${participant.identity}`}
            >
              <MicOff className="w-4 h-4" /> Mute their mic
            </button>
          )}
          {participant.isCameraEnabled && (
            <button
              onClick={() => {
                setOpen(false);
                onCamOff();
              }}
              className={`${item} text-amber-200`}
              data-testid={`row-camoff-${participant.identity}`}
            >
              <VideoOff className="w-4 h-4" /> Stop their video
            </button>
          )}
          {!participant.isMicrophoneEnabled && !participant.isCameraEnabled && (
            <div className="px-3 py-2.5 text-[11.5px] text-white/35">
              Mic and camera are already off
            </div>
          )}
          <button
            onClick={() => {
              setOpen(false);
              onKick();
            }}
            className={`${item} text-red-300 border-t border-white/[0.07]`}
            data-testid={`row-kick-${participant.identity}`}
          >
            <UserMinus className="w-4 h-4" /> Remove &amp; block
          </button>
        </div>
      )}
    </div>
  );
}

function ChatBubble({ m, isHost, userName, pinned = false, onDelete, onPin, onBan }) {
  const canDelete = isHost || m.user_name === userName;
  const canPin = isHost;
  const canBan = isHost && m.user_name !== userName && !m.from_host;
  // Only host messages get linkified; guest links were already stripped server-side.
  const renderText = () => {
    if (!m.from_host) return m.text;
    const parts = m.text.split(/(https?:\/\/\S+)/g);
    return parts.map((p, i) =>
      /^https?:\/\//i.test(p) ? (
        <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="text-emerald-300 underline break-all">
          {p}
        </a>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  };
  return (
    <div className="group text-sm flex gap-2.5" data-testid={`chat-msg-${m.id}`}>
      {m.from_host ? (
        <BrandAvatar size={28} testid="chat-brand-host" />
      ) : (
        <img
          src={avatarUrl(m.user_name || m.user_id)}
          alt={m.user_name || "Guest"}
          loading="lazy"
          className="w-7 h-7 shrink-0 rounded-full ring-1 ring-emerald-400/25 bg-emerald-500/10 object-cover"
        />
      )}
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[9.5px] uppercase tracking-[0.18em] text-white/35 truncate">{m.user_name}</span>
          {m.from_host && (
            <span className="text-[9px] uppercase tracking-widest text-emerald-300 ring-1 ring-emerald-400/35 rounded-full px-1.5">Host</span>
          )}
        </div>
        <div className="inline-flex items-start gap-2 max-w-full">
          <div
            className={`px-3.5 py-2 rounded-2xl break-words text-[12.5px] ${
              pinned
                ? "bg-emerald-500/[0.12] ring-1 ring-emerald-400/25 text-emerald-50"
                : m.from_host
                ? "bg-emerald-500/[0.10] ring-1 ring-emerald-400/20 text-emerald-50"
                : "bg-white/[0.045] ring-1 ring-white/[0.07] text-white/90"
            }`}
          >
            {renderText()}
          </div>
          <div className="hidden group-hover:flex items-center gap-1 pt-1">
          {canPin && (
            <button
              onClick={() => onPin(m)}
              className="text-[10px] uppercase text-amber-300 hover:text-amber-200 px-1.5 py-0.5 border border-amber-500/40 rounded-full"
              data-testid={`chat-pin-${m.id}`}
              title={pinned ? "Unpin" : "Pin"}
            >
              {pinned ? "Unpin" : "Pin"}
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(m)}
              className="text-[10px] uppercase text-red-300 hover:text-red-200 px-1.5 py-0.5 border border-red-500/40 rounded-full"
              data-testid={`chat-del-${m.id}`}
              title="Delete"
            >
              Delete
            </button>
          )}
          {canBan && (
            <button
              onClick={() => onBan(m)}
              className="text-[10px] uppercase text-red-400 hover:text-red-300 px-1.5 py-0.5 border border-red-500/50 rounded-full bg-red-500/10"
              data-testid={`chat-ban-${m.id}`}
              title="Ban from meeting"
            >
              Ban
            </button>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Everyone, in tiles — the Google-Meet-style wall. One card per participant,
 * camera when they have one on and their animal card when they don't, scrolling
 * as deep as the class goes. The main stage stays focused on the host; this is
 * where you go to actually see the room.
 */
function EveryoneGrid({ open, onClose, participants, cameraTracks, pinnedIdentity, coHosts, chatOpen }) {
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!open) setQ("");
  }, [open]);
  const swipe = useRef(null);
  const onTouchStart = (e) => {
    if (e.touches.length !== 1) {
      swipe.current = null;
      return;
    }
    const t = e.touches[0];
    swipe.current = { x: t.clientX, y: t.clientY, at: Date.now() };
  };
  const onTouchEnd = (e) => {
    const s = swipe.current;
    swipe.current = null;
    const t = e.changedTouches?.[0];
    if (!s || !t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (dx > 64 && dx > Math.abs(dy) * 2 && Date.now() - s.at < 700) onClose();
  };
  const camFor = (identity) =>
    cameraTracks.find(
      (t) => t.participant.identity === identity && t.publication?.track && !t.publication.isMuted
    );
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? participants.filter((p) => (p.name || p.identity).toLowerCase().includes(needle))
    : participants;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", stiffness: 420, damping: 40 }}
          className={`fixed inset-0 z-[19] bg-[#050505] flex flex-col pt-14 md:pt-16 [@media(max-height:520px)]:pt-11 transition-[padding] duration-300 ${
            chatOpen ? "lg:pr-[372px]" : ""
          }`}
          data-testid="everyone-grid"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="h-12 shrink-0 px-3 flex items-center gap-2">
            <button
              onClick={onClose}
              className="h-9 pl-2 pr-3 inline-flex items-center gap-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/[0.08] transition-colors"
              data-testid="everyone-grid-back"
              aria-label="Back to class"
            >
              <ArrowLeft className="w-[18px] h-[18px]" strokeWidth={2} />
              <span className="text-[9.5px] uppercase tracking-[0.16em]">Back to class</span>
            </button>
            <div className="font-display text-base font-semibold ml-1">
              Everyone <span className="text-white/35">· {participants.length}</span>
            </div>
            <span className="ml-auto text-[9.5px] uppercase tracking-[0.16em] text-white/25 hidden sm:inline">
              Swipe right to go back
            </span>
          </div>

          <div className="px-3 pt-1 shrink-0">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search a student by name…"
                className="w-full h-11 pl-10 pr-10 rounded-full bg-white/[0.05] ring-1 ring-white/[0.08] focus:ring-emerald-400/40 text-[13px] text-white placeholder:text-white/25 outline-none transition-shadow"
                data-testid="everyone-search"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-full text-white/40 hover:text-white hover:bg-white/[0.08]"
                  data-testid="everyone-search-clear"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {needle && (
              <div className="text-[10.5px] text-white/35 mt-2 ml-1" data-testid="everyone-search-count">
                {shown.length} of {participants.length} match “{q.trim()}”
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar p-3 pb-40 [@media(max-height:520px)]:pb-24">
            {shown.length === 0 ? (
              <div className="pt-16 text-center text-[13px] text-white/30" data-testid="everyone-no-match">
                Nobody here by that name.
              </div>
            ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {shown.map((p) => {
                const cam = camFor(p.identity);
                const name = p.name || p.identity;
                const host = isHostParticipant(p);
                const muted = !p.isMicrophoneEnabled;
                return (
                  <div
                    key={p.identity}
                    className={`relative aspect-[4/3] rounded-2xl overflow-hidden bg-neutral-950 ring-1 grid place-items-center ${
                      pinnedIdentity === p.identity ? "ring-emerald-400/60" : "ring-white/[0.07]"
                    }`}
                    data-testid={`everyone-tile-${p.identity}`}
                  >
                    {cam ? (
                      <VideoTrack trackRef={cam} className="absolute inset-0 w-full h-full object-cover" />
                    ) : host ? (
                      <BrandAvatar size={72} />
                    ) : (
                      <img
                        src={avatarUrl(name)}
                        alt=""
                        className="w-[72px] h-[72px] rounded-full object-cover ring-1 ring-emerald-500/30"
                      />
                    )}
                    <div className="absolute inset-x-0 bottom-0 px-2.5 py-1.5 bg-gradient-to-t from-black/90 to-transparent flex items-center gap-1.5">
                      {muted && <MicOff className="w-3 h-3 text-red-400 shrink-0" />}
                      <span className="text-[11px] text-white/85 truncate">
                        {name}
                        {p.isLocal && <span className="text-white/40"> (you)</span>}
                      </span>
                      {(host || coHosts?.has(p.identity)) && (
                        <span className="ml-auto text-[8.5px] uppercase tracking-widest text-emerald-300 shrink-0">
                          {host ? "Host" : "Co-host"}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SideDrawer({ open, onClose, title, testid, children, wide = false }) {
  return (
    <div
      className={`absolute top-0 right-0 h-full ${wide ? "w-full sm:w-[28rem]" : "w-full sm:w-96"} bg-[#0f0f10] border-l border-neutral-900 z-40 transform transition-transform duration-300 ease-out flex flex-col ${
        open ? "translate-x-0" : "translate-x-full"
      }`}
      data-testid={testid}
    >
      <div className="h-14 px-3 flex items-center justify-between border-b border-neutral-900 shrink-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 shrink-0 grid place-items-center rounded-full text-white/60 hover:text-white hover:bg-white/[0.08] transition-colors"
            data-testid={`${testid}-back`}
            aria-label="Back to class"
            title="Back to class"
          >
            <ArrowLeft className="w-[18px] h-[18px]" strokeWidth={2} />
          </button>
          <div className="font-display text-lg font-semibold truncate">{title}</div>
        </div>
        <Button
          variant="ghost"
          onClick={onClose}
          className="text-neutral-400 hover:text-white h-8 w-8 p-0"
          data-testid={`${testid}-close`}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">{children}</div>
    </div>
  );
}

function HostToggleRow({ testid, onLabel, offLabel, active, icon: Icon, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!active)}
      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition-colors text-left ${
        active
          ? "border-emerald-500/30 bg-emerald-500/[0.06] hover:bg-emerald-500/[0.1]"
          : "border-neutral-800 bg-neutral-950/50 hover:bg-neutral-900/70"
      }`}
      data-testid={testid}
      aria-pressed={active}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        {Icon && (
          <Icon
            className={`w-4 h-4 shrink-0 ${
              active ? "text-emerald-400" : "text-neutral-500"
            }`}
          />
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium text-white truncate">
            {active ? onLabel : offLabel}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            {active ? "ON — enforced" : "OFF"}
          </div>
        </div>
      </div>
      {/* iOS-style slider */}
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
          active ? "bg-emerald-500" : "bg-neutral-700"
        }`}
        aria-hidden
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
            active ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}

// ----------------------------------------------------------------------
// Participant WhatsApp collection popup — host-triggered mid-class.
// Same premium look as the Broadcast / Poll cards. Users who already have
// a number on file (join-form or personalized invite) never see this.
// ----------------------------------------------------------------------
function AskWhatsAppPopup({ message, onSubmit, onDismiss }) {
  const [num, setNum] = useState("");
  const [busy, setBusy] = useState(false);
  const digits = num.replace(/\D/g, "");
  const valid = digits.length >= 10;

  const handleSubmit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    const ok = await onSubmit(digits);
    if (!ok) setBusy(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[57] flex items-center justify-center bg-black/95 px-4"
      data-testid="ask-whatsapp-popup"
    >
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        style={{ willChange: "transform, opacity" }}
        className="relative max-w-md w-full"
      >

        <div className="relative rounded-[28px] border border-emerald-500/50 bg-neutral-950 p-7 md:p-9 shadow-[0_35px_80px_-20px_rgba(16,185,129,0.45)]">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 shrink-0" />
            <div className="text-[10px] tracking-[0.4em] text-emerald-400 font-bold">HOST&nbsp;REQUEST</div>
          </div>
          <motion.h3
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="font-display text-2xl md:text-3xl font-extrabold mb-2 leading-[1.1] tracking-tight text-white"
          >
            {message?.trim() || "Get the class recording & notes on WhatsApp"}
          </motion.h3>
          <p className="text-sm text-neutral-400 mb-5">Drop your 10-digit WhatsApp number below and we'll send it straight to your phone.</p>

          <div className="flex items-center gap-2 rounded-full border border-white/10 bg-neutral-950 px-4 py-3 mb-4 focus-within:border-emerald-400/60 focus-within:ring-2 focus-within:ring-emerald-500/30">
            <span className="text-neutral-500 font-mono text-sm">+91</span>
            <input
              type="tel"
              inputMode="numeric"
              autoFocus
              value={num}
              onChange={(e) => setNum(e.target.value.replace(/\D/g, "").slice(0, 10))}
              onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
              placeholder="98765 43210"
              className="flex-1 bg-transparent outline-none text-white text-base font-mono tracking-wider placeholder:text-neutral-600"
              data-testid="ask-whatsapp-input"
              aria-label="WhatsApp number"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={!valid || busy}
            className="udyog-shimmer-btn w-full h-14 rounded-full text-black font-display font-bold text-base tracking-tight shadow-[0_10px_35px_-10px_rgba(16,185,129,0.6)] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="ask-whatsapp-submit"
          >
            {busy ? "Sending…" : "Send it to me →"}
          </button>

          <button
            onClick={onDismiss}
            className="mt-3 mx-auto block text-[11px] uppercase tracking-widest text-neutral-500 hover:text-neutral-300"
            data-testid="ask-whatsapp-dismiss"
          >
            Not now
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}


// Host on/off switch list — shared by the header popover.
function HostTogglesCard({ hostToggles = [], onOpenPanel, quality, onQuality, isRecording, recordingBusy, onRecording }) {
  const preset = QUALITY_PRESETS[quality] || QUALITY_PRESETS.sharp;
  return (
    <>
      <button
        onClick={onOpenPanel}
        data-testid="host-more-tools"
        className="w-full flex items-center gap-3 px-3 py-3 mb-1.5 rounded-2xl bg-emerald-500/[0.14] ring-1 ring-emerald-400/30 text-[12.5px] text-emerald-200 hover:bg-emerald-500/25 transition-colors duration-200"
      >
        <span className="w-8 h-8 shrink-0 grid place-items-center rounded-full bg-emerald-500 text-black">
          <Megaphone className="w-[15px] h-[15px]" strokeWidth={2} />
        </span>
        <span className="flex-1 text-left leading-tight">
          <span className="block font-semibold text-white">Broadcast, questions &amp; fees</span>
          <span className="block text-[10.5px] text-white/40">
            Send a question, a CTA card or nudge non-payers
          </span>
        </span>
        <ChevronRight className="w-4 h-4 shrink-0 text-white/40" />
      </button>
      {onQuality && (
        <div className="px-3 pt-2 pb-3 mb-1 border-b border-white/[0.06]">
          <div className="text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80 mb-2">
            Class quality · data per student
          </div>
          <div className="grid grid-cols-3 gap-1 p-1 rounded-full bg-black/50 ring-1 ring-white/[0.07]">
            {Object.values(QUALITY_PRESETS).map((p) => (
              <button
                key={p.key}
                onClick={() => onQuality(p.key)}
                data-testid={`quality-${p.key}`}
                className={`h-8 rounded-full text-[10.5px] font-medium transition-colors ${
                  quality === p.key ? "bg-emerald-500 text-black" : "text-white/45 hover:text-white hover:bg-white/5"
                }`}
              >
                {p.short}
              </button>
            ))}
          </div>
          <div className="mt-2 text-[10.5px] text-white/40" data-testid="quality-hint">
            <span className="text-white/70">{preset.label}</span> · {preset.hint}
          </div>
          <div className="mt-2 space-y-1" data-testid="quality-data-table">
            {Object.values(QUALITY_PRESETS).map((p) => (
              <div
                key={p.key}
                className={`flex items-center justify-between text-[10.5px] ${
                  quality === p.key ? "text-emerald-300" : "text-white/35"
                }`}
              >
                <span>{p.label}</span>
                <span className="tabular-nums">{p.perHour}</span>
              </div>
            ))}
          </div>
          <div className="mt-1.5 text-[9.5px] text-white/25 leading-snug">
            Per student, per hour. Anyone on a weak network is stepped down
            automatically — their audio never drops.
          </div>
        </div>
      )}
      <div className="px-3 py-2 text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80">
        Host controls
      </div>
      {onRecording && (
        <button
          onClick={onRecording}
          disabled={recordingBusy}
          data-testid="host-recording-btn"
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-[12.5px] transition-colors duration-200 disabled:opacity-60 ${
            isRecording
              ? "text-red-200 bg-red-500/[0.08] hover:bg-red-500/[0.14]"
              : "text-white/80 hover:bg-white/[0.06] hover:text-white"
          }`}
        >
          <span
            className={`w-8 h-8 shrink-0 grid place-items-center rounded-full ring-1 ${
              isRecording ? "bg-red-500/20 text-red-300 ring-red-400/40" : "bg-white/[0.05] text-white/80 ring-white/10"
            }`}
          >
            {recordingBusy ? (
              <Loader2 className="w-[15px] h-[15px] animate-spin" />
            ) : isRecording ? (
              <Square className="w-[13px] h-[13px] fill-current" strokeWidth={1.7} />
            ) : (
              <Circle className="w-[15px] h-[15px] fill-current" strokeWidth={1.7} />
            )}
          </span>
          <span className="flex-1 text-left">
            {isRecording ? "Stop cloud recording" : "Start cloud recording"}
          </span>
          {isRecording && (
            <span className="text-[10px] uppercase tracking-widest text-red-300/80">REC</span>
          )}
        </button>
      )}
      {hostToggles.map((t) => (
        <button
          key={t.key}
          onClick={() => t.onChange(!t.active)}
          data-testid={`host-switch-${t.key}`}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-[12.5px] text-white/80 hover:bg-white/[0.06] hover:text-white transition-colors duration-200"
        >
          <span
            className={`w-8 h-8 shrink-0 grid place-items-center rounded-full ring-1 ${
              t.active
                ? "bg-emerald-500/20 text-emerald-300 ring-emerald-400/35"
                : "bg-white/[0.05] text-white/80 ring-white/10"
            }`}
          >
            <t.icon className="w-[15px] h-[15px]" strokeWidth={1.7} />
          </span>
          <span className="flex-1 text-left">{t.label}</span>
          <span
            className={`w-10 h-[22px] rounded-full relative shrink-0 transition-colors duration-200 ${
              t.active ? "bg-emerald-500" : "bg-white/[0.14] ring-1 ring-white/10"
            }`}
          >
            <span
              className="absolute top-[3px] w-4 h-4 rounded-full bg-black/85 transition-[left] duration-200"
              style={{ left: t.active ? 21 : 3 }}
            />
          </span>
        </button>
      ))}
    </>
  );
}

const REACTIONS = ["👏", "❤️", "🔥", "😂", "😮", "👍"];

function ControlDock({
  onLeave,
  chatUnread,
  onOpenChat,
  isHost,
  isCoHost,
  onOpenHostPanel,
  hostToggles = [],
  onOpenRoster,
  onOpenEveryone,
  participantCount,
  onFullscreen,
  raiseHandDisabled,
  micForcedOff,
  camForcedOff,
  shareLocked,
  chatLocked,
  onEnterPip,
  onReact,
  onRaiseHand,
  viewQuality,
  onViewQuality,
  lowData,
  onToggleLowData,
  reactionsOff,
  hidden,
}) {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } =
    useLocalParticipant();
  const [mic, setMic] = useState(false);
  const [cam, setCam] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [reactOpen, setReactOpen] = useState(false);
  const [hostOpen, setHostOpen] = useState(false);
  const [micLevel, setMicLevel] = useState(0); // 0..1 RMS for VU bar
  const [studioMic, setStudioMicState] = useState(() => getStudioMicPref());
  const studioProcessorRef = useRef(null);
  const studioTrackRef = useRef(null);
  const studioApplyingRef = useRef(false);
  const studioSrcRef = useRef(null);
  const [studioEpoch, setStudioEpoch] = useState(0);
  const longPressTimerRef = useRef(null);

  // Keep the dock in sync with reality — the browser's own "Stop sharing" bar
  // and host force-mutes change these outside our click handlers.
  useEffect(() => {
    setMic(!!isMicrophoneEnabled);
    setCam(!!isCameraEnabled);
    setSharing(!!isScreenShareEnabled);
  }, [isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled]);

  // Real-time VU meter — analyses the local mic track and updates a 0-1
  // level so we can render a small emerald level bar next to the mic
  // button. Runs only while mic is on.
  useEffect(() => {
    if (!localParticipant || !mic) {
      setMicLevel(0);
      return;
    }
    const pub = localParticipant.getTrackPublication?.(Track.Source.Microphone);
    const mediaTrack = pub?.audioTrack?.mediaStreamTrack;
    if (!mediaTrack) return;
    let audioCtx, analyser, source, rafId;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      audioCtx = new AC();
      const stream = new MediaStream([mediaTrack]);
      source = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setMicLevel(Math.min(1, rms * 3.5));
        rafId = requestAnimationFrame(tick);
      };
      tick();
    } catch (_) {}
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      try {
        source?.disconnect();
        analyser?.disconnect();
        audioCtx?.close();
      } catch (_) {}
    };
  }, [localParticipant, mic]);

  // Studio Mic pipeline — DeepFilterNet3 (AI noise suppression) + broadcast DSP.
  // Runs by DEFAULT for every participant. When DFN3 fails to load (very old
  // browsers, no WASM SIMD, or CDN blocked), we fail SILENTLY back to the raw
  // mic. No error toast — audio still works (Chrome AEC + our getUserMedia
  // constraints still apply), just without the AI cleanup.
  useEffect(() => {
    if (!localParticipant) return;
    let cancelled = false;

    const apply = async () => {
      if (studioApplyingRef.current) return;
      studioApplyingRef.current = true;
      let retry = false;
      try {
        const pub = localParticipant.getTrackPublication?.(Track.Source.Microphone);
        const audioTrack = pub?.audioTrack;
        if (!audioTrack) {
          // toggleMic flips React state before getUserMedia resolves, so the
          // publication often isn't there on this pass. Come back for it —
          // without this the DSP silently never attached.
          retry = studioMic && mic;
          return;
        }

        // Attach on the FIRST unmute and then LEAVE IT ATTACHED. Muting already
        // silences the track, and tearing the DFN3 pipeline down meant every
        // unmute paid the 5-7s WASM + AudioWorklet load again before any voice
        // got through. If LiveKit replaced the track (device change, republish)
        // the processor belongs to a dead track and must be rebuilt.
        if (studioMic) {
          if (studioProcessorRef.current) {
            if (studioTrackRef.current === audioTrack) return; // already attached
            try { await studioProcessorRef.current.destroy(); } catch (_) {}
            studioProcessorRef.current = null;
            studioTrackRef.current = null;
          }
          const mode = getStudioMicMode();
          if (mode === "off") {
            console.info("[studio-mic] mode=off — leaving raw mic in place");
            return;
          }
          const proc = new StudioMicProcessor({ mode });
          studioProcessorRef.current = proc;
          try {
            await audioTrack.setProcessor(proc);
            if (cancelled) {
              await audioTrack.stopProcessor().catch(() => {});
              studioProcessorRef.current = null;
              return;
            }
            studioTrackRef.current = audioTrack;
            studioSrcRef.current = audioTrack.mediaStreamTrack || null;
            console.info(`[studio-mic] active — mode=${proc.mode}`);
            // Build the next rig in the background so a device change or a
            // re-publish is instant too.
            prewarmStudioMic({ mode });
          } catch (e) {
            // Fail-safe: clean up the half-attached processor + destroy the
            // AudioContext, then leave the raw mic in place. User still hears
            // audio, just without the AI cleanup.
            try { await proc.destroy(); } catch (_) {}
            studioProcessorRef.current = null;
            studioTrackRef.current = null;
            console.warn("[studio-mic] falling back to raw mic — DFN3 failed:", e?.message || e);
          }
        } else {
          if (studioProcessorRef.current) {
            try {
              await audioTrack.stopProcessor();
            } catch (_) {}
            studioProcessorRef.current = null;
            studioTrackRef.current = null;
          }
        }
      } finally {
        studioApplyingRef.current = false;
        if (retry && !cancelled) setTimeout(() => !cancelled && apply(), 350);
      }
    };

    apply();
    return () => {
      cancelled = true;
    };
  }, [localParticipant, mic, studioMic, studioEpoch]);

  // Studio Mic watchdog — the pipeline can go deaf without anything throwing:
  // the phone suspends the AudioContext when the class is backgrounded, or the
  // browser hands LiveKit a fresh mic track (device switch, an incoming call,
  // the OS reclaiming the input) and our MediaStreamSource is left holding a
  // dead one. That is what "my voice isn't stable until I switch Studio Mic
  // off and on" was. Now it does that toggle itself, in under three seconds.
  useEffect(() => {
    if (!studioMic || !mic || !localParticipant) return;
    let stop = false;
    const check = async () => {
      if (stop) return;
      const proc = studioProcessorRef.current;
      if (!proc) return;
      const pub = localParticipant.getTrackPublication?.(Track.Source.Microphone);
      const audioTrack = pub?.audioTrack;
      if (!audioTrack) return;
      const ctx = proc.audioContext;
      if (ctx && ctx.state === "suspended") {
        try {
          await ctx.resume();
        } catch (_) {}
      }
      const src = audioTrack.mediaStreamTrack || null;
      const swapped = !!(src && studioSrcRef.current && src !== studioSrcRef.current);
      if (!swapped && proc.isHealthy()) return;
      console.warn(
        `[studio-mic] pipeline went stale (${swapped ? "mic track replaced" : "pipeline unhealthy"}) — rebuilding`
      );
      studioSrcRef.current = src;
      try {
        await audioTrack.stopProcessor();
      } catch (_) {}
      try {
        await proc.destroy();
      } catch (_) {}
      studioProcessorRef.current = null;
      studioTrackRef.current = null;
      if (!stop) setStudioEpoch((n) => n + 1);
    };
    const t = setInterval(check, 2500);
    const onVis = () => {
      if (!document.hidden) check();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [studioMic, mic, localParticipant]);

  // Keep the phone in "call" mode so the volume rocker controls the class the
  // way it controls a phone call — see lib/callAudio.js. Released while the
  // student's own mic is live, because LiveKit owns the capture then.
  useEffect(() => {
    if (!isMobileDevice()) return;
    if (mic) {
      releaseCallAudio();
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const ok = await holdCallAudio();
      if (ok && !cancelled && !localStorage.getItem("udyog_callaudio_note")) {
        localStorage.setItem("udyog_callaudio_note", "1");
        toast("Your volume keys now control the class", {
          description: "Your mic is not being shared — this only puts the audio on the call channel.",
        });
      }
    }, 1200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [mic]);

  useEffect(() => () => releaseCallAudio(), []);

  // Build the DFN3 pipeline in the background as soon as the class opens, so
  // the first unmute is instant instead of waiting 5-7s on the WASM load.
  useEffect(() => {
    if (!studioMic) return;
    const id = setTimeout(() => {
      prewarmStudioMic({ mode: getStudioMicMode() });
    }, 600);
    return () => clearTimeout(id);
  }, [studioMic]);

  // On unmount, make sure the processor's AudioContext is closed.
  useEffect(() => {
    return () => {
      const proc = studioProcessorRef.current;
      if (proc) {
        proc.destroy().catch(() => {});
        studioProcessorRef.current = null;
      }
      destroyWarmStudioMic();
    };
  }, []);

  const toggleStudioMic = useCallback(() => {
    setStudioMicState((prev) => {
      const next = !prev;
      setStudioMicPref(next);
      toast(next
        ? "Studio Mic ON — AI noise cancellation + compressor active"
        : "Studio Mic OFF — using raw microphone");
      return next;
    });
  }, []);

  // Long-press the mic to toggle Studio Mic — TOUCH ONLY. On a laptop a
  // slightly slow click used to trip the long-press and swallow the mute,
  // which felt like a dead button. Desktop uses right-click instead.
  const didLongPressRef = useRef(false);
  const onMicPressStart = (e) => {
    if (e && e.type !== "touchstart") return;
    didLongPressRef.current = false;
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      didLongPressRef.current = true;
      toggleStudioMic();
    }, 800);
  };
  const onMicPressEnd = () => {
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
  };
  const onMicContextMenu = (e) => {
    e.preventDefault();
    toggleStudioMic();
  };


  // Co-hosts are exempt so they can help the host moderate.
  useEffect(() => {
    if (!localParticipant || !micForcedOff) return;
    if (isHost || isCoHost) return;
    if (localParticipant.isMicrophoneEnabled) {
      localParticipant.setMicrophoneEnabled(false).catch(() => {});
    }
    setMic(false);
  }, [micForcedOff, localParticipant, isHost, isCoHost]);
  useEffect(() => {
    if (!localParticipant || !camForcedOff) return;
    if (isHost || isCoHost) return;
    if (localParticipant.isCameraEnabled) {
      localParticipant.setCameraEnabled(false).catch(() => {});
    }
    setCam(false);
  }, [camForcedOff, localParticipant, isHost, isCoHost]);

  const toggleMic = async () => {
    if (!localParticipant) return;
    if (didLongPressRef.current) {
      didLongPressRef.current = false;
      return; // long-press already handled → don't toggle mic
    }
    if (micForcedOff && !isHost && !isCoHost) {
      toast("Mic is locked by the host", { description: "You'll be able to unmute once the host allows it." });
      return;
    }
    const next = !mic;
    setMic(next);
    try {
      await localParticipant.setMicrophoneEnabled(next);
    } catch (e) {
      setMic(!next);
      toast.error("Couldn't toggle microphone");
    }
  };
  const toggleCam = async () => {
    if (!localParticipant) return;
    if (camForcedOff && !isHost && !isCoHost) {
      toast("Camera is locked by the host", { description: "You'll be able to turn it on once the host allows it." });
      return;
    }
    const next = !cam;
    setCam(next);
    try {
      await localParticipant.setCameraEnabled(next);
    } catch (e) {
      setCam(!next);
      toast.error("Couldn't toggle camera");
    }
  };
  const toggleShare = async () => {
    if (!localParticipant) return;
    const next = !sharing;
    try {
      if (next) {
        // Captured by hand rather than through LiveKit's helper, because the
        // ONLY way to keep the teacher's mouse pointer out of the students'
        // view is the `cursor: "never"` capture constraint — and LiveKit's
        // options don't carry it through.
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            cursor: "never",
            // Opens Chrome's picker on the TAB list. A tab capture is the ONLY
            // capture mode that carries no mouse pointer — a whole-screen or
            // window capture always paints the cursor into the frames and no
            // constraint can remove it.
            displaySurface: "browser",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 20, max: 20 },
          },
          audio: true,
          systemAudio: "include",
          selfBrowserSurface: "exclude",
          surfaceSwitching: "include",
          suppressLocalAudioPlayback: false,
        });
        const [videoTrack] = stream.getVideoTracks();
        // Some Chrome builds accept the constraint only after the fact.
        try {
          await videoTrack.applyConstraints({ cursor: "never" });
        } catch (_) {}
        videoTrack.contentHint = "text";
        // The browser's own "Stop sharing" bar must end our share too.
        videoTrack.addEventListener("ended", () => {
          localParticipant.setScreenShareEnabled(false).catch(() => {});
          setSharing(false);
        });
        // Publish the capture EXACTLY as the browser gives it to us. An
        // experiment that cropped the frames before the encoder (to spend the
        // bitrate only on the visible region) left students with a black
        // screen on some Android builds — never trade a working share for
        // sharper pixels.
        const lkVideo = new LocalVideoTrack(videoTrack, undefined, false);
        await localParticipant.publishTrack(lkVideo, {
          source: Track.Source.ScreenShare,
          screenShareEncoding: { maxBitrate: 5_000_000, maxFramerate: 20, priority: 'high' },
          screenShareSimulcastLayers: [ScreenSharePresets.h720fps15, ScreenSharePresets.h540fps15],
          simulcast: true,
          videoCodec: "vp8",
        });
        const [audioTrack] = stream.getAudioTracks();
        if (audioTrack) {
          await localParticipant.publishTrack(new LocalAudioTrack(audioTrack, undefined, false), {
            source: Track.Source.ScreenShareAudio,
            audioPreset: { maxBitrate: 96_000 },
          });
        }
        toast.success("Screen sharing is live", {
          description:
            videoTrack.getSettings?.().displaySurface === "browser"
              ? "You shared a tab — your mouse pointer is NOT visible to students."
              : "You shared your whole screen, so students can see your mouse pointer. Share a Chrome TAB instead to hide it.",
          duration: 9000,
        });
      } else {
        await localParticipant.setScreenShareEnabled(false);
      }
      setSharing(next);
    } catch (e) {
      toast.error("Couldn't start screen share");
    }
  };
  const toggleHand = () => {};

  // Close the More menu on any outside click / Escape.
  useEffect(() => {
    if (!moreOpen && !hostOpen) return;
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      // Clicks inside either popover must not close it — only outside clicks do.
      if (
        e.type === "pointerdown" &&
        e.target?.closest?.(
          '[data-testid="host-toggles"], [data-testid="more-menu"], [data-testid="host-controls-open-btn"], [data-testid="ctl-more"]'
        )
      )
        return;
      setMoreOpen(false);
      setHostOpen(false);
    };
    const t = setTimeout(() => {
      document.addEventListener("pointerdown", close);
      document.addEventListener("keydown", close);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [moreOpen, hostOpen]);

  // Faded look when host has locked the local user out of a capability.
  // Kept mild so the icon is still clearly visible — just dimmer than an
  // active button. Chat is NEVER faded per product decision.
  // Hosts AND co-hosts bypass all locks.
  const privileged = isHost || isCoHost;
  const micLocked = !!(micForcedOff && !privileged);
  const camLocked = !!(camForcedOff && !privileged);
  const shareBlocked = !!(shareLocked && !privileged);
  const chatBlocked = !!(chatLocked && !privileged);
  const handBlocked = !!(raiseHandDisabled && !privileged);
  const fadedIfBlocked = (blocked) => (blocked ? "opacity-60" : "");

  // Circular control with the label underneath — one shared shape for every
  // dock button so nothing is square and everything lines up.
  const DockBtn = ({
    icon: Icon,
    label,
    onClick,
    active,
    big,
    badge,
    badgeTone = "warn",
    testid,
    title,
    faded,
    extra,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    onTouchStart,
    onTouchEnd,
    onTouchCancel,
    onContextMenu,
  }) => (
    <button
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchCancel}
      onContextMenu={onContextMenu}
      title={title || label}
      aria-label={label}
      data-testid={testid}
      className={`group flex flex-col items-center gap-1.5 [@media(max-height:520px)]:gap-0.5 shrink-0 select-none ${faded ? "opacity-60" : ""}`}
    >
      <span className="relative">
        <span
          className={`grid place-items-center rounded-full transition-[transform,background-color,box-shadow] duration-200 group-hover:-translate-y-[2px] ${
            big
              ? "h-14 w-14 sm:h-16 sm:w-16 [@media(max-height:520px)]:h-11 [@media(max-height:520px)]:w-11 bg-gradient-to-b from-emerald-400 to-emerald-600 text-black shadow-[0_10px_34px_-8px_rgba(16,185,129,0.85)] ring-1 ring-emerald-300/50"
              : active
              ? "h-11 w-11 sm:h-12 sm:w-12 [@media(max-height:520px)]:h-9 [@media(max-height:520px)]:w-9 bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/40"
              : "h-11 w-11 sm:h-12 sm:w-12 [@media(max-height:520px)]:h-9 [@media(max-height:520px)]:w-9 bg-white/[0.06] text-white/85 ring-1 ring-white/10 group-hover:bg-white/[0.12]"
          }`}
        >
          <Icon className={big ? "w-6 h-6" : "w-[19px] h-[19px]"} strokeWidth={1.8} />
        </span>
        {badge > 0 && (
          <span
            className={`absolute -top-1.5 -right-2 min-w-[20px] h-[20px] px-1 rounded-full text-[10.5px] font-bold leading-none grid place-items-center ring-2 ring-[#1a1d1b] ${
              badgeTone === "alert" ? "bg-red-500 text-white" : "bg-amber-400 text-black"
            }`}
          >
            {badge}
          </span>
        )}
        {extra}
      </span>
      <span
        className={`text-[9px] sm:text-[9.5px] uppercase tracking-[0.16em] [@media(max-height:520px)]:hidden ${
          big ? "text-emerald-300/90 font-semibold" : "text-white/45 group-hover:text-white/75"
        }`}
      >
        {label}
      </span>
    </button>
  );


  const moreItems = [
    {
      icon: LayoutGrid,
      label: "See everyone in tiles",
      onClick: () => {
        setMoreOpen(false);
        onOpenEveryone?.();
      },
      testid: "more-everyone-tiles",
    },
    ...(privileged
      ? []
      : [
          {
            icon: MonitorUp,
            label: sharing ? "Stop sharing" : "Share my screen",
            active: sharing,
            onClick: () => {
              setMoreOpen(false);
              if (shareBlocked) {
                toast("Screen sharing is disabled by the host");
                return;
              }
              toggleShare();
            },
            testid: "more-share",
          },
        ]),
    {
      icon: lowData ? WifiOff : Wifi,
      label: lowData ? "Low data mode · on" : "Low data mode",
      active: !!lowData,
      onClick: () => {
        setMoreOpen(false);
        onToggleLowData?.();
      },
      testid: "more-low-data",
    },
    ...(privileged
      ? [
    {
      icon: Copy,
      label: "Copy class link",
      onClick: () => {
        setMoreOpen(false);
        try {
          copyText(window.location.origin + window.location.pathname);
          toast.success("Class link copied — share it with a classmate");
        } catch (_) {
          toast.error("Couldn't copy the link");
        }
      },
      testid: "more-copy-link",
    },
    {
      icon: PictureInPicture2,
      label: "Keep floating (PiP)",
      onClick: () => {
        setMoreOpen(false);
        onEnterPip?.();
      },
      testid: "more-pip",
    },
        ]
      : []),
    {
      icon: Maximize2,
      label: "Full screen",
      onClick: () => {
        setMoreOpen(false);
        onFullscreen?.();
      },
      testid: "more-fullscreen",
    },
    ...(privileged
      ? [
          {
            icon: Sparkles,
            label: studioMic ? "Studio mic · on" : "Studio mic · off",
            active: studioMic,
            onClick: () => {
              setMoreOpen(false);
              toggleStudioMic();
            },
            testid: "more-studio-mic",
          },
        ]
      : []),
  ];

  return (
    <div
      className={`fixed bottom-3 sm:bottom-5 [@media(max-height:520px)]:bottom-1.5 left-0 right-0 z-20 pointer-events-none flex justify-center px-2 transition-opacity duration-300 ${
        hidden ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, type: "spring", damping: 22, stiffness: 240 }}
        className="pointer-events-auto"
      >
        <div
          className="relative flex items-end gap-4 sm:gap-6 [@media(max-height:520px)]:gap-2.5 px-4 sm:px-8 [@media(max-height:520px)]:px-3 py-2.5 sm:py-3 [@media(max-height:520px)]:py-1.5 rounded-full bg-black shadow-[0_20px_60px_-24px_rgba(0,0,0,0.95)]"
          data-testid="control-dock"
        >
          {/* Reactions — own little satellite, so the Chat button never moves */}
          {onReact && (
            <div className="absolute bottom-full mb-3 [@media(max-height:520px)]:mb-2 right-2 flex items-center gap-2">
              <AnimatePresence>
                {reactOpen && (
                  <motion.div
                    initial={{ opacity: 0, x: 16, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 16, scale: 0.95 }}
                    transition={{ duration: 0.16 }}
                    className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#0a0c0b]/95 ring-1 ring-white/[0.12] shadow-[0_18px_50px_-20px_rgba(0,0,0,1)]"
                    data-testid="reaction-strip"
                  >
                    {REACTIONS.map((e) => (
                      <button
                        key={e}
                        onClick={() => {
                          onReact(e);
                          setReactOpen(false);
                        }}
                        className="w-8 h-8 grid place-items-center rounded-full text-[18px] hover:bg-white/[0.09] hover:scale-110 transition-transform duration-150"
                        data-testid={`reaction-${e}`}
                        title="Send reaction"
                      >
                        {e}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
              <button
                onClick={() => {
                  if (reactionsOff) {
                    toast("Reactions are turned off by the host");
                    return;
                  }
                  setReactOpen((v) => !v);
                }}
                className={`w-10 h-10 [@media(max-height:520px)]:w-9 [@media(max-height:520px)]:h-9 grid place-items-center rounded-full transition-colors duration-200 ${
                  reactionsOff
                    ? "bg-white/[0.04] text-white/25 ring-1 ring-white/[0.06]"
                    : reactOpen
                    ? "bg-emerald-500 text-black"
                    : "bg-[#1a1d1b] text-emerald-300 ring-1 ring-white/[0.1] hover:bg-[#242927]"
                }`}
                data-testid="ctl-react"
                title={reactionsOff ? "Reactions off" : "Send a reaction"}
              >
                <Smile className="w-[18px] h-[18px]" strokeWidth={1.9} />
              </button>
            </div>
          )}
          {/* Left — mic & camera */}
          <div className="flex items-end gap-3 sm:gap-4">
            <DockBtn
              icon={mic ? Mic : MicOff}
              label="Mic"
              active={mic}
              faded={micLocked}
              onClick={toggleMic}
              onTouchStart={onMicPressStart}
              onTouchEnd={onMicPressEnd}
              onTouchCancel={onMicPressEnd}
              onContextMenu={onMicContextMenu}
              testid="ctl-mic"
              title={micLocked ? "Locked by host" : studioMic ? "Tap to mute · right-click to turn OFF Studio Mic" : "Tap to mute · right-click to turn ON Studio Mic"}
              extra={
                <>
                  {mic && (
                    <span
                      className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.7)] transition-[width] duration-75"
                      style={{ width: `${Math.max(4, micLevel * 32)}px` }}
                      data-testid="mic-vu-bar"
                    />
                  )}
                </>
              }
            />
            <DockBtn
              icon={cam ? VideoIcon : VideoOff}
              label="Video"
              active={cam}
              faded={camLocked}
              onClick={toggleCam}
              testid="ctl-cam"
              title={camLocked ? "Locked by host" : "Toggle camera"}
            />
            {privileged && (
              <DockBtn
                icon={MonitorUp}
                label={sharing ? "Stop" : "Share"}
                active={sharing}
                faded={shareBlocked}
                onClick={() => {
                  if (shareBlocked) {
                    toast("Screen sharing is disabled by the host");
                    return;
                  }
                  toggleShare();
                }}
                testid="ctl-share"
                title={shareBlocked ? "Disabled by host" : sharing ? "Stop sharing" : "Share your screen"}
              />
            )}
          </div>

          {/* Centre — chat */}
          <DockBtn
            icon={MessageSquare}
            label="Chat"
            big
            badge={chatUnread}
            badgeTone="alert"
            onClick={onOpenChat}
            testid="ctl-chat"
            title={chatBlocked ? "Only the host can post — you can still read" : "Open chat"}
          />

          {/* Right — people + more */}
          <div className="relative flex items-end gap-3 sm:gap-4">
            <DockBtn
              icon={Users}
              label="People"
              badge={participantCount}
              onClick={() => onOpenRoster?.()}
              testid="ctl-people"
              title="Participants · pin anyone"
            />
            <DockBtn
              icon={MoreHorizontal}
              label="More"
              active={moreOpen}
              onClick={() => {
                setHostOpen(false);
                setMoreOpen((v) => !v);
              }}
              testid="ctl-more"
              title="More options"
            />

            {/* Host controls now live in the header (top-right). */}

            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.16 }}
                  className="absolute bottom-full right-0 mb-4 w-64 sm:w-72 rounded-[24px] bg-[#0a0c0b] ring-1 ring-white/[0.12] shadow-[0_30px_80px_-20px_rgba(0,0,0,1)] p-2.5 z-50"
                  data-testid="more-menu"
                >
                  {onViewQuality && (
                    <div className="px-3 pt-2 pb-3 mb-1 border-b border-white/[0.06]">
                      <div className="text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80 mb-2">
                        Class quality
                      </div>
                      <div className="grid grid-cols-4 gap-1 p-1 rounded-full bg-black/50 ring-1 ring-white/[0.07]">
                        {Object.values(VIEW_QUALITY).map((v) => (
                          <button
                            key={v.key}
                            onClick={() => onViewQuality(v.key)}
                            data-testid={`view-quality-${v.key}`}
                            className={`h-8 rounded-full text-[10.5px] font-medium transition-colors ${
                              viewQuality === v.key
                                ? "bg-emerald-500 text-black"
                                : "text-white/45 hover:text-white hover:bg-white/5"
                            }`}
                          >
                            {v.label}
                          </button>
                        ))}
                      </div>
                      <div className="mt-2 text-[10.5px] text-white/40" data-testid="view-quality-hint">
                        {(VIEW_QUALITY[viewQuality] || VIEW_QUALITY["1080p"]).hint}
                      </div>
                    </div>
                  )}
                  <div className="px-3 py-2 text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80">
                    More options
                  </div>
                  {moreItems.map((it) => (
                    <button
                      key={it.label}
                      onClick={it.onClick}
                      data-testid={it.testid}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-[12.5px] text-white/80 hover:bg-white/[0.06] hover:text-white transition-colors duration-200"
                    >
                      <span
                        className={`w-8 h-8 shrink-0 grid place-items-center rounded-full ring-1 ${
                          it.active
                            ? "bg-emerald-500/20 text-emerald-300 ring-emerald-400/35"
                            : "bg-white/[0.05] text-white/80 ring-white/10"
                        }`}
                      >
                        <it.icon className="w-[15px] h-[15px]" strokeWidth={1.7} />
                      </span>
                      <span className="text-left">{it.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
